import Phaser from "phaser";
import { clampZoom, clampCameraScrollAxis, WHEEL_STEP_IN, WHEEL_STEP_OUT } from "../cameraZoom";
import { fitCameraToDesign, fitHudCamera, backdropCoverSize } from "../ui/responsive";
import { AudioManager } from "../AudioManager";
import { type BattleMode, isBattleMode, modeStartPressure, modeAllowsWin } from "../battleMode";
import { loadCastleLayout } from "../castleLayout";
import { startGoldBonus, fortressHpMultiplier, techDiscount } from "../shop";
import { type DailyChallenge, dailyChallengeFromSeed } from "../dailyChallenge";
import type {
  BattleEvent,
  LaneBattleAnalysis,
  MercyBattleSummary,
  BattleReport,
  BattleResult,
  BattleSummary,
  CastleSegmentId,
  CastleStateSummary,
  LaneStateSummary,
  TechUpgradeId
} from "../../../../shared/types/BattleEvent";
import type { LaneNumber, StrategyType } from "../../../../shared/types/CommanderDecision";
import { classifyTauntIntent } from "../../../../shared/tauntIntent";
import type { CommanderProfile } from "../../../../shared/types/CommanderProfile";
import type { GameCommand, UnitManeuverCommand } from "../../../../shared/types/GameCommand";
import type { VeteranUnitMemory } from "../../../../shared/types/PlayerMemory";
import { CommanderSocket } from "../../ai/commanderSocket";
import { createBattleSummary } from "../../ai/aiEventClient";
import {
  appendBattleHistory,
  getOrCreatePlayerId,
  hasSeenTutorial,
  loadSettings,
  loadCommanderProfile,
  loadPlayerMemory,
  saveCommanderProfile,
  savePlayerMemory,
  setTutorialSeen,
  updateMemoryFromReport
} from "../../ai/localMemory";
import { t } from "../../i18n";
import { BuildPanel, type BuildSelection } from "../../ui/BuildPanel";
import { CommanderChat } from "../../ui/CommanderChat";
import { LaneStatusPanel } from "../../ui/LaneStatusPanel";
import { MiniMapPanel } from "../../ui/MiniMapPanel";
import { Castle } from "../entities/Castle";
import { Tower, type TowerTargetPriority } from "../entities/Tower";
import { Unit, type UnitFaction, type UnitManualAbilityId, type UnitWaypoint } from "../entities/Unit";
import { BattleEventLogger } from "../systems/BattleEventLogger";
import { CombatSystem } from "../systems/CombatSystem";
import { CounterAttackSystem } from "../systems/CounterAttackSystem";
import { DifficultySystem } from "../systems/DifficultySystem";
import { EconomySystem } from "../systems/EconomySystem";
import { EconomyInvestmentSystem } from "../systems/EconomyInvestmentSystem";
import { EconomyOutpostSystem } from "../systems/EconomyOutpostSystem";
import { EventSystem } from "../systems/EventSystem";
import { WaveSystem, type AiBudgetReport } from "../systems/WaveSystem";
import { ObjectiveSystem } from "../systems/ObjectiveSystem";
import { TechSystem } from "../systems/TechSystem";
import mapConfig from "../data/mapConfig.json";
import towersData from "../data/towers.json";
import unitsData from "../data/units.json";

type TowerData = (typeof towersData)[number];
type UnitData = (typeof unitsData)[number];
type BattleSettings = ReturnType<typeof loadSettings>;
type CastleGroup = Record<CastleSegmentId, Castle>;
type InteractionMode = "build" | "upgrade" | "unitCommand" | "cameraPan";
type CameraPanKeys = { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
type PointerIntent = "left" | "right" | "middle" | "touchPan";
type LaneCrossing = {
  id: string;
  fromLane: LaneNumber;
  toLane: LaneNumber;
  x: number;
  radius: number;
  enabled: boolean;
};
type CrossingOwner = "neutral" | "player" | "enemy";
type CrossingControlState = {
  config: LaneCrossing;
  owner: CrossingOwner;
  progress: number;
  playerUnits: number;
  enemyUnits: number;
  ring: Phaser.GameObjects.Graphics;
  progressFill: Phaser.GameObjects.Rectangle;
  flag: Phaser.GameObjects.Text;
};
type MovePathStyle = {
  lineColor?: number;
  fillColor?: number;
  alpha?: number;
  depth?: number;
};
type SummonQueueItem = {
  unitId: string;
  lane: LaneNumber;
  queuedAt: number;
};
type VeteranProgress = {
  veteranId: string;
  unitId: string;
  nickname: string;
  spawnedAt: number;
  kills: number;
  promoted: boolean;
  survivedBattles: number;
  preferredLane: LaneNumber;
};
type LaneCollapseState = {
  until: number;
  penalty: number;
};
type MerchantOffer = {
  id: "gold" | "repair" | "counter";
  label: string;
  description: string;
  apply: () => void;
};

const SIDE_CASTLE_HP = 960;
const MAIN_CASTLE_HP = SIDE_CASTLE_HP * 3;
const AI_URGENT_DECISION_COOLDOWN_MS = 8000;
const MERCY_COOLDOWN_MS = 18000;
const HUMILIATION_OFFER_COOLDOWN_MS = 22000;
const MAX_SUMMON_QUEUE = 8;
const BASE_SUMMON_INTERVAL_MS = 650;
const SAME_LANE_SUMMON_INTERVAL_MS = 900;
const CASTLE_REPAIR_COOLDOWN_MS = 20000;
const CASTLE_REPAIR_MAX_HP = 120;
const CASTLE_REPAIR_GOLD_PER_HP = 0.55;
const ECONOMY_INVESTMENT_COST = 180;
const VETERAN_REQUIRED_KILLS = 3;
const VETERAN_REQUIRED_SURVIVAL_MS = 45000;
const CROSSING_CAPTURE_MS = 6000;
const LANE_COLLAPSE_MS = 12000;
const ADJACENT_LANE_COLLAPSE_MS = 8000;
const LANE_COLLAPSE_REPEAT_WINDOW_MS = 30000;
const PLAYER_ULTIMATE_MS = 8000;
const AI_ULTIMATE_MS = 7000;
const AI_ULTIMATE_MIN_BATTLE_MS = 38000;
const AI_ULTIMATE_COOLDOWN_MS = 62000;
const NARRATIVE_TAUNT_COOLDOWN_MS = 26000;
const MERCHANT_OFFER_DURATION_MS = 12000;
const AI_TRAP_COOLDOWN_MS = 45000;
const CUTIN_COOLDOWN_MS = 45000;

export class BattleScene extends Phaser.Scene {
  private readonly playerUnits: Unit[] = [];
  private readonly enemies: Unit[] = [];
  private readonly towers: Tower[] = [];
  private readonly occupiedPads = new Set<string>();
  private readonly padSprites = new Map<string, Phaser.GameObjects.Image>();
  private readonly recentActions: string[] = [];
  private readonly selectedPlayerUnits = new Set<Unit>();
  private readonly selectedUnitRings = new Map<Unit, Phaser.GameObjects.Graphics>();
  private readonly moraleRings = new Map<Unit, Phaser.GameObjects.Graphics>();
  private readonly laneMoraleCache = new Map<string, number>();
  private readonly laneCollapseStates = new Map<LaneNumber, LaneCollapseState>();
  private readonly laneLastCollapseAt = new Map<LaneNumber, number>();
  private readonly crossingStates = new Map<string, CrossingControlState>();
  private readonly summonQueue: SummonQueueItem[] = [];
  private readonly veteranProgress = new Map<Unit, VeteranProgress>();
  private readonly deployedVeteranIds = new Set<string>();
  private playerUltimateGauge = 0;
  private aiUltimateGauge = 0;
  private playerUltimateUntil = 0;
  private aiUltimateUntil = 0;
  private aiUltimateLane?: LaneNumber;
  private lastAiUltimateAt = -Infinity;
  private aiLastStandTriggered = false;
  private ultimateButton?: Phaser.GameObjects.Container;
  private ultimateButtonBg?: Phaser.GameObjects.Rectangle;
  private ultimateButtonFill?: Phaser.GameObjects.Rectangle;
  private ultimateButtonText?: Phaser.GameObjects.Text;
  private battleTimeScale = 1;
  private speedButtonBg?: Phaser.GameObjects.Rectangle;
  private speedButtonText?: Phaser.GameObjects.Text;
  private selection?: BuildSelection;
  private interactionMode: InteractionMode = "unitCommand";
  private economy!: EconomySystem;
  private economyInvestments!: EconomyInvestmentSystem;
  private economyOutposts!: EconomyOutpostSystem;
  private difficulty!: DifficultySystem;
  private eventSystem!: EventSystem;
  private counter!: CounterAttackSystem;
  private combat!: CombatSystem;
  private waves!: WaveSystem;
  private objectives!: ObjectiveSystem;
  private tech!: TechSystem;
  private logger!: BattleEventLogger;
  private chat!: CommanderChat;
  private buildPanel!: BuildPanel;
  private lanePanel!: LaneStatusPanel;
  private miniMapPanel!: MiniMapPanel;
  private commanderSocket?: CommanderSocket;
  private playerCastle!: Castle;
  private aiCastle!: Castle;
  private playerCastles!: CastleGroup;
  private aiCastles!: CastleGroup;
  private destroyedTowerIds: string[] = [];
  private upgradePatterns: string[] = [];
  private memoryVeterans: VeteranUnitMemory[] = [];
  private playerId = "";
  private battleStartedAt = 0;
  private lastSummaryAt = 0;
  private lastEventsAt = 0;
  private lastFieldManeuverAt = 0;
  private lastThreatDecisionAt = 0;
  private lastNarrativeTauntAt = 0;
  private lastNarrativeTauntKey = "";
  private lastTrapAt = 0;
  private trapCount = 0;
  private readonly lastCutInAt = new Map<string, number>();
  private lastMercyAt = 0;
  private lastHumiliationOfferAt = 0;
  private humiliationPleaCount = 0;
  private merchantPanel?: Phaser.GameObjects.Container;
  private merchantOffered = false;
  private merchantExpiresAt = 0;
  private ended = false;
  private pendingResult?: BattleResult;
  private resultReport?: BattleReport;
  private tutorialOverlay?: Phaser.GameObjects.Container;
  private tutorialStep = 0;
  private isCameraDragging = false;
  private cameraDragPointerId?: number;
  private lastDragX = 0;
  private lastDragY = 0;
  private pointerIntent?: PointerIntent;
  private pointerDownX = 0;
  private pointerDownY = 0;
  private pointerDownWorld?: Phaser.Math.Vector2;
  private pointerStartedInBattlefield = false;
  private pointerStartedInCameraArea = false;
  private readonly activeTouchPointers = new Map<number, { x: number; y: number }>();
  private isPinching = false;
  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  private pinchPointerIds?: [number, number];
  private zoomToast?: Phaser.GameObjects.Text;
  private mobileCommandRing?: Phaser.GameObjects.Container;
  private mobileCommandRingWorld?: Phaser.Math.Vector2;
  private mobileCommandRingRadius = 0;
  // Set while a tap is landing on the command-ring buttons so the scene's pointer
  // handlers don't also treat it as a battlefield tap (which would close/reopen the ring
  // and eat the button press — the iOS "ring shows but Move does nothing" bug).
  private mobileCommandRingTap = false;
  // Live preview of where the selected units will actually arrive (lane-snapped),
  // shown on the world while the command ring is open so the destination always
  // matches what the player pointed at.
  private mobileCommandTargetMarker?: Phaser.GameObjects.Graphics;
  private unitAbilityButton?: Phaser.GameObjects.Container;
  private unitAbilityButtonBg?: Phaser.GameObjects.Rectangle;
  private unitAbilityButtonLabel?: Phaser.GameObjects.Text;
  private selectionBox?: Phaser.GameObjects.Rectangle;
  private commandPreview?: Phaser.GameObjects.Graphics;
  private commandPreviewLabel?: Phaser.GameObjects.Text;
  private lastClickedUnit?: Unit;
  private lastUnitClickAt = 0;
  private towerRangePreview?: Phaser.GameObjects.Graphics;
  private towerPriorityMenu?: Phaser.GameObjects.Container;
  private settings!: BattleSettings;
  private uiCamera?: Phaser.Cameras.Scene2D.Camera;
  private fullBleedBackdrop?: Phaser.GameObjects.Image;
  private cameraCursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private cameraPanKeys?: CameraPanKeys;
  private forceTutorial = false;
  private battleMode: BattleMode = "normal";
  private daily?: DailyChallenge;
  private tutorialWasOpen = false;
  private nextSummonAt = 0;
  private lastSummonQueueLane?: LaneNumber;
  private readonly lastCastleRepairAt: Partial<Record<CastleSegmentId, number>> = {};

  constructor() {
    super("BattleScene");
  }

  init(data?: { forceTutorial?: boolean; mode?: BattleMode; dailySeed?: number }) {
    this.forceTutorial = Boolean(data?.forceTutorial);
    this.battleMode = isBattleMode(data?.mode) ? data!.mode! : "normal";
    this.daily =
      this.battleMode === "daily" && typeof data?.dailySeed === "number" ? dailyChallengeFromSeed(data.dailySeed) : undefined;
    this.tutorialWasOpen = false;
  }

  create() {
    this.ended = false;
    this.playerUnits.length = 0;
    this.enemies.length = 0;
    this.towers.length = 0;
    this.occupiedPads.clear();
    this.padSprites.clear();
    this.recentActions.length = 0;
    this.summonQueue.length = 0;
    this.selectedPlayerUnits.clear();
    this.crossingStates.clear();
    this.veteranProgress.clear();
    this.deployedVeteranIds.clear();
    this.lastThreatDecisionAt = 0;
    this.lastMercyAt = 0;
    this.lastHumiliationOfferAt = 0;
    this.humiliationPleaCount = 0;
    this.nextSummonAt = 0;
    this.lastSummonQueueLane = undefined;
    for (const segment of ["top", "main", "bottom"] as CastleSegmentId[]) {
      this.lastCastleRepairAt[segment] = -CASTLE_REPAIR_COOLDOWN_MS;
    }
    this.clearSelectionRings();
    this.clearMoraleRings();
    this.destroyedTowerIds = [];
    this.upgradePatterns = [];
    this.selection = undefined;
    this.interactionMode = "unitCommand";

    this.settings = loadSettings();
    this.input.mouse?.disableContextMenu();
    if (this.settings.beta.cameraZoom) {
      this.setupCameraControls();
    }
    this.fullBleedBackdrop = this.add
      .image(mapConfig.designWidth / 2, mapConfig.designHeight / 2, mapConfig.mapAssetKey)
      .setDepth(-10)
      .setTint(0x23201d)
      .setAlpha(0.92);
    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720).setDepth(0);
    this.drawLaneGuides();
    this.drawCrossingGuides();
    this.drawTowerPads();

    this.economy = new EconomySystem();
    // War Funds shop: permanent "War Chest" upgrade adds starting gold.
    this.economy.gold += startGoldBonus(loadPlayerMemory(getOrCreatePlayerId()).upgrades.war_chest ?? 0);
    // Daily challenge: the day's rolled gold modifier (gift or famine), with a playable floor.
    if (this.daily) this.economy.gold = Math.max(100, this.economy.gold + this.daily.goldDelta);
    this.economyInvestments = new EconomyInvestmentSystem(this, {
      onBuilt: (investment) => {
        this.logger.log("ECONOMY_INVESTMENT_BUILT", this.battleTime, { incomePerSecond: investment.incomePerSecond }, investment.lane);
      },
      onDisabled: (investment) => {
        this.logger.log("ECONOMY_INVESTMENT_DISABLED", this.battleTime, { remainingSec: investment.remainingSec }, investment.lane);
      },
      onExpired: (investment) => {
        this.logger.log("ECONOMY_INVESTMENT_EXPIRED", this.battleTime, { active: investment.active }, investment.lane);
      }
    });
    this.economyOutposts = new EconomyOutpostSystem(this, {
      onCaptured: (outpost) => {
        this.logger.log("ECONOMY_OUTPOST_CAPTURED", this.battleTime, { owner: outpost.owner, progress: outpost.progress }, outpost.lane);
        if (outpost.owner === "player") {
          this.buildPanel?.setStatus(`${t(this.settings.language, "economyOutpostCaptured")} ${outpost.lane}`);
          this.recentActions.push(`captured_outpost_lane_${outpost.lane}`);
        }
      }
    });
    // Daily challenge folds its rolled pressure modifier onto the mode's base (clamped by the system).
    this.difficulty = new DifficultySystem(modeStartPressure(this.battleMode) + (this.daily?.pressureDelta ?? 0));
    this.eventSystem = new EventSystem(this);
    this.counter = new CounterAttackSystem(this);
    this.logger = new BattleEventLogger();
    this.waves = new WaveSystem(this, this.difficulty, this.settings.beta.spriteAnimations);
    this.combat = new CombatSystem(this, this.economy, this.eventSystem, this.counter, {
      contactCombat: this.settings.beta.contactCombat,
      laneMorale: (lane, faction) => this.laneMoraleForCombat(lane, faction),
      onEnemyKilled: (enemy, killer) => {
        this.creditVeteranKill(killer, enemy);
        this.addPlayerUltimate(enemy.role === "siege" || enemy.role === "siege_beast" ? 7 : 4.5);
        this.logger.log("ENEMY_KILLED", this.battleTime, { enemyType: enemy.id, reward: enemy.goldReward, killerId: killer?.id }, enemy.lane);
      },
      onPlayerUnitKilled: (unit) => {
        this.addAiUltimate(unit.role === "siege" || unit.role === "siege_beast" ? 7 : 4.5);
        this.logger.log("PLAYER_UNIT_KILLED", this.battleTime, { unitId: unit.id, level: unit.level }, unit.lane);
      },
      onTowerDamaged: (tower, attacker, damage) => {
        this.logger.log("TOWER_DAMAGED", this.battleTime, { towerId: tower.id, attackerId: attacker.id, damage, hp: tower.hp }, tower.lane);
      },
      onTowerDestroyed: (tower, attacker) => this.handleTowerDestroyed(tower, attacker.id),
      onEnemySummon: (summoner, enemyId) => {
        this.waves.spawnEnemyById(enemyId, summoner.lane, this.enemies, Math.max(120, summoner.x - 42));
        this.logger.log("ENEMY_SUMMONED", this.battleTime, { summonerId: summoner.id, enemyId }, summoner.lane);
      },
      onUnitConverted: (unit, converter) => {
        this.waves.spawnEnemyById("raider", unit.lane, this.enemies, unit.x);
        this.logger.log("UNIT_CONVERTED", this.battleTime, { unitId: unit.id, converterId: converter.id }, unit.lane);
      }
    });
    this.objectives = new ObjectiveSystem(this, this.economy, {
      onSpawned: (objective) => {
        this.logger.log(
          "OBJECTIVE_SPAWNED",
          this.battleTime,
          { objectiveId: objective.id, reward: objective.reward, riskLevel: objective.riskLevel, rewardTier: objective.rewardTier, x: objective.x },
          objective.lane
        );
      },
      onCompleted: (objective, reward) => {
        this.addPlayerUltimate(12);
        this.logger.log(
          "OBJECTIVE_COMPLETED",
          this.battleTime,
          { objectiveId: objective.id, reward, riskLevel: objective.riskLevel, rewardTier: objective.rewardTier },
          objective.lane
        );
        this.buildPanel?.setStatus(`${t(this.settings.language, "objectiveComplete")}: ${this.objectiveRewardLabel(reward)} T${objective.rewardTier}`);
      },
      onExpired: (objective) => {
        this.logger.log(
          "OBJECTIVE_EXPIRED",
          this.battleTime,
          { objectiveId: objective.id, reward: objective.reward, riskLevel: objective.riskLevel, rewardTier: objective.rewardTier },
          objective.lane
        );
      }
    });
    this.tech = new TechSystem(techDiscount(loadPlayerMemory(getOrCreatePlayerId()).upgrades.foresight ?? 0));

    this.aiCastles = this.createCastleGroup("ai");
    this.playerCastles = this.createCastleGroup("player");
    this.aiCastle = this.aiCastles.main;
    this.playerCastle = this.playerCastles.main;
    this.chat = new CommanderChat(this, this.settings.language, this.settings.uiScale, this.settings.highContrastText);
    this.lanePanel = new LaneStatusPanel(this, this.settings.language, this.settings.uiScale, this.settings.highContrastText);
    this.miniMapPanel = new MiniMapPanel(this, {
      language: this.settings.language,
      onFocus: (x, y) => this.focusCameraAt(x, y),
      uiScale: this.settings.uiScale,
      highContrastText: this.settings.highContrastText
    });
    this.buildPanel = new BuildPanel(this, {
      onSelect: (selection) => {
        this.selection = selection;
        this.interactionMode = selection ? "build" : "upgrade";
        if (selection) {
          this.clearSelectedUnits();
        }
      },
      onCounterAttack: () => this.activateCounterAttack(),
      onUpgradeIncome: () => this.upgradeGoldIncome(),
      onUpgradeCounterCharge: () => this.upgradeCounterCharge(),
      onRepairCastle: () => this.repairWeakestCastleSegment(),
      onBuildEconomy: () => this.selectEconomyInvestment(),
      onBuyTech: (tech) => this.researchTech(tech)
    }, this.settings.language, this.settings.uiScale, this.settings.highContrastText);

    this.playerId = getOrCreatePlayerId();
    const profile = loadCommanderProfile();
    const memory = loadPlayerMemory(this.playerId);
    this.memoryVeterans = memory.veterans ?? [];
    this.commanderSocket = new CommanderSocket({
      onCommanderCreated: (createdProfile) => {
        saveCommanderProfile(createdProfile);
        this.chat.setCommander(createdProfile.commanderName, this.commanderTitleWithDoctrine(createdProfile));
        this.chat.setTheme(createdProfile.visualTheme);
      },
      onTaunt: (taunt, mood) => this.chat.pushTaunt(taunt, mood),
      onCommand: (command) => {
        this.eventSystem.apply(command.eventCommand);
        if (command.eventCommand && command.eventCommand.type !== "none") {
          this.logger.log("AI_EVENT_TRIGGERED", this.battleTime, command.eventCommand, command.eventCommand.targetLane);
          if (command.eventCommand.type === "merchant") this.showMerchantOffers(command.eventCommand.power);
        }
        const budgetReports = this.waves.applyGameCommand(command, this.enemies);
        this.logAiBudgetReports(budgetReports);
        this.markAiPriorityTowerTargets(command);
        this.applyAiManeuverCommands(command.maneuverCommands ?? []);
        this.chat.pushTaunt(command.taunt, command.commanderMood);
      },
      onReport: (report) => {
        this.resultReport = report;
        if (this.pendingResult) {
          const updated = updateMemoryFromReport(loadPlayerMemory(this.playerId), this.pendingResult, report);
          savePlayerMemory(updated);
          appendBattleHistory(this.pendingResult, report);
          this.scene.start("ResultScene", { result: this.pendingResult.result, report, battleResult: this.pendingResult });
        }
      },
      onStatus: (status) => {
        this.chat.setConnectionStatus(status);
        if (status === "offline") this.chat.pushTaunt(t(this.settings.language, "commanderOffline"), "calm");
        if (status === "misconfigured") this.chat.pushTaunt(t(this.settings.language, "commanderMisconfigured"), "calm");
        if (status === "health-failed") this.chat.pushTaunt(t(this.settings.language, "commanderHealthFailed"), "calm");
        if (status === "error") this.chat.pushTaunt(t(this.settings.language, "commanderError"), "calm");
      },
      onChat: (reply, mood) => this.chat.pushTaunt(reply, mood)
    });
    if (profile) this.chat.setCommander(profile.commanderName, this.commanderTitleWithDoctrine(profile));
    if (profile?.visualTheme) this.chat.setTheme(profile.visualTheme);
    this.commanderSocket.connect(this.playerId, profile, memory, this.settings.language);
    this.chat.setSendHandler((text) => {
      this.tryHumiliationPlea(text);
      this.commanderSocket?.sendChat(text, this.settings.language, this.createSummary());
    });

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => this.handlePointerDown(pointer));
    this.input.on("pointerup", (pointer: Phaser.Input.Pointer) => this.handlePointerUp(pointer));
    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => this.handlePointerMove(pointer));
    this.input.on("wheel", (pointer: Phaser.Input.Pointer, _targets: unknown, _deltaX: number, deltaY: number) => this.handleWheelZoom(pointer, deltaY));
    this.input.keyboard?.on("keydown-ESC", () => this.cancelTacticalSelection());
    this.input.keyboard?.on("keydown-R", () => this.activatePlayerUltimate());
    this.input.keyboard?.on("keydown-A", () => this.activateSelectedUnitAbility());
    this.input.keyboard?.on("keydown-H", () => this.holdSelectedUnits());
    this.input.keyboard?.on("keydown-G", () => this.advanceSelectedUnits());
    // Pan the camera with the arrow keys (and WASD). addCapture stops the arrows from
    // also scrolling the page.
    this.cameraCursors = this.input.keyboard?.createCursorKeys();
    this.cameraPanKeys = this.input.keyboard?.addKeys("W,A,S,D") as CameraPanKeys | undefined;
    this.input.keyboard?.addCapture("UP,DOWN,LEFT,RIGHT");
    this.createUiCamera();
    this.createFullscreenButton();
    this.createUltimateButton();
    this.createZoomButtons();
    this.createSpeedButton();
    this.initResponsiveLayout();
    this.applyAccessibilityTextSettings();
    if (this.forceTutorial || !hasSeenTutorial()) {
      this.showTutorial();
    }
    this.battleStartedAt = this.time.now;
    this.lastSummaryAt = this.time.now + 8000;
    this.lastEventsAt = this.time.now + 4000;
    this.lastFieldManeuverAt = this.time.now + 14000;
  }

  update(time: number, deltaMs: number) {
    if (this.ended) return;
    const scaledDeltaMs = deltaMs * this.battleTimeScale;

    if (this.tutorialOverlay) {
      this.syncCameraLayers();
      return;
    }

    this.economyOutposts.update(time, this.playerUnits, this.enemies);
    this.economy.economyOutpostCount = this.economyOutposts.playerOwnedCount;
    this.economyInvestments.update(time);
    this.economy.investmentIncomePerSecond = this.economyInvestments.incomePerSecond;
    this.economy.update(scaledDeltaMs, this.playerUnits.filter((unit) => unit.alive).length + this.summonQueue.length);
    this.counter.update(time, scaledDeltaMs);
    this.updateUltimateGauges(time, scaledDeltaMs);
    this.eventSystem.update(time);
    this.waves.tickPending(scaledDeltaMs);
    this.waves.update(time, this.enemies);
    this.objectives.update(time, this.playerUnits);
    this.updateSummonQueue(time);
    this.handleKeyboardPan(deltaMs);
    this.syncCameraLayers();
    this.pruneSelectedUnits();

    this.combat.prepareUnitMovement(this.towers, this.playerUnits, this.enemies);
    for (const unit of [...this.playerUnits, ...this.enemies]) {
      unit.setTerrainSpeedMultiplier(this.terrainSpeedMultiplierForUnit(unit));
      unit.update(time, scaledDeltaMs);
    }
    for (const tower of this.towers) {
      tower.update(time);
    }
    this.updateCrossingControls(scaledDeltaMs);
    this.refreshLaneMoraleCache();
    this.maybeActivateAiUltimate(time);
    this.maybeTriggerAiLastStand(time);
    this.updateUltimateButton();

    this.combat.update(time, scaledDeltaMs, this.towers, this.playerUnits, this.enemies);
    this.updateVeteranProgress(time);
    this.resolveCastleContacts();
    this.updateEncirclementVisuals();
    this.maybeRunMercyDirector(time);
    this.maybeRunNarrativeTaunt(time);
    this.maybeTriggerAiTrap(time);
    this.maybeRunFieldCommanderManeuver(time);
    this.updatePanels();
    this.maybeSendAiMessages(time);

    if (this.playerCastles.main.hp <= 0) {
      this.finishBattle("loss", "castle_breached");
    } else if (this.aiCastles.main.hp <= 0 && modeAllowsWin(this.battleMode)) {
      this.finishBattle("win", "counterattack_success");
    }
  }

  private get battleTime() {
    return Math.floor((this.time.now - this.battleStartedAt) / 1000);
  }

  private commanderTitleWithDoctrine(profile: CommanderProfile) {
    const personality = profile.strategicPersonality ?? this.deriveCommanderPersonality(profile);
    const label = this.settings.language === "ko" ? this.commanderPersonalityKo(personality) : personality;
    return `${profile.commanderTitle} · ${label}`;
  }

  private deriveCommanderPersonality(profile: CommanderProfile): NonNullable<CommanderProfile["strategicPersonality"]> {
    if ((profile.personality?.mockery ?? 0) >= 0.78) return "humiliator";
    if ((profile.personality?.chaos ?? 0) >= 0.62) return "deceptive";
    if ((profile.personality?.patience ?? 0) >= 0.62) return "defensive";
    if ((profile.personality?.aggression ?? 0) >= 0.72) return "aggressive";
    return "attrition";
  }

  private commanderPersonalityKo(personality: string) {
    if (personality === "aggressive") return "공세형";
    if (personality === "defensive") return "방어형";
    if (personality === "deceptive") return "기만형";
    if (personality === "humiliator") return "모욕형";
    return "소모전형";
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer) {
    if (this.tutorialOverlay) return;
    // If the command ring is open and this tap is on it, let the ring buttons handle the
    // press exclusively — don't run battlefield logic that would close/reopen the ring.
    if (this.isPointerOnMobileCommandRing(pointer)) {
      this.mobileCommandRingTap = true;
      this.pointerIntent = undefined;
      return;
    }
    this.mobileCommandRingTap = false;
    this.pointerDownX = pointer.x;
    this.pointerDownY = pointer.y;
    this.pointerDownWorld = this.pointerToWorld(pointer);
    this.pointerStartedInBattlefield = this.isBattlefieldPointer(pointer);
    this.pointerStartedInCameraArea = this.isCameraPointer(pointer);
    this.trackTouchPointer(pointer);
    if (this.activeTouchPointers.size >= 2 && this.pointerStartedInCameraArea) {
      this.startPinchZoom();
      return;
    }
    const touchPan = this.isTouchPointer(pointer) && this.isMultiTouchActive();
    this.pointerIntent = touchPan ? "touchPan" : pointer.middleButtonDown() ? "middle" : pointer.rightButtonDown() ? "right" : "left";

    if (this.pointerIntent === "touchPan" && this.pointerStartedInCameraArea) {
      this.clearSelectionBox();
      this.startCameraDrag(pointer);
      return;
    }

    if (this.pointerIntent === "middle" && this.pointerStartedInCameraArea) {
      this.startCameraDrag(pointer);
      return;
    }

    if (this.pointerIntent === "right" && this.pointerStartedInCameraArea) {
      return;
    }

    if (!this.pointerStartedInBattlefield) return;

    if (this.pointerIntent === "left" && !this.selection) {
      this.startSelectionBox(pointer);
    }
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer) {
    this.trackTouchPointer(pointer);
    if (this.isPinching) {
      this.updatePinchZoom();
      return;
    }

    if (this.isCameraDragging && this.cameraDragPointerId !== undefined && pointer.id !== this.cameraDragPointerId) {
      return;
    }

    if (this.pointerIntent === "left" && this.pointerStartedInBattlefield && !this.selectionBox && this.pointerDistance(pointer) > 8) {
      this.cancelBuildSelectionForUnitCommand();
      this.startSelectionBoxAt(this.pointerDownX, this.pointerDownY);
      this.updateSelectionBox(pointer);
      return;
    }

    if (this.pointerIntent === "left" && this.selectionBox) {
      this.updateSelectionBox(pointer);
      return;
    }

    this.updateCommandPreview(pointer);

    if ((this.pointerIntent === "right" || this.pointerIntent === "touchPan") && this.pointerStartedInCameraArea && !this.isCameraDragging && this.pointerDistance(pointer) > 8) {
      this.startCameraDrag(pointer);
    }

    if (!this.isCameraDragging) return;
    const camera = this.cameras.main;
    camera.scrollX -= (pointer.x - this.lastDragX) / camera.zoom;
    camera.scrollY -= (pointer.y - this.lastDragY) / camera.zoom;
    this.lastDragX = pointer.x;
    this.lastDragY = pointer.y;
    this.clampCameraScroll();
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer) {
    if (this.tutorialOverlay) return;
    // The matching up for a command-ring tap: the ring button's own pointerup handler runs
    // the command, so the scene must not also process this as a battlefield tap.
    if (this.mobileCommandRingTap) {
      this.mobileCommandRingTap = false;
      this.activeTouchPointers.delete(pointer.id);
      return;
    }
    const wasPinching = this.isPinching;
    this.activeTouchPointers.delete(pointer.id);
    if (this.activeTouchPointers.size < 2) {
      this.isPinching = false;
    }
    if (wasPinching) {
      this.isCameraDragging = false;
      this.cameraDragPointerId = undefined;
      this.pointerIntent = undefined;
      this.clearSelectionBox();
      this.pointerStartedInBattlefield = false;
      this.pointerStartedInCameraArea = false;
      return;
    }

    const intent = this.pointerIntent;
    const wasCameraDragging = this.isCameraDragging;
    this.isCameraDragging = false;
    this.cameraDragPointerId = undefined;
    this.pointerIntent = undefined;

    if (!intent || !this.pointerDownWorld) {
      this.clearSelectionBox();
      this.pointerStartedInBattlefield = false;
      this.pointerStartedInCameraArea = false;
      return;
    }

    if (intent === "right" && !wasCameraDragging && this.pointerStartedInBattlefield && this.pointerDistance(pointer) <= 8 && this.isBattlefieldPointer(pointer)) {
      this.issueMoveCommandAt(this.pointerToWorld(pointer));
      this.clearSelectionBox();
      this.pointerStartedInBattlefield = false;
      this.pointerStartedInCameraArea = false;
      return;
    }

    if (intent === "left" && this.pointerStartedInBattlefield && !wasCameraDragging) {
      if (this.selectionBox && this.pointerDistance(pointer) > 6) {
        this.selectUnitsInBox(pointer);
      } else {
        this.handleBattleClick(pointer);
      }
    }

    this.clearSelectionBox();
    this.pointerStartedInBattlefield = false;
    this.pointerStartedInCameraArea = false;
  }

  private handleBattleClick(pointer: Phaser.Input.Pointer) {
    if (!this.isBattlefieldPointer(pointer)) return;
    const worldPoint = this.pointerToWorld(pointer);
    if (!this.selection && this.interactionMode !== "upgrade") {
      const tower = this.findTowerAt(worldPoint);
      if (tower) {
        this.showTowerPriorityMenu(tower);
        return;
      }
    }
    const touchCommandTap = !this.selection && this.selectedPlayerUnits.size > 0 && this.isTouchPointer(pointer);
    if (this.selectSingleUnitAt(worldPoint, this.isSelectionAdditive(pointer), touchCommandTap)) {
      this.clearMobileCommandRing();
      this.clearTowerPriorityMenu();
      this.cancelBuildSelectionForUnitCommand();
      return;
    }

    if (!this.selection && this.interactionMode === "upgrade") {
      this.tryUpgradeAt(worldPoint.x, worldPoint.y);
      return;
    }

    if (!this.selection && this.selectedPlayerUnits.size > 0 && this.isTouchPointer(pointer)) {
      this.clearTowerPriorityMenu();
      this.showMobileCommandRing(worldPoint, pointer);
      return;
    }

    if (!this.selection) {
      this.clearMobileCommandRing();
      this.clearTowerPriorityMenu();
      return;
    }

    if (this.selection.kind === "tower") {
      this.placeTower(worldPoint.x, worldPoint.y, this.selection.id);
    } else if (this.selection.kind === "economy") {
      const lane = this.nearestLane(worldPoint.y);
      if (lane) {
        this.buildEconomyInvestment(lane);
      }
    } else {
      const lane = this.nearestLane(worldPoint.y);
      if (lane) {
        this.summonPlayerUnit(this.selection.id, lane);
      }
    }
  }

  private isBattlefieldPointer(pointer: Phaser.Input.Pointer) {
    return pointer.y >= 48 && pointer.y <= 610;
  }

  private isCameraPointer(pointer: Phaser.Input.Pointer) {
    return pointer.y <= 632;
  }

  private pointerDistance(pointer: Phaser.Input.Pointer) {
    return Phaser.Math.Distance.Between(pointer.x, pointer.y, this.pointerDownX, this.pointerDownY);
  }

  private isSelectionAdditive(pointer: Phaser.Input.Pointer) {
    return Boolean((pointer.event as MouseEvent | undefined)?.shiftKey);
  }

  private isTouchPointer(pointer: Phaser.Input.Pointer) {
    const event = pointer.event as PointerEvent | TouchEvent | MouseEvent | undefined;
    if (event && "pointerType" in event) return event.pointerType === "touch" || event.pointerType === "pen";
    return Boolean(event?.type.startsWith("touch"));
  }

  private isMultiTouchActive() {
    return Boolean(this.input.pointer1?.isDown && this.input.pointer2?.isDown);
  }

  private trackTouchPointer(pointer: Phaser.Input.Pointer) {
    if (!this.isTouchPointer(pointer)) return;
    if (pointer.isDown) {
      this.activeTouchPointers.set(pointer.id, { x: pointer.x, y: pointer.y });
    } else {
      this.activeTouchPointers.delete(pointer.id);
    }
  }

  private startPinchZoom() {
    if (!this.settings.beta.cameraZoom) return;
    const ids = [...this.activeTouchPointers.keys()].slice(0, 2);
    if (ids.length < 2) return;
    this.clearSelectionBox();
    this.isCameraDragging = false;
    this.cameraDragPointerId = undefined;
    this.isPinching = true;
    this.pointerIntent = "touchPan";
    this.pinchPointerIds = [ids[0]!, ids[1]!];
    this.seedPinchBaseline();
  }

  /** The two tracked touch points currently driving the pinch, or null if a tracked finger lifted. */
  private pinchTrackedPoints(): [{ x: number; y: number }, { x: number; y: number }] | null {
    if (!this.pinchPointerIds) return null;
    const a = this.activeTouchPointers.get(this.pinchPointerIds[0]);
    const b = this.activeTouchPointers.get(this.pinchPointerIds[1]);
    if (!a || !b) return null;
    return [a, b];
  }

  /** Capture the current finger distance + camera zoom as the baseline for the active pinch. */
  private seedPinchBaseline() {
    const points = this.pinchTrackedPoints();
    if (!points) return;
    this.pinchStartDistance = Phaser.Math.Distance.Between(points[0].x, points[0].y, points[1].x, points[1].y);
    this.pinchStartZoom = this.cameras.main.zoom;
  }

  private updatePinchZoom() {
    if (!this.settings.beta.cameraZoom) return;
    let points = this.pinchTrackedPoints();
    if (!points) {
      // A tracked finger lifted (or a different pair is now down). Re-seat the baseline
      // from the current top-2 touches so the zoom continues smoothly instead of jumping.
      const ids = [...this.activeTouchPointers.keys()].slice(0, 2);
      if (ids.length < 2) {
        this.isPinching = false;
        this.pinchPointerIds = undefined;
        return;
      }
      this.pinchPointerIds = [ids[0]!, ids[1]!];
      this.seedPinchBaseline();
      points = this.pinchTrackedPoints();
      if (!points) return;
    }
    const distance = Phaser.Math.Distance.Between(points[0].x, points[0].y, points[1].x, points[1].y);
    if (this.pinchStartDistance <= 0 || distance <= 0) return;
    const centerX = (points[0].x + points[1].x) / 2;
    const centerY = (points[0].y + points[1].y) / 2;
    const camera = this.cameras.main;
    const before = camera.getWorldPoint(centerX, centerY);
    const nextZoom = clampZoom(this.pinchStartZoom * (distance / this.pinchStartDistance), this.currentFitZoom());
    camera.setZoom(nextZoom);
    // setZoom() leaves the camera's transform matrix stale until the next preRender, so
    // getWorldPoint() would return pre-zoom coords and the focal correction below would
    // shove the camera off the map (revealing the black void). Refresh it first.
    camera.preRender();
    const after = camera.getWorldPoint(centerX, centerY);
    camera.scrollX += before.x - after.x;
    camera.scrollY += before.y - after.y;
    this.clampCameraScroll();
    this.showZoomToast(nextZoom);
  }

  private startCameraDrag(pointer: Phaser.Input.Pointer) {
    this.clearCommandPreview();
    this.clearTowerPriorityMenu();
    this.interactionMode = "cameraPan";
    this.isCameraDragging = true;
    this.cameraDragPointerId = pointer.id;
    this.lastDragX = pointer.x;
    this.lastDragY = pointer.y;
  }

  private startSelectionBox(pointer: Phaser.Input.Pointer) {
    this.startSelectionBoxAt(pointer.x, pointer.y);
  }

  private startSelectionBoxAt(x: number, y: number) {
    this.clearCommandPreview();
    this.clearTowerPriorityMenu();
    this.selectionBox?.destroy();
    this.selectionBox = this.add
      .rectangle(x, y, 1, 1, 0x5de9ff, 0.08)
      .setStrokeStyle(1, 0x9df5ff, 0.86)
      .setDepth(180)
      .setScrollFactor(0);
  }

  private cancelBuildSelectionForUnitCommand() {
    if (!this.selection && this.interactionMode === "unitCommand") return;
    this.selection = undefined;
    this.interactionMode = "unitCommand";
    this.buildPanel.clearSelectedBuild();
  }

  private cancelTacticalSelection() {
    this.selection = undefined;
    this.interactionMode = "unitCommand";
    this.clearSelectedUnits();
    this.clearCommandPreview();
    this.clearTowerPriorityMenu();
    this.clearSelectionBox();
    this.clearMobileCommandRing();
    this.buildPanel.clearSelectedBuild();
    this.buildPanel.setStatus(this.unitCommandHint());
  }

  private updateSelectionBox(pointer: Phaser.Input.Pointer) {
    if (!this.selectionBox) return;
    const left = Math.min(this.pointerDownX, pointer.x);
    const top = Math.min(this.pointerDownY, pointer.y);
    const width = Math.abs(pointer.x - this.pointerDownX);
    const height = Math.abs(pointer.y - this.pointerDownY);
    this.selectionBox.setPosition(left + width / 2, top + height / 2);
    this.selectionBox.setSize(Math.max(1, width), Math.max(1, height));
  }

  private clearSelectionBox() {
    this.selectionBox?.destroy();
    this.selectionBox = undefined;
    if (this.interactionMode === "cameraPan") {
      this.interactionMode = this.selection ? "build" : "unitCommand";
    }
  }

  private showMobileCommandRing(worldPoint: Phaser.Math.Vector2, pointer: Phaser.Input.Pointer) {
    this.clearMobileCommandRing();
    // Snap the stored target to the lane the player aimed at so the destination
    // is identical to where the units will actually march, and preview it.
    const previewLane = this.nearestLane(worldPoint.y);
    const snappedY = previewLane ? this.laneY(previewLane) : worldPoint.y;
    this.mobileCommandRingWorld = new Phaser.Math.Vector2(worldPoint.x, snappedY);
    this.showCommandTargetPreview(worldPoint.x, snappedY);
    const abilityUnit = this.selectedAbilityUnit();
    const hasAbility = Boolean(abilityUnit);
    const x = Phaser.Math.Clamp(pointer.x, 96, mapConfig.designWidth - 96);
    const y = Phaser.Math.Clamp(pointer.y - 58, 92, 548);
    const ring = this.add.container(x, y).setDepth(190).setScrollFactor(0);
    // Tap radius must reach the outermost button (the Hold button sits at y = +92/+58),
    // so the scene's hit-test claims the whole ring, not just the backing circle.
    this.mobileCommandRingRadius = hasAbility ? 120 : 86;
    ring.add(this.add.circle(0, 0, hasAbility ? 88 : 74, 0x071118, 0.72).setStrokeStyle(1, 0x8de7ff, 0.64));
    ring.add(this.createMobileCommandButton(0, hasAbility ? -54 : -44, t(this.settings.language, "commandMove"), 0x5de9ff, () => this.runMobileCommand("move")));
    ring.add(this.createMobileCommandButton(-66, hasAbility ? 22 : 30, t(this.settings.language, "commandRetreat"), 0xf7d66f, () => this.runMobileCommand("retreat")));
    ring.add(this.createMobileCommandButton(66, hasAbility ? 22 : 30, t(this.settings.language, "commandAdvance"), 0xff8c3a, () => this.runMobileCommand("advance")));
    ring.add(this.createMobileCommandButton(0, hasAbility ? 92 : 58, t(this.settings.language, "commandHold"), 0xc9b5ff, () => this.runMobileCommand("hold")));
    if (abilityUnit) {
      ring.add(this.createMobileCommandButton(0, 58, this.unitAbilityShortLabel(abilityUnit.manualAbility), 0xa7ffcf, () => this.runMobileAbility()));
    }
    this.mobileCommandRing = ring;
  }

  private createMobileCommandButton(x: number, y: number, label: string, color: number, onClick: () => void) {
    const button = this.add.container(x, y);
    const bg = this.add.circle(0, 0, 28, 0x101820, 0.94).setStrokeStyle(2, color, 0.9);
    const text = this.add
      .text(0, 0, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "12px" : "10px",
        color: "#f7f2df",
        stroke: "#08070a",
        strokeThickness: 3,
        fixedWidth: 48
      })
      .setResolution(2)
      .setOrigin(0.5)
      .setAlign("center");
    button.add([bg, text]);
    button.setSize(56, 56);
    button.setInteractive({ useHandCursor: true });
    // Fire on pointerup: on iOS Safari a tap reliably delivers pointerup to the button,
    // whereas pointerdown can be pre-empted. Guard against the press being lost if the
    // pointer leaves the button before release by also honoring pointerout->up is skipped.
    let pressed = false;
    button.on("pointerdown", () => {
      pressed = true;
      this.mobileCommandRingTap = true;
      bg.setFillStyle(0x1d3346, 0.98);
    });
    button.on("pointerup", () => {
      bg.setFillStyle(0x101820, 0.94);
      if (!pressed) return;
      pressed = false;
      onClick();
    });
    button.on("pointerout", () => {
      pressed = false;
      bg.setFillStyle(0x101820, 0.94);
    });
    return button;
  }

  private holdSelectedUnits() {
    const units = [...this.selectedPlayerUnits].filter((unit) => unit.alive);
    if (units.length === 0) {
      this.buildPanel.setStatus(t(this.settings.language, "noUnitsSelected"));
      return;
    }
    for (const unit of units) {
      unit.holdInPlace(this.time.now);
      this.logger.log("UNIT_MOVE_COMMAND", this.battleTime, { unitId: unit.id, fromLane: unit.lane, targetLane: unit.lane, mode: "hold" }, unit.lane);
    }
    this.buildPanel.setStatus(t(this.settings.language, "commandHoldSet"));
  }

  private advanceSelectedUnits() {
    const units = [...this.selectedPlayerUnits].filter((unit) => unit.alive);
    if (units.length === 0) {
      this.buildPanel.setStatus(t(this.settings.language, "noUnitsSelected"));
      return;
    }
    for (const unit of units) {
      unit.resumeAdvance();
      this.logger.log("UNIT_MOVE_COMMAND", this.battleTime, { unitId: unit.id, fromLane: unit.lane, targetLane: unit.lane, mode: "advance" }, unit.lane);
    }
    this.buildPanel.setStatus(t(this.settings.language, "commandAdvanceSet"));
  }

  private runMobileCommand(mode: "move" | "retreat" | "advance" | "hold") {
    if (mode === "hold") {
      this.clearMobileCommandRing();
      this.holdSelectedUnits();
      return;
    }
    const target = this.mobileCommandRingWorld?.clone();
    this.clearMobileCommandRing();
    if (!target) return;
    const targetLane = this.nearestLane(target.y);
    if (!targetLane) {
      this.showCommandReject(target.x, target.y, t(this.settings.language, "noCrossingAvailable"));
      return;
    }
    if (mode === "retreat") target.x = mapConfig.playerCastleX - 92;
    if (mode === "advance") target.x = mapConfig.aiFortressX + 120;
    target.y = this.laneY(targetLane);
    this.issuePlayerUnitCommandAt(target, mode);
  }

  private runMobileAbility() {
    this.clearMobileCommandRing();
    this.activateSelectedUnitAbility();
  }

  // A pulsing reticle drawn in world space at the exact lane-snapped spot the
  // units will move to, so the player can see the real destination before
  // confirming the order.
  private showCommandTargetPreview(x: number, y: number) {
    const marker = this.add.graphics().setDepth(67);
    marker.fillStyle(0x5de9ff, 0.18);
    marker.fillCircle(x, y, 13);
    marker.lineStyle(2, 0x9feaff, 0.95);
    marker.strokeCircle(x, y, 13);
    marker.lineBetween(x - 20, y, x + 20, y);
    marker.lineBetween(x, y - 16, x, y + 16);
    this.mobileCommandTargetMarker = marker;
    this.tweens.add({
      targets: marker,
      alpha: { from: 0.5, to: 1 },
      duration: 520,
      yoyo: true,
      repeat: -1
    });
  }

  /** True when the command ring is open and the pointer is within its circular tap area. */
  private isPointerOnMobileCommandRing(pointer: Phaser.Input.Pointer) {
    const ring = this.mobileCommandRing;
    if (!ring) return false;
    // The ring is a scrollFactor-0 HUD object, so its position is in design/UI space —
    // the same space as pointer.x/pointer.y under FIT. A small margin makes the thumb-
    // sized buttons forgiving to tap.
    return Phaser.Math.Distance.Between(pointer.x, pointer.y, ring.x, ring.y) <= this.mobileCommandRingRadius + 16;
  }

  private clearMobileCommandRing() {
    this.mobileCommandRing?.destroy();
    this.mobileCommandRing = undefined;
    this.mobileCommandRingWorld = undefined;
    this.mobileCommandRingTap = false;
    if (this.mobileCommandTargetMarker) {
      this.tweens.killTweensOf(this.mobileCommandTargetMarker);
      this.mobileCommandTargetMarker.destroy();
      this.mobileCommandTargetMarker = undefined;
    }
  }

  private selectedAbilityUnit() {
    const now = this.time.now;
    return [...this.selectedPlayerUnits]
      .filter((unit) => unit.alive && unit.getManualAbilityState(now))
      .sort((a, b) => {
        const aState = a.getManualAbilityState(now)!;
        const bState = b.getManualAbilityState(now)!;
        return Number(bState.ready) - Number(aState.ready) || aState.cooldownRemainingMs - bState.cooldownRemainingMs || a.lane - b.lane;
      })[0];
  }

  private activateSelectedUnitAbility() {
    const unit = this.selectedAbilityUnit();
    if (!unit) {
      this.buildPanel?.setStatus(t(this.settings.language, "unitAbilityNone"));
      return;
    }

    const state = unit.getManualAbilityState(this.time.now);
    if (!state) return;
    if (!state.ready) {
      this.buildPanel.setStatus(`${t(this.settings.language, "unitAbilityCooldown")} ${Math.ceil(state.cooldownRemainingMs / 1000)}s`);
      return;
    }

    const ability = state.id;
    const affected = this.previewAbilityTargets(unit, ability);
    if ((ability === "fire_burst" || ability === "repair_burst") && affected <= 0) {
      this.buildPanel.setStatus(t(this.settings.language, "unitAbilityNoTarget"));
      return;
    }
    if (!unit.activateManualAbility(this.time.now)) {
      this.buildPanel.setStatus(`${t(this.settings.language, "unitAbilityCooldown")} ${Math.ceil((unit.getManualAbilityState(this.time.now)?.cooldownRemainingMs ?? 0) / 1000)}s`);
      return;
    }

    const resolved = this.resolveUnitAbility(unit, ability);
    this.showUnitAbilityPulse(unit, this.unitAbilityColor(ability), this.unitAbilityLabel(ability));
    this.logger.log("UNIT_ABILITY_USED", this.battleTime, { unitId: unit.id, abilityId: ability, affected: resolved }, unit.lane);
    this.recentActions.push(`manual_ability_${ability}_lane_${unit.lane}`);
    this.buildPanel.setStatus(`${t(this.settings.language, "unitAbilityUsed")}: ${this.unitAbilityLabel(ability)}`);
    this.updateUnitAbilityButton();
  }

  private previewAbilityTargets(unit: Unit, ability: Exclude<UnitManualAbilityId, "none">) {
    if (ability === "fire_burst") {
      return this.enemies.filter((enemy) => enemy.alive && enemy.lane === unit.lane && Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y) <= this.fireBurstRadius(unit)).length;
    }
    if (ability === "repair_burst") {
      const damagedTowers = this.towers.filter((tower) => !tower.destroyed && tower.lane === unit.lane && tower.hp < tower.maxHp && Phaser.Math.Distance.Between(unit.x, unit.y, tower.x, tower.y) <= 165).length;
      const woundedUnits = this.playerUnits.filter((ally) => ally.alive && ally.lane === unit.lane && ally.hp < ally.maxHp && Phaser.Math.Distance.Between(unit.x, unit.y, ally.x, ally.y) <= 135).length;
      return damagedTowers + woundedUnits;
    }
    return 1;
  }

  private resolveUnitAbility(unit: Unit, ability: Exclude<UnitManualAbilityId, "none">) {
    if (ability === "shield_wall") {
      unit.clearMoveCommand(this.time.now, 8000);
      return 1;
    }
    if (ability === "rally_banner") {
      return this.playerUnits.filter((ally) => ally.alive && ally.lane === unit.lane && Phaser.Math.Distance.Between(unit.x, unit.y, ally.x, ally.y) <= 170).length;
    }
    if (ability === "fire_burst") {
      const damage = unit.id === "powder_carrier" ? 54 + unit.level * 8 : 38 + unit.level * 7;
      let affected = 0;
      for (const enemy of this.enemies) {
        if (!enemy.alive || enemy.lane !== unit.lane) continue;
        if (Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y) > this.fireBurstRadius(unit)) continue;
        enemy.takeDamage(damage);
        enemy.applySlow(0.18, this.time.now + 1000);
        this.showAbilityHit(enemy.x, enemy.y, 0xff8c3a);
        affected += 1;
      }
      return affected;
    }

    const towerRepair = 42 + unit.level * 9;
    const unitHeal = 28 + unit.level * 6;
    let affected = 0;
    for (const tower of this.towers) {
      if (tower.destroyed || tower.lane !== unit.lane || tower.hp >= tower.maxHp) continue;
      if (Phaser.Math.Distance.Between(unit.x, unit.y, tower.x, tower.y) > 165) continue;
      tower.heal(towerRepair);
      this.showAbilityHit(tower.x, tower.y, 0xa7ffcf);
      affected += 1;
    }
    for (const ally of this.playerUnits) {
      if (!ally.alive || ally.lane !== unit.lane || ally.hp >= ally.maxHp) continue;
      if (Phaser.Math.Distance.Between(unit.x, unit.y, ally.x, ally.y) > 135) continue;
      ally.heal(unitHeal);
      this.showAbilityHit(ally.x, ally.y, 0xa7ffcf);
      affected += 1;
    }
    return affected;
  }

  private fireBurstRadius(unit: Unit) {
    return unit.id === "powder_carrier" ? 118 : 104;
  }

  private unitAbilityColor(ability: Exclude<UnitManualAbilityId, "none">) {
    if (ability === "shield_wall") return 0x9feaff;
    if (ability === "fire_burst") return 0xff8c3a;
    if (ability === "repair_burst") return 0xa7ffcf;
    return 0xffe58e;
  }

  private unitAbilityLabel(ability: UnitManualAbilityId) {
    if (ability === "shield_wall") return t(this.settings.language, "abilityShieldWall");
    if (ability === "fire_burst") return t(this.settings.language, "abilityFireBurst");
    if (ability === "repair_burst") return t(this.settings.language, "abilityRepairBurst");
    if (ability === "rally_banner") return t(this.settings.language, "abilityRallyBanner");
    return t(this.settings.language, "unitAbility");
  }

  private unitAbilityShortLabel(ability: UnitManualAbilityId) {
    const label = this.unitAbilityLabel(ability);
    return this.settings.language === "ko" ? label.slice(0, 3) : label.split(" ")[0] ?? label;
  }

  private selectUnitsInBox(pointer: Phaser.Input.Pointer) {
    if (!this.selectionBox) return;
    this.clearMobileCommandRing();
    const camera = this.cameras.main;
    const left = this.selectionBox.x - this.selectionBox.width / 2;
    const right = this.selectionBox.x + this.selectionBox.width / 2;
    const top = this.selectionBox.y - this.selectionBox.height / 2;
    const bottom = this.selectionBox.y + this.selectionBox.height / 2;
    const topLeft = camera.getWorldPoint(left, top);
    const bottomRight = camera.getWorldPoint(right, bottom);
    const worldRect = new Phaser.Geom.Rectangle(
      Math.min(topLeft.x, bottomRight.x),
      Math.min(topLeft.y, bottomRight.y),
      Math.abs(bottomRight.x - topLeft.x),
      Math.abs(bottomRight.y - topLeft.y)
    );

    if (!this.isSelectionAdditive(pointer)) {
      this.clearSelectedUnits();
    }
    for (const unit of this.playerUnits) {
      if (unit.alive && Phaser.Geom.Rectangle.Contains(worldRect, unit.x, unit.y)) {
        this.selectedPlayerUnits.add(unit);
      }
    }
    this.refreshSelectedUnitVisuals();
    this.buildPanel.setStatus(
      this.selectedPlayerUnits.size > 0
        ? `${this.selectedPlayerUnits.size} ${t(this.settings.language, "unitsSelected")}`
        : t(this.settings.language, "noUnitsSelected")
    );
  }

  private selectSingleUnitAt(worldPoint: Phaser.Math.Vector2, additive = false, preserveSelectionOnMiss = false) {
    const unit = this.playerUnits
      .filter((candidate) => candidate.alive)
      .sort((a, b) => Phaser.Math.Distance.Between(worldPoint.x, worldPoint.y, a.x, a.y) - Phaser.Math.Distance.Between(worldPoint.x, worldPoint.y, b.x, b.y))[0];
    if (unit && Phaser.Math.Distance.Between(worldPoint.x, worldPoint.y, unit.x, unit.y) <= 34) {
      const doubleClick = this.lastClickedUnit === unit && this.time.now - this.lastUnitClickAt <= 360;
      this.lastClickedUnit = unit;
      this.lastUnitClickAt = this.time.now;
      if (!additive && !doubleClick) {
        this.clearSelectedUnits();
      }
      if (doubleClick) {
        this.selectLaneUnits(unit.lane, additive);
        return true;
      }
      if (additive && this.selectedPlayerUnits.has(unit)) {
        this.selectedPlayerUnits.delete(unit);
        this.selectedUnitRings.get(unit)?.destroy();
        this.selectedUnitRings.delete(unit);
      } else {
        this.selectedPlayerUnits.add(unit);
      }
      this.refreshSelectedUnitVisuals();
      this.setSelectedUnitStatus();
      return true;
    } else {
      if (!additive && !preserveSelectionOnMiss) {
        this.clearSelectedUnits();
      }
      if (!preserveSelectionOnMiss) {
        this.buildPanel.setStatus(this.unitCommandHint());
      }
      return false;
    }
  }

  private selectLaneUnits(lane: LaneNumber, additive = false) {
    if (!additive) {
      this.clearSelectedUnits();
    }
    for (const unit of this.playerUnits) {
      if (unit.alive && unit.lane === lane) {
        this.selectedPlayerUnits.add(unit);
      }
    }
    this.refreshSelectedUnitVisuals();
    this.setSelectedUnitStatus();
  }

  private clearSelectedUnits() {
    this.selectedPlayerUnits.clear();
    this.clearSelectionRings();
    this.clearMobileCommandRing();
    this.clearCommandPreview();
    this.clearTowerPriorityMenu();
    this.destroyUnitAbilityButton();
  }

  private pruneSelectedUnits() {
    for (const unit of [...this.selectedPlayerUnits]) {
      if (!unit.alive) {
        this.selectedPlayerUnits.delete(unit);
        this.selectedUnitRings.get(unit)?.destroy();
        this.selectedUnitRings.delete(unit);
      }
    }
    this.refreshSelectedUnitVisuals();
  }

  private refreshSelectedUnitVisuals() {
    this.clearSelectionRingsForUnselectedUnits();
    for (const unit of this.selectedPlayerUnits) {
      if (!unit.alive) continue;
      let ring = this.selectedUnitRings.get(unit);
      if (!ring) {
        ring = this.add.graphics().setDepth(44);
        this.selectedUnitRings.set(unit, ring);
      }
      ring.clear();
      ring.lineStyle(2, 0x9feaff, 0.88);
      ring.fillStyle(0x5de9ff, 0.08);
      ring.fillEllipse(unit.x, unit.y + 17, 45, 18);
      ring.strokeEllipse(unit.x, unit.y + 17, 45, 18);
    }
  }

  private setSelectedUnitStatus() {
    this.buildPanel.setStatus(
      this.selectedPlayerUnits.size > 0
        ? `${this.selectedPlayerUnits.size} ${t(this.settings.language, "unitsSelected")}`
        : t(this.settings.language, "noUnitsSelected")
    );
  }

  private clearSelectionRings() {
    for (const ring of this.selectedUnitRings.values()) {
      ring.destroy();
    }
    this.selectedUnitRings.clear();
  }

  private clearSelectionRingsForUnselectedUnits() {
    for (const [unit, ring] of [...this.selectedUnitRings]) {
      if (!this.selectedPlayerUnits.has(unit) || !unit.alive) {
        ring.destroy();
        this.selectedUnitRings.delete(unit);
      }
    }
  }

  private updateEncirclementVisuals() {
    const encircled = new Set<Unit>();
    const encircling = new Set<Unit>();

    for (const unit of this.playerUnits) {
      if (!unit.alive) continue;
      const surrounding = this.surroundingUnits(unit, this.enemies);
      if (surrounding.length >= 2) {
        encircled.add(unit);
        surrounding.forEach((enemy) => encircling.add(enemy));
      }
    }

    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      const surrounding = this.surroundingUnits(enemy, this.playerUnits);
      if (surrounding.length >= 2) {
        encircled.add(enemy);
        surrounding.forEach((unit) => encircling.add(unit));
      }
    }

    for (const [unit, ring] of [...this.moraleRings]) {
      if (!unit.alive || (!encircled.has(unit) && !encircling.has(unit))) {
        ring.destroy();
        this.moraleRings.delete(unit);
      }
    }

    for (const unit of [...encircling, ...encircled]) {
      if (!unit.alive) continue;
      const isBroken = encircled.has(unit);
      const color = isBroken ? (unit.faction === "player" ? 0xff604f : 0xff8c3a) : 0xf7e9bb;
      const alpha = isBroken ? 0.72 : 0.42;
      const width = isBroken ? 3 : 2;
      let ring = this.moraleRings.get(unit);
      if (!ring) {
        ring = this.add.graphics().setDepth(unit.faction === "player" ? 46 : 43);
        this.moraleRings.set(unit, ring);
      }
      ring.clear();
      ring.lineStyle(width, color, alpha);
      ring.fillStyle(color, isBroken ? 0.06 : 0.035);
      ring.fillEllipse(unit.x, unit.y + 19, isBroken ? 50 : 40, isBroken ? 20 : 15);
      ring.strokeEllipse(unit.x, unit.y + 19, isBroken ? 50 : 40, isBroken ? 20 : 15);
      if (isBroken) {
        ring.lineBetween(unit.x - 15, unit.y + 13, unit.x - 3, unit.y + 24);
        ring.lineBetween(unit.x + 4, unit.y + 13, unit.x + 17, unit.y + 23);
      }
    }
  }

  private surroundingUnits(unit: Unit, opponents: Unit[]) {
    const nearby = opponents.filter((opponent) => opponent.alive && opponent.lane === unit.lane && Math.abs(opponent.x - unit.x) <= 138);
    if (!nearby.some((opponent) => opponent.x < unit.x - 18) || !nearby.some((opponent) => opponent.x > unit.x + 18)) return [];
    const left = nearby
      .filter((opponent) => opponent.x < unit.x - 18)
      .sort((a, b) => Math.abs(a.x - unit.x) - Math.abs(b.x - unit.x))[0];
    const right = nearby
      .filter((opponent) => opponent.x > unit.x + 18)
      .sort((a, b) => Math.abs(a.x - unit.x) - Math.abs(b.x - unit.x))[0];
    return [left, right].filter((candidate): candidate is Unit => Boolean(candidate));
  }

  private clearMoraleRings() {
    for (const ring of this.moraleRings.values()) {
      ring.destroy();
    }
    this.moraleRings.clear();
  }

  private issueMoveCommandAt(worldPoint: Phaser.Math.Vector2) {
    this.clearCommandPreview();
    this.issuePlayerUnitCommandAt(worldPoint, "move");
  }

  private issuePlayerUnitCommandAt(worldPoint: Phaser.Math.Vector2, mode: "move" | "retreat" | "advance") {
    if (this.selectedPlayerUnits.size === 0) {
      this.buildPanel.setStatus(t(this.settings.language, "noUnitsSelected"));
      this.showCommandReject(worldPoint.x, worldPoint.y, t(this.settings.language, "noUnitsSelected"));
      return;
    }

    const targetLane = this.nearestLane(worldPoint.y);
    if (!targetLane) {
      this.buildPanel.setStatus(t(this.settings.language, "noCrossingAvailable"));
      this.showCommandReject(worldPoint.x, worldPoint.y, t(this.settings.language, "noCrossingAvailable"));
      return;
    }

    let issued = 0;
    let blocked = 0;
    const commandDirection = this.commandDirectionForGroup([...this.selectedPlayerUnits], worldPoint.x);
    const units = [...this.selectedPlayerUnits]
      .filter((unit) => unit.alive)
      .sort((a, b) => a.lane - b.lane || this.formationRank(a) - this.formationRank(b) || this.sortByDirection(a, b, commandDirection));
    const offsetBias = this.formationOffsetBias(units);
    for (let index = 0; index < units.length; index += 1) {
      const unit = units[index]!;
      if (!unit.alive) continue;
      const targetX = this.formationTargetX(unit, worldPoint.x, index, units.length, commandDirection, offsetBias);
      const waypoints = this.createUnitPath(unit, targetLane, targetX);
      if (waypoints.length === 0) {
        blocked += 1;
        continue;
      }
      const fromLane = unit.lane;
      unit.commandMove(waypoints, this.time.now, mode === "retreat" ? 9000 : 6000, mode);
      this.showMovePath([{ x: unit.x, y: unit.y }, ...waypoints], { lineColor: 0x9feaff, fillColor: 0x9feaff });
      this.logger.log("UNIT_MOVE_COMMAND", this.battleTime, { unitId: unit.id, fromLane, targetLane, targetX: Math.round(targetX), mode }, fromLane);
      if (fromLane !== targetLane) {
        this.logger.log("UNIT_LANE_CHANGED", this.battleTime, { unitId: unit.id, owner: "player", fromLane, targetLane }, fromLane);
      }
      issued += 1;
    }

    if (issued > 0) {
      this.showCommandMarker(worldPoint.x, this.laneY(targetLane), issued);
      this.time.delayedCall(220, () => this.refreshSelectedUnitVisuals());
    } else if (blocked > 0) {
      this.showCommandReject(worldPoint.x, this.laneY(targetLane), t(this.settings.language, "noCrossingAvailable"));
    }
    this.buildPanel.setStatus(issued > 0 ? t(this.settings.language, "moveCommandIssued") : t(this.settings.language, "noCrossingAvailable"));
  }

  private formationTargetX(unit: Unit, targetX: number, index: number, total: number, direction?: -1 | 1, offsetBias = 0) {
    const commandDirection = direction ?? (targetX < unit.x ? -1 : 1);
    // Center the role stagger on the click point: subtracting the group's mean
    // offset keeps tanks/siege spaced apart relative to each other while making
    // the formation's centroid land exactly where the player pointed. A single
    // unit has offsetBias === its own offset, so it stops on the click.
    const roleOffset = this.formationRoleOffset(unit) - offsetBias;
    const spread = (index - (total - 1) / 2) * 10;
    return targetX - commandDirection * roleOffset + spread;
  }

  // Mean role offset across the commanded group, used to recenter the formation
  // on the click point (see formationTargetX).
  private formationOffsetBias(units: Unit[]) {
    const alive = units.filter((unit) => unit.alive);
    if (alive.length === 0) return 0;
    return alive.reduce((sum, unit) => sum + this.formationRoleOffset(unit), 0) / alive.length;
  }

  private commandDirectionForGroup(units: Unit[], targetX: number): -1 | 1 {
    const alive = units.filter((unit) => unit.alive);
    if (alive.length === 0) return targetX < mapConfig.playerSpawnX ? -1 : 1;
    const averageX = alive.reduce((total, unit) => total + unit.x, 0) / alive.length;
    return targetX < averageX ? -1 : 1;
  }

  private sortByDirection(a: Unit, b: Unit, direction: -1 | 1) {
    return direction === -1 ? a.x - b.x : b.x - a.x;
  }

  private formationRank(unit: Unit) {
    const role = unit.role;
    if (role === "defense" || role === "tank" || role === "elite") return 0;
    if (role === "attack" || role === "rush" || role === "berserker" || role === "flanker") return 1;
    if (role === "reach" || role === "explosive" || role === "exploder") return 2;
    if (role === "siege" || role === "siege_beast") return 4;
    return 3;
  }

  private formationRoleOffset(unit: Unit) {
    const role = unit.role;
    if (role === "defense" || role === "tank" || role === "elite") return 0;
    if (role === "attack" || role === "rush" || role === "berserker" || role === "flanker") return 26;
    if (role === "reach" || role === "explosive" || role === "exploder") return 48;
    if (role === "siege" || role === "siege_beast") return 112;
    return 78;
  }

  private createUnitPath(unit: Unit, targetLane: LaneNumber, targetX: number): UnitWaypoint[] {
    const safeTargetX = Phaser.Math.Clamp(targetX, mapConfig.aiFortressX + 84, mapConfig.playerCastleX - 84);
    const waypoints: UnitWaypoint[] = [];
    let lane = unit.lane;
    let x = unit.x;

    while (lane !== targetLane) {
      const nextLane = (lane + Math.sign(targetLane - lane)) as LaneNumber;
      const crossing = this.bestCrossing(lane, nextLane, x, safeTargetX, unit.faction);
      if (!crossing) return [];
      const currentLaneY = this.laneY(lane);
      const nextLaneY = this.laneY(nextLane);
      waypoints.push({ x: crossing.x, y: currentLaneY });
      waypoints.push({ x: crossing.x, y: nextLaneY, lane: nextLane });
      x = crossing.x;
      lane = nextLane;
    }

    waypoints.push({ x: safeTargetX, y: this.laneY(targetLane), lane: targetLane });
    return waypoints;
  }

  private updateCommandPreview(pointer: Phaser.Input.Pointer) {
    if (
      this.selection ||
      this.selectedPlayerUnits.size === 0 ||
      this.isCameraDragging ||
      this.isPinching ||
      this.selectionBox ||
      !this.isBattlefieldPointer(pointer) ||
      this.pointerIntent === "left" ||
      this.pointerIntent === "middle" ||
      this.pointerIntent === "touchPan"
    ) {
      this.clearCommandPreview();
      return;
    }

    const worldPoint = this.pointerToWorld(pointer);
    const targetLane = this.nearestLane(worldPoint.y);
    if (!targetLane) {
      this.clearCommandPreview();
      return;
    }

    const units = [...this.selectedPlayerUnits].filter((unit) => unit.alive).slice(0, 4);
    if (units.length === 0) {
      this.clearCommandPreview();
      return;
    }

    const direction = this.commandDirectionForGroup(units, worldPoint.x);
    const offsetBias = this.formationOffsetBias(units);
    const paths = units.map((unit, index) => {
      const targetX = this.formationTargetX(unit, worldPoint.x, index, units.length, direction, offsetBias);
      return [{ x: unit.x, y: unit.y }, ...this.createUnitPath(unit, targetLane, targetX)];
    });
    const blocked = paths.every((path) => path.length <= 1);
    this.drawCommandPreview(paths, worldPoint.x, this.laneY(targetLane), blocked, units.length);
  }

  private drawCommandPreview(paths: UnitWaypoint[][], x: number, y: number, blocked: boolean, unitCount: number) {
    this.clearCommandPreview();
    const color = blocked ? 0xff604f : 0x9feaff;
    this.commandPreview = this.add.graphics().setDepth(54);
    this.commandPreview.lineStyle(blocked ? 2 : 1, color, blocked ? 0.62 : 0.42);
    this.commandPreview.fillStyle(color, blocked ? 0.16 : 0.12);
    for (const path of paths) {
      if (path.length <= 1) continue;
      for (let index = 1; index < path.length; index += 1) {
        const previous = path[index - 1]!;
        const waypoint = path[index]!;
        this.commandPreview.lineBetween(previous.x, previous.y, waypoint.x, waypoint.y);
        this.commandPreview.fillCircle(waypoint.x, waypoint.y, index === path.length - 1 ? 5 : 3);
      }
    }
    if (blocked) {
      this.commandPreview.strokeCircle(x, y, 16);
      this.commandPreview.lineBetween(x - 10, y - 10, x + 10, y + 10);
      this.commandPreview.lineBetween(x - 10, y + 10, x + 10, y - 10);
    } else {
      this.commandPreview.strokeCircle(x, y, 15);
      this.commandPreview.fillCircle(x, y, 8);
    }
    this.commandPreviewLabel = this.add
      .text(x, y - 30, blocked ? t(this.settings.language, "noCrossingAvailable") : `${t(this.settings.language, "commandMarker")} x${unitCount}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "11px",
        color: blocked ? "#ffd6cb" : "#dff9ff",
        stroke: "#061018",
        strokeThickness: 2
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(55);
  }

  private clearCommandPreview() {
    this.commandPreview?.destroy();
    this.commandPreview = undefined;
    this.commandPreviewLabel?.destroy();
    this.commandPreviewLabel = undefined;
  }

  private bestCrossing(fromLane: LaneNumber, toLane: LaneNumber, fromX: number, targetX: number, faction?: "player" | "enemy") {
    const crossings = (mapConfig.laneCrossings as LaneCrossing[] | undefined ?? [])
      .filter((crossing) => crossing.enabled && this.crossingConnects(crossing, fromLane, toLane));
    return crossings
      .sort((a, b) =>
        this.crossingOwnerPenalty(a, faction) - this.crossingOwnerPenalty(b, faction) ||
        Math.abs(a.x - fromX) + Math.abs(a.x - targetX) - (Math.abs(b.x - fromX) + Math.abs(b.x - targetX))
      )[0];
  }

  private crossingOwnerPenalty(crossing: LaneCrossing, faction?: "player" | "enemy") {
    if (!faction) return 0;
    const owner = this.crossingStates.get(crossing.id)?.owner ?? "neutral";
    if (owner === "neutral" || owner === faction) return 0;
    return 120;
  }

  private crossingConnects(crossing: LaneCrossing, fromLane: LaneNumber, toLane: LaneNumber) {
    return (crossing.fromLane === fromLane && crossing.toLane === toLane) || (crossing.fromLane === toLane && crossing.toLane === fromLane);
  }

  private updateCrossingControls(deltaMs: number) {
    for (const state of this.crossingStates.values()) {
      const previousOwner = state.owner;
      state.playerUnits = this.unitsNearCrossing(state, this.playerUnits);
      state.enemyUnits = this.unitsNearCrossing(state, this.enemies);
      const pressure = Math.sign(state.playerUnits - state.enemyUnits);
      if (pressure > 0) {
        state.progress = Math.min(CROSSING_CAPTURE_MS, state.progress + deltaMs * Math.min(1.7, 1 + (state.playerUnits - 1) * 0.2));
      } else if (pressure < 0) {
        state.progress = Math.max(-CROSSING_CAPTURE_MS, state.progress - deltaMs * Math.min(1.7, 1 + (state.enemyUnits - 1) * 0.2));
      } else {
        state.progress *= 0.994;
        if (Math.abs(state.progress) < 10) state.progress = 0;
      }

      if (state.progress >= CROSSING_CAPTURE_MS) {
        state.owner = "player";
      } else if (state.progress <= -CROSSING_CAPTURE_MS) {
        state.owner = "enemy";
      } else if (Math.abs(state.progress) < CROSSING_CAPTURE_MS * 0.18) {
        state.owner = "neutral";
      }

      if (state.owner !== previousOwner && state.owner !== "neutral") {
        this.logger.log("CROSSING_CONTROL_CHANGED", this.battleTime, { crossingId: state.config.id, owner: state.owner, progress: Number((state.progress / CROSSING_CAPTURE_MS).toFixed(2)) }, state.config.fromLane);
        this.recentActions.push(`${state.owner}_controlled_crossing_${state.config.id}`);
      }
      this.updateCrossingMarker(state);
    }
  }

  private unitsNearCrossing(state: CrossingControlState, units: Unit[]) {
    return units.filter(
      (unit) =>
        unit.alive &&
        (unit.lane === state.config.fromLane || unit.lane === state.config.toLane) &&
        Math.abs(unit.x - state.config.x) <= state.config.radius
    ).length;
  }

  private updateCrossingMarker(state: CrossingControlState) {
    const color = state.owner === "player" ? 0x70d7ff : state.owner === "enemy" ? 0xff604f : 0xd2c37d;
    const fromY = this.laneY(state.config.fromLane);
    const toY = this.laneY(state.config.toLane);
    const midY = (fromY + toY) / 2;
    state.ring.clear();
    state.ring.lineStyle(2, color, state.owner === "neutral" ? 0.5 : 0.82);
    state.ring.strokeCircle(state.config.x, midY, Math.max(15, state.config.radius * 0.36));
    state.ring.fillStyle(color, state.owner === "neutral" ? 0.025 : 0.06);
    state.ring.fillCircle(state.config.x, midY, Math.max(15, state.config.radius * 0.36));
    state.flag.setColor(`#${color.toString(16).padStart(6, "0")}`);
    state.flag.setText(state.owner === "player" ? "◆" : state.owner === "enemy" ? "◆" : "◇");
    state.progressFill.setFillStyle(color, 0.92);
    state.progressFill.width = Math.max(1, 48 * Math.min(1, Math.abs(state.progress) / CROSSING_CAPTURE_MS));
    state.progressFill.x = state.config.x + (state.progress < 0 ? 24 - state.progressFill.width : -24);
  }

  private crossingControlSummaries(): NonNullable<BattleSummary["crossingControls"]> {
    return [...this.crossingStates.values()].map((state) => ({
      id: state.config.id,
      fromLane: state.config.fromLane,
      toLane: state.config.toLane,
      x: state.config.x,
      owner: state.owner,
      progress: Number((state.progress / CROSSING_CAPTURE_MS).toFixed(2)),
      playerUnits: state.playerUnits,
      enemyUnits: state.enemyUnits
    }));
  }

  private refreshLaneMoraleCache() {
    for (const lane of [1, 2, 3, 4, 5] as LaneNumber[]) {
      this.laneMoraleCache.set(this.laneMoraleKey(lane, "player"), this.computeLaneMorale(lane, "player"));
      this.laneMoraleCache.set(this.laneMoraleKey(lane, "enemy"), this.computeLaneMorale(lane, "enemy"));
    }
  }

  private laneMoraleForCombat(lane: LaneNumber, faction: UnitFaction) {
    return this.laneMoraleCache.get(this.laneMoraleKey(lane, faction)) ?? 60;
  }

  private laneMoraleSummary(): Partial<Record<LaneNumber, { player: number; enemy: number }>> {
    const summary: Partial<Record<LaneNumber, { player: number; enemy: number }>> = {};
    for (const lane of [1, 2, 3, 4, 5] as LaneNumber[]) {
      summary[lane] = {
        player: this.laneMoraleForCombat(lane, "player"),
        enemy: this.laneMoraleForCombat(lane, "enemy")
      };
    }
    return summary;
  }

  private laneMoraleKey(lane: LaneNumber, faction: UnitFaction) {
    return `${faction}:${lane}`;
  }

  private computeLaneMorale(lane: LaneNumber, faction: UnitFaction) {
    const allies = (faction === "player" ? this.playerUnits : this.enemies).filter((unit) => unit.alive && unit.lane === lane);
    const opponents = (faction === "player" ? this.enemies : this.playerUnits).filter((unit) => unit.alive && unit.lane === lane);
    const encircledAllies = allies.filter((unit) => this.surroundingUnits(unit, opponents).length >= 2).length;
    const encircledOpponents = opponents.filter((unit) => this.surroundingUnits(unit, allies).length >= 2).length;
    const outpost = this.economyOutposts.summaries.find((item) => item.lane === lane);
    const recentDamage = this.recentCastleDamageForOwner(faction, lane);

    let morale = 60;
    morale += Phaser.Math.Clamp((allies.length - opponents.length) * 3, -12, 12);
    morale -= encircledAllies * 18;
    morale += encircledOpponents * 8;
    morale -= Math.min(14, recentDamage / 40);

    if (outpost?.owner === faction) morale += 4;
    if (outpost?.owner && outpost.owner !== "neutral" && outpost.owner !== faction) morale -= 4;
    if (faction === "player" && allies.some((unit) => unit.hasActiveManualAbility("rally_banner", this.time.now))) morale += 10;
    if (faction === "player") {
      morale -= this.laneCollapsePenalty(lane);
      if (this.isPlayerUltimateActive()) morale += 20;
      if (this.isAiUltimateActive() && this.aiUltimateLane === lane) morale -= 12;
    } else if (this.isAiUltimateActive() && this.aiUltimateLane === lane) {
      morale += 10;
    }

    if (faction === "player") {
      const nearestEnemy = opponents.sort((a, b) => b.x - a.x)[0]?.x;
      if (nearestEnemy !== undefined) morale -= Phaser.Math.Clamp((nearestEnemy - 760) / 55, 0, 10);
    } else {
      const nearestPlayer = opponents.sort((a, b) => a.x - b.x)[0]?.x;
      if (nearestPlayer !== undefined) morale -= Phaser.Math.Clamp((620 - nearestPlayer) / 55, 0, 10);
    }

    return Math.round(Phaser.Math.Clamp(morale, 0, 100));
  }

  private laneCollapsePenalty(lane: LaneNumber) {
    const state = this.laneCollapseStates.get(lane);
    if (!state || this.time.now >= state.until) return 0;
    return state.penalty;
  }

  private laneCollapseSummary(): Partial<Record<LaneNumber, { active: boolean; remainingSec: number; penalty: number }>> {
    const summary: Partial<Record<LaneNumber, { active: boolean; remainingSec: number; penalty: number }>> = {};
    for (const lane of [1, 2, 3, 4, 5] as LaneNumber[]) {
      const state = this.laneCollapseStates.get(lane);
      const remainingMs = state ? Math.max(0, state.until - this.time.now) : 0;
      if (state && remainingMs > 0) {
        summary[lane] = {
          active: true,
          remainingSec: Math.ceil(remainingMs / 1000),
          penalty: state.penalty
        };
      }
    }
    return summary;
  }

  private recentCastleDamageForOwner(faction: UnitFaction, lane: LaneNumber) {
    const owner = faction === "player" ? "player" : "ai";
    const recentWindowStart = Math.max(0, this.battleTime - 45);
    return this.logger
      .snapshot()
      .filter((event) => event.type === "CASTLE_DAMAGED" && event.payload.owner === owner && event.lane === lane && event.time >= recentWindowStart)
      .reduce((total, event) => total + Number(event.payload.damage ?? 0), 0);
  }

  private commanderUltimateSummary(): NonNullable<BattleSummary["commanderUltimate"]> {
    return {
      playerGauge: Math.round(this.playerUltimateGauge),
      aiGauge: Math.round(this.aiUltimateGauge),
      playerActive: this.isPlayerUltimateActive(),
      playerRemainingSec: Math.ceil(Math.max(0, this.playerUltimateUntil - this.time.now) / 1000),
      aiActive: this.isAiUltimateActive(),
      aiLane: this.isAiUltimateActive() ? this.aiUltimateLane : undefined,
      aiRemainingSec: Math.ceil(Math.max(0, this.aiUltimateUntil - this.time.now) / 1000)
    };
  }

  private activePlayerAbilitySummary(): NonNullable<BattleSummary["activePlayerAbilities"]> {
    const summary: NonNullable<BattleSummary["activePlayerAbilities"]> = [];
    for (const unit of this.playerUnits) {
      if (!unit.alive) continue;
      const state = unit.getManualAbilityState(this.time.now);
      if (!state) continue;
      summary.push({
        lane: unit.lane,
        unitId: unit.id,
        abilityId: state.id,
        active: state.activeRemainingMs > 0,
        remainingSec: Math.ceil(state.activeRemainingMs / 1000),
        cooldownSec: Math.ceil(state.cooldownRemainingMs / 1000)
      });
    }
    return summary;
  }

  private laneY(lane: LaneNumber) {
    return mapConfig.lanes.find((item) => item.lane === lane)?.y ?? mapConfig.lanes[2]!.y;
  }

  private laneTerrain(lane: LaneNumber): LaneStateSummary["terrain"] {
    return mapConfig.lanes.find((item) => item.lane === lane)?.terrain as LaneStateSummary["terrain"];
  }

  private terrainSpeedMultiplierForUnit(unit: Unit) {
    const terrain = this.laneTerrain(unit.lane);
    let multiplier = 1;
    if (terrain === "cobblestone") multiplier = 1.1;
    if (terrain === "wall_road") multiplier = 1.04;
    if (terrain === "forest") multiplier = 0.96;
    if (terrain === "ruins") multiplier = 0.98;
    if (terrain === "marsh") multiplier = 0.86;
    if ((unit.role === "siege" || unit.role === "siege_beast") && terrain === "marsh") {
      multiplier *= 0.92;
    }
    if (unit.faction === "player" && this.isPlayerUltimateActive()) {
      multiplier *= 1.1;
    }
    return multiplier;
  }

  private placeTower(x: number, y: number, towerId: string) {
    const towerData = towersData.find((tower) => tower.id === towerId);
    if (!towerData) {
      return;
    }

    if (!this.economy.canAfford(towerData.cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }

    const pad = mapConfig.towerPads
      .filter((candidate) => !this.occupiedPads.has(candidate.id))
      .sort((a, b) => Phaser.Math.Distance.Between(x, y, a.x, a.y) - Phaser.Math.Distance.Between(x, y, b.x, b.y))[0];

    if (!pad || Phaser.Math.Distance.Between(x, y, pad.x, pad.y) > 48) {
      this.buildPanel.setStatus(t(this.settings.language, "noPadInRange"));
      return;
    }

    this.placeTowerOnPad(towerData, pad);
  }

  private placeTowerOnPad(towerData: TowerData, pad: (typeof mapConfig.towerPads)[number]) {
    if (this.occupiedPads.has(pad.id)) {
      this.buildPanel.setStatus(t(this.settings.language, "padOccupied"));
      return;
    }

    if (!this.economy.spend(towerData.cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }

    this.occupiedPads.add(pad.id);
    this.padSprites.get(pad.id)?.setAlpha(0.35);
    const tower = this.createTower(towerData, pad.id, pad.lane as LaneNumber, pad.x, pad.y);
    this.towers.push(tower);
    AudioManager.playSfx("build");
    this.showTowerRange(tower);
    this.selection = undefined;
    this.interactionMode = "unitCommand";
    this.buildPanel.setStatus(t(this.settings.language, "towerBuilt"));
    this.recentActions.push(`placed_${towerData.id}_lane_${pad.lane}`);
    this.logger.log("TOWER_PLACED", this.battleTime, { towerId: towerData.id, playerGoldAfter: this.economy.gold }, pad.lane as LaneNumber);
  }

  private summonPlayerUnit(unitId: string, lane: LaneNumber) {
    const unitData = unitsData.find((unit) => unit.id === unitId);
    if (!unitData) {
      return;
    }

    if (this.summonQueue.length >= MAX_SUMMON_QUEUE) {
      this.buildPanel.setStatus(t(this.settings.language, "summonQueueFull"));
      return;
    }

    if (!this.economy.spend(unitData.cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }

    this.summonQueue.push({ unitId, lane, queuedAt: this.time.now });
    AudioManager.playSfx("summon");
    this.buildPanel.setStatus(`${t(this.settings.language, "unitQueued")} ${this.summonQueue.length}/${MAX_SUMMON_QUEUE}`);
    this.recentActions.push(`queued_${unitId}_lane_${lane}`);
    this.updateSummonQueue(this.time.now);
  }

  private updateSummonQueue(time: number) {
    if (this.summonQueue.length === 0) {
      this.buildPanel?.setSummonQueue(0);
      return;
    }

    this.buildPanel?.setSummonQueue(this.summonQueue.length, Math.max(0, this.nextSummonAt - time));
    if (time < this.nextSummonAt) return;

    const item = this.summonQueue.shift();
    if (!item) return;
    this.deployQueuedUnit(item);
    const sameLane = this.lastSummonQueueLane === item.lane;
    this.lastSummonQueueLane = item.lane;
    this.nextSummonAt = time + (sameLane ? SAME_LANE_SUMMON_INTERVAL_MS : BASE_SUMMON_INTERVAL_MS);
    this.buildPanel?.setSummonQueue(this.summonQueue.length, Math.max(0, this.nextSummonAt - time));
  }

  private deployQueuedUnit(item: SummonQueueItem) {
    const unitData = unitsData.find((unit) => unit.id === item.unitId);
    if (!unitData) return;
    const unit = this.createPlayerUnit(unitData, item.lane);
    this.playerUnits.push(unit);
    this.buildPanel.setStatus(t(this.settings.language, "unitDeployed"));
    this.recentActions.push(`summoned_${item.unitId}_lane_${item.lane}`);
    this.logger.log("UNIT_SUMMONED", this.battleTime, { unitId: item.unitId, queuedMs: Math.round(this.time.now - item.queuedAt), playerGoldAfter: this.economy.gold }, item.lane);

    if (unit.scoutCoverage) {
      this.counter.add(6);
      this.logger.log("SCOUT_SUCCESS", this.battleTime, { unitId: item.unitId }, item.lane);
    }
  }

  private createTower(data: TowerData, padId: string, lane: LaneNumber, x: number, y: number) {
    const tower = new Tower(this, {
      id: data.id,
      name: data.name,
      lane,
      padId,
      x,
      y,
      texture: data.id,
      hp: data.hp,
      damage: data.damage,
      range: data.range,
      attackRateMs: data.attackRateMs,
      healing: "healing" in data ? Boolean(data.healing) : false,
      blocker: "blocker" in data ? Boolean(data.blocker) : false,
      singleUse: "singleUse" in data ? Boolean(data.singleUse) : false,
      splashRadius: "splashRadius" in data ? Number(data.splashRadius) : 0,
      slow: "slow" in data ? Number(data.slow) : 0,
      scoutCoverage: "scoutCoverage" in data ? Boolean(data.scoutCoverage) : false,
      deployTimeMs: "deployTimeMs" in data ? Number(data.deployTimeMs) : 0
    });
    this.applyTechToTower(tower);
    tower.sprite.setInteractive({ useHandCursor: true });
    tower.sprite.on("pointerover", () => this.showTowerRangePreview(tower));
    tower.sprite.on("pointerout", () => this.clearTowerRangePreview());
    return tower;
  }

  private findTowerAt(point: Phaser.Math.Vector2) {
    return this.towers
      .filter((candidate) => !candidate.destroyed)
      .sort((a, b) => Phaser.Math.Distance.Between(point.x, point.y, a.x, a.y) - Phaser.Math.Distance.Between(point.x, point.y, b.x, b.y))
      .find((tower) => Phaser.Math.Distance.Between(point.x, point.y, tower.x, tower.y) <= 46);
  }

  private showTowerPriorityMenu(tower: Tower) {
    if (tower.destroyed) return;
    this.clearTowerPriorityMenu();
    this.clearMobileCommandRing();
    this.clearCommandPreview();
    this.showTowerRangePreview(tower);

    const options = this.towerPriorityOptions();
    const width = 330;
    const x = Phaser.Math.Clamp(tower.x, 190, mapConfig.designWidth - 190);
    const y = Phaser.Math.Clamp(tower.y - 86, 84, mapConfig.designHeight - 92);
    const menu = this.add.container(x, y).setDepth(94);
    const frame = this.add.rectangle(0, 0, width, 72, 0x071018, 0.9).setStrokeStyle(1, 0x8de7ff, 0.68);
    const title = this.add
      .text(-width / 2 + 12, -24, `${t(this.settings.language, "towerPriorityReady")}: ${tower.name}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "11px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 2
      })
      .setOrigin(0, 0.5)
      .setResolution(2);
    menu.add([frame, title]);

    options.forEach((option, index) => {
      const button = this.createTowerPriorityButton(tower, option.priority, option.label, -132 + index * 66, 14);
      menu.add(button);
    });

    menu.setSize(width, 72);
    menu.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -36, width, 72), Phaser.Geom.Rectangle.Contains);
    this.towerPriorityMenu = menu;
    this.syncCameraLayers();
  }

  private createTowerPriorityButton(tower: Tower, priority: TowerTargetPriority, label: string, x: number, y: number) {
    const active = tower.targetPriority === priority;
    const button = this.add.container(x, y);
    const bg = this.add.rectangle(0, 0, 58, 28, active ? 0x214a5d : 0x101820, active ? 0.96 : 0.88).setStrokeStyle(active ? 2 : 1, active ? 0xf7d66f : 0x8de7ff, active ? 0.95 : 0.58);
    const text = this.add
      .text(0, -1, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "12px" : "10px",
        color: active ? "#fff3c2" : "#dff9ff",
        stroke: "#08070a",
        strokeThickness: 2,
        fixedWidth: 52
      })
      .setOrigin(0.5)
      .setAlign("center")
      .setResolution(2);
    button.add([bg, text]);
    button.setSize(58, 28);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => bg.setFillStyle(active ? 0x2c6178 : 0x17364a, 0.96));
    button.on("pointerout", () => bg.setFillStyle(active ? 0x214a5d : 0x101820, active ? 0.96 : 0.88));
    button.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation?.();
      tower.setTargetPriority(priority);
      this.logger.log("TOWER_PRIORITY_CHANGED", this.battleTime, { towerId: tower.id, priority }, tower.lane);
      this.buildPanel.setStatus(`${t(this.settings.language, "towerPriorityChanged")}: ${label}`);
      this.showTowerRange(tower);
      this.showTowerPriorityMenu(tower);
    });
    return button;
  }

  private towerPriorityOptions(): Array<{ priority: TowerTargetPriority; label: string }> {
    return [
      { priority: "nearest", label: t(this.settings.language, "towerPriorityNearest") },
      { priority: "lowest_hp", label: t(this.settings.language, "towerPriorityLowestHp") },
      { priority: "highest_threat", label: t(this.settings.language, "towerPriorityHighestThreat") },
      { priority: "siege_first", label: t(this.settings.language, "towerPrioritySiegeFirst") },
      { priority: "fast_first", label: t(this.settings.language, "towerPriorityFastFirst") }
    ];
  }

  private clearTowerPriorityMenu() {
    this.towerPriorityMenu?.destroy(true);
    this.towerPriorityMenu = undefined;
  }

  private createPlayerUnit(data: UnitData, lane: LaneNumber) {
    const laneConfig = mapConfig.lanes.find((item) => item.lane === lane) ?? mapConfig.lanes[2]!;
    const unit = new Unit(this, {
      id: data.id,
      name: data.name,
      lane,
      x: mapConfig.playerSpawnX,
      y: laneConfig.y,
      faction: "player",
      role: data.role,
      texture: this.settings.beta.spriteAnimations ? `${data.id}_walk` : data.id,
      hp: data.hp,
      damage: data.damage,
      range: data.range,
      attackRateMs: data.attackRateMs,
      speed: data.speed,
      splashRadius: "splashRadius" in data ? Number(data.splashRadius) : 0,
      slow: "slow" in data ? Number(data.slow) : 0,
      freezeChance: "freezeChance" in data ? Number(data.freezeChance) : 0,
      scoutCoverage: "scoutCoverage" in data ? Boolean(data.scoutCoverage) : false,
      buffAura: "buffAura" in data ? Number(data.buffAura) : 0,
      repairPower: "repairPower" in data ? Number(data.repairPower) : 0,
      deployTimeMs: "deployTimeMs" in data ? Number(data.deployTimeMs) : 0
    });
    this.applyTechToUnit(unit);
    this.registerVeteranProgress(unit, data, lane);
    return unit;
  }

  private registerVeteranProgress(unit: Unit, data: UnitData, lane: LaneNumber) {
    const savedVeteran = this.memoryVeterans.find((veteran) => veteran.unitId === data.id && !this.deployedVeteranIds.has(veteran.veteranId));
    const progress: VeteranProgress = savedVeteran
      ? {
          veteranId: savedVeteran.veteranId,
          unitId: savedVeteran.unitId,
          nickname: savedVeteran.nickname,
          spawnedAt: this.time.now,
          kills: savedVeteran.kills,
          promoted: true,
          survivedBattles: savedVeteran.survivedBattles,
          preferredLane: lane
        }
      : {
          veteranId: this.createVeteranId(data.id),
          unitId: data.id,
          nickname: this.createVeteranNickname(data),
          spawnedAt: this.time.now,
          kills: 0,
          promoted: false,
          survivedBattles: 0,
          preferredLane: lane
        };
    this.veteranProgress.set(unit, progress);
    if (savedVeteran) {
      this.deployedVeteranIds.add(savedVeteran.veteranId);
      unit.applyVeteran(savedVeteran.nickname, savedVeteran.bonus);
      this.recentActions.push(`deployed_veteran_${data.id}_lane_${lane}`);
      this.logger.log("UNIT_VETERAN_PROMOTED", this.battleTime, { unitId: data.id, veteranId: savedVeteran.veteranId, nickname: savedVeteran.nickname, restored: true }, lane);
    }
  }

  private creditVeteranKill(killer: Unit | undefined, _enemy: Unit) {
    if (!killer || !killer.alive) return;
    const progress = this.veteranProgress.get(killer);
    if (!progress) return;
    progress.kills += 1;
    progress.preferredLane = killer.lane;
    if (!progress.promoted) {
      this.tryPromoteVeteran(killer, progress);
    }
  }

  private updateVeteranProgress(time: number) {
    for (const [unit, progress] of this.veteranProgress) {
      if (!unit.alive) {
        this.veteranProgress.delete(unit);
        continue;
      }
      progress.preferredLane = unit.lane;
      if (!progress.promoted && time - progress.spawnedAt >= VETERAN_REQUIRED_SURVIVAL_MS) {
        this.tryPromoteVeteran(unit, progress);
      }
    }
  }

  private tryPromoteVeteran(unit: Unit, progress: VeteranProgress) {
    if (progress.promoted || progress.kills < VETERAN_REQUIRED_KILLS || this.time.now - progress.spawnedAt < VETERAN_REQUIRED_SURVIVAL_MS) return;
    progress.promoted = true;
    unit.applyVeteran(progress.nickname);
    this.buildPanel?.setStatus(`${t(this.settings.language, "veteranPromoted")}: ${progress.nickname}`);
    this.recentActions.push(`veteran_${progress.unitId}_lane_${unit.lane}`);
    this.logger.log(
      "UNIT_VETERAN_PROMOTED",
      this.battleTime,
      { unitId: progress.unitId, veteranId: progress.veteranId, nickname: progress.nickname, kills: progress.kills },
      unit.lane
    );
  }

  private recordSurvivingVeterans() {
    const survivors = [...this.veteranProgress.entries()]
      .filter(([unit, progress]) => unit.alive && progress.promoted)
      .sort(([, a], [, b]) => b.kills - a.kills || b.survivedBattles - a.survivedBattles)
      .slice(0, 3);
    for (const [unit, progress] of survivors) {
      this.logger.log(
        "VETERAN_RECORDED",
        this.battleTime,
        {
          veteranId: progress.veteranId,
          unitId: progress.unitId,
          nickname: progress.nickname,
          kills: progress.kills,
          survivedBattles: progress.survivedBattles + 1,
          preferredLane: unit.lane
        },
        unit.lane
      );
    }
  }

  private createVeteranId(unitId: string) {
    return `vet_${unitId}_${Math.round(this.time.now)}_${Phaser.Math.Between(1000, 9999)}`;
  }

  private createVeteranNickname(data: UnitData) {
    const unitName = this.settings.language === "ko" ? this.koreanUnitName(data.id) : data.name;
    if (this.settings.language === "ko") {
      return Phaser.Utils.Array.GetRandom([`노련한 ${unitName}`, `${unitName} 생존자`, `${unitName} 선봉`]);
    }
    return Phaser.Utils.Array.GetRandom([`Veteran ${unitName}`, `${unitName} Survivor`, `${unitName} Vanguard`]);
  }

  private koreanUnitName(unitId: string) {
    const names: Record<string, string> = {
      swordsman: "검병",
      shield_soldier: "방패병",
      monk: "수도사",
      powder_carrier: "화약병",
      frost_mage: "냉기술사",
      raven_scout: "정찰병",
      spearman_new: "창병",
      alchemist: "연금술사",
      standard_bearer: "군기병",
      engineer: "공병"
    };
    return names[unitId] ?? "병사";
  }

  private activateCounterAttack() {
    if (!this.counter.activate()) {
      this.buildPanel.setStatus(t(this.settings.language, "counterGaugeNotReady"));
      return;
    }
    this.buildPanel.setStatus(t(this.settings.language, "counterattackLaunched"));
    this.logger.log("COUNTER_ATTACK_STARTED", this.battleTime, { gauge: 100 });
    for (const lane of [1, 2, 3, 4, 5] as LaneNumber[]) {
      const unitData = unitsData.find((unit) => unit.id === "swordsman") ?? unitsData[0]!;
      this.playerUnits.push(this.createPlayerUnit(unitData, lane));
    }
  }

  private activatePlayerUltimate() {
    if (this.playerUltimateGauge < 100) {
      this.buildPanel.setStatus(t(this.settings.language, "ultimateNotReady"));
      return;
    }
    this.playerUltimateGauge = 0;
    this.playerUltimateUntil = this.time.now + PLAYER_ULTIMATE_MS;
    AudioManager.playSfx("ability");
    this.buildPanel.setStatus(t(this.settings.language, "playerUltimateUsed"));
    this.logger.log("ULTIMATE_USED", this.battleTime, { faction: "player", type: "rallying_horn", durationMs: PLAYER_ULTIMATE_MS });
    this.recentActions.push("player_used_rallying_horn");
    this.showPlayerUltimatePulse();
    this.showBattleCutIn("player_ultimate", this.settings.language === "ko" ? "집결의 나팔" : "Rallying Horn", 0x8dffbf);
    this.updateUltimateButton();
  }

  private updateUltimateGauges(time: number, deltaMs: number) {
    if (time >= this.playerUltimateUntil) this.playerUltimateUntil = 0;
    if (time >= this.aiUltimateUntil) {
      this.aiUltimateUntil = 0;
      this.aiUltimateLane = undefined;
    }
    const seconds = Math.max(0, deltaMs / 1000);
    this.addPlayerUltimate(0.48 * seconds);
    this.addAiUltimate(0.42 * seconds + Math.max(0, this.difficulty.pressure) * 0.015 * seconds);
  }

  private addPlayerUltimate(amount: number) {
    if (this.isPlayerUltimateActive()) return;
    this.playerUltimateGauge = Math.min(100, this.playerUltimateGauge + amount);
  }

  private addAiUltimate(amount: number) {
    if (this.isAiUltimateActive()) return;
    this.aiUltimateGauge = Math.min(100, this.aiUltimateGauge + amount);
  }

  private isPlayerUltimateActive() {
    return this.time.now < this.playerUltimateUntil;
  }

  private isAiUltimateActive() {
    return this.time.now < this.aiUltimateUntil && this.aiUltimateLane !== undefined;
  }

  private maybeActivateAiUltimate(time: number) {
    if (this.aiUltimateGauge < 100 || this.isAiUltimateActive()) return;
    if (time - this.battleStartedAt < AI_ULTIMATE_MIN_BATTLE_MS || time - this.lastAiUltimateAt < AI_ULTIMATE_COOLDOWN_MS) return;
    const target = this.aiUltimateTargetLane();
    if (!target) return;
    this.aiUltimateGauge = 0;
    this.aiUltimateLane = target;
    this.aiUltimateUntil = time + AI_ULTIMATE_MS;
    this.lastAiUltimateAt = time;
    this.buildPanel.setStatus(t(this.settings.language, "aiUltimateUsed"));
    this.chat.pushTaunt(this.aiUltimateLine(target), "furious");
    this.logger.log("ULTIMATE_USED", this.battleTime, { faction: "enemy", type: "dread_decree", durationMs: AI_ULTIMATE_MS }, target);
    this.showAiUltimateMarker(target);
    this.showBattleCutIn("ai_ultimate", this.settings.language === "ko" ? `공포의 칙령: ${target}번 전선` : `Dread Decree: Lane ${target}`, 0xff574f, target);
  }

  private aiUltimateTargetLane(): LaneNumber | undefined {
    return this.createLaneStates()
      .filter((state) => (state.enemyUnitCount ?? 0) > 0 || state.riskLevel === "danger" || state.riskLevel === "collapse" || state.laneCollapseActive)
      .sort((a, b) =>
        this.riskRank(b.riskLevel) - this.riskRank(a.riskLevel) ||
        Number(Boolean(b.laneCollapseActive)) - Number(Boolean(a.laneCollapseActive)) ||
        (b.forwardEnemyUnits ?? 0) - (a.forwardEnemyUnits ?? 0) ||
        (b.recentDamageToCastle ?? 0) - (a.recentDamageToCastle ?? 0) ||
        (a.playerMorale ?? 60) - (b.playerMorale ?? 60)
      )[0]?.lane;
  }

  private aiUltimateLine(lane: LaneNumber) {
    if (this.settings.language === "ko") {
      return `전선 ${lane}. 공포를 선포한다. 네 병사들이 먼저 무너질 것이다.`;
    }
    return `Lane ${lane}. I declare dread. Your soldiers will break before the wall does.`;
  }

  private maybeTriggerAiLastStand(time: number) {
    if (this.aiLastStandTriggered || this.ended || this.battleTime < 70) return;
    if (this.aiCastle.hp / this.aiCastle.maxHp > 0.24) return;
    const target = this.createLaneStates()
      .sort((a, b) =>
        (b.playerThreatToAiCastle ?? 0) - (a.playerThreatToAiCastle ?? 0) ||
        (b.forwardPlayerUnits ?? 0) - (a.forwardPlayerUnits ?? 0) ||
        (b.playerCommitmentScore ?? 0) - (a.playerCommitmentScore ?? 0)
      )[0]?.lane ?? 3;
    this.aiLastStandTriggered = true;
    this.aiUltimateGauge = 0;
    this.aiUltimateLane = target;
    this.aiUltimateUntil = time + AI_ULTIMATE_MS;
    this.lastAiUltimateAt = time;
    this.showAiUltimateMarker(target);
    this.buildPanel.setStatus(this.settings.language === "ko" ? `최후 저항: ${target}번 전선` : `Last stand: lane ${target}`);
    this.chat.pushTaunt(this.aiLastStandLine(target), "furious");
    this.logger.log("AI_EVENT_TRIGGERED", this.battleTime, { type: "last_stand", targetLane: target, aiCastleHp: Math.round(this.aiCastle.hp) }, target);
    this.logger.log("ULTIMATE_USED", this.battleTime, { faction: "enemy", type: "dread_decree_last_stand", durationMs: AI_ULTIMATE_MS }, target);
    this.showBattleCutIn("last_stand", this.settings.language === "ko" ? "최후의 명령" : "Last Stand", 0xff9d3d, target);
    this.waves.applyGameCommand(
      {
        taunt: "",
        spawnCommands: [
          { lane: target, units: ["shield_soldier", "warlock", "siege_cart"], purpose: "counter" },
          { lane: target, units: ["raider", "spearman"], purpose: "counter", delayMs: 3600 }
        ],
        difficultyAdjustment: 0.04,
        commanderMood: "furious",
        detectedPattern: "ai_last_stand",
        preferredEnemyRoles: ["tank", "debuffer", "siege"],
        towerAttackIntent: "pressure",
        objectiveDisruptionIntent: "none",
        reason: "AI fortress dropped below last-stand threshold."
      },
      this.enemies
    );
  }

  private aiLastStandLine(lane: LaneNumber) {
    if (this.settings.language === "ko") {
      return `좋다. ${lane}번 전선에 마지막 깃발을 꽂겠다. 내 요새는 쉽게 죽지 않는다.`;
    }
    return `Good. I plant the last banner on lane ${lane}. My fortress does not die quietly.`;
  }

  // Cut-in emblem. The art keys ("player_ultimate"/"ai_ultimate"/"last_stand")
  // have no loaded texture, which used to make Phaser draw its green "missing
  // texture" box mid-battle. Use the real image when present, otherwise draw a
  // heraldic crest in the cut-in color so the moment still reads as intentional.
  private createCutInEmblem(key: string, color: number): Phaser.GameObjects.GameObject {
    if (this.textures.exists(key)) {
      return this.add.image(-360, 0, key).setDisplaySize(150, 150);
    }
    const emblem = this.add.container(-360, 0);
    const g = this.add.graphics();
    g.fillStyle(0x05060a, 0.85);
    g.fillRoundedRect(-58, -64, 116, 128, 16);
    g.lineStyle(3, color, 0.92);
    g.strokeRoundedRect(-58, -64, 116, 128, 16);
    // Shield silhouette.
    g.fillStyle(color, 0.16);
    g.beginPath();
    g.moveTo(0, -44);
    g.lineTo(40, -28);
    g.lineTo(34, 30);
    g.lineTo(0, 54);
    g.lineTo(-34, 30);
    g.lineTo(-40, -28);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, color, 0.85);
    g.strokePath();
    // Crossed-swords slash to suggest a battle emblem.
    g.lineStyle(3, color, 0.95);
    g.lineBetween(-20, -16, 20, 28);
    g.lineBetween(20, -16, -20, 28);
    emblem.add(g);
    return emblem;
  }

  private showBattleCutIn(key: string, label: string, color: number, lane?: LaneNumber) {
    const now = this.time.now;
    if (now - (this.lastCutInAt.get(key) ?? -Infinity) < CUTIN_COOLDOWN_MS) return;
    this.lastCutInAt.set(key, now);
    this.logger.log("CUTIN_TRIGGERED", this.battleTime, { key, label }, lane);

    const container = this.add.container(640, 118).setDepth(210).setScrollFactor(0);
    const panel = this.add.rectangle(0, 0, 520, 58, 0x08070a, 0.78).setStrokeStyle(2, color, 0.95);
    const accent = this.add.rectangle(-252, 0, 8, 58, color, 0.95);
    const text = this.add
      .text(0, 0, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "23px" : "22px",
        color: "#f7f2df",
        stroke: "#08070a",
        strokeThickness: 5,
        fixedWidth: 470
      })
      .setOrigin(0.5)
      .setAlign("center");
    container.add([panel, accent, text]);
    container.setAlpha(0);
    this.tweens.add({
      targets: container,
      alpha: { from: 0, to: 1 },
      y: { from: 90, to: 118 },
      duration: 180,
      ease: "Sine.easeOut",
      yoyo: true,
      hold: 760,
      onComplete: () => container.destroy(true)
    });
  }

  private showPlayerUltimatePulse() {
    const pulse = this.add.graphics().setDepth(90);
    pulse.lineStyle(4, 0x8dffbf, 0.72);
    pulse.strokeCircle(mapConfig.playerCastleX - 120, 360, 98);
    this.tweens.add({
      targets: pulse,
      alpha: 0,
      scaleX: 1.8,
      scaleY: 1.8,
      duration: 760,
      ease: "Sine.easeOut",
      onComplete: () => pulse.destroy()
    });
  }

  private showAiUltimateMarker(lane: LaneNumber) {
    const y = this.laneY(lane);
    const marker = this.add.graphics().setDepth(91);
    marker.fillStyle(0x5a1028, 0.16);
    marker.fillRect(mapConfig.aiFortressX + 110, y - 34, mapConfig.playerCastleX - mapConfig.aiFortressX - 200, 68);
    marker.lineStyle(3, 0xff604f, 0.75);
    marker.strokeRect(mapConfig.aiFortressX + 110, y - 34, mapConfig.playerCastleX - mapConfig.aiFortressX - 200, 68);
    this.tweens.add({
      targets: marker,
      alpha: 0.16,
      duration: 520,
      yoyo: true,
      repeat: 5,
      onComplete: () => marker.destroy()
    });
  }

  private upgradeGoldIncome() {
    const cost = this.economy.incomeUpgradeCost;
    if (!Number.isFinite(cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "incomeUpgradeMaxed"));
      return;
    }
    if (!this.economy.upgradeIncome()) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }
    this.buildPanel.setStatus(`${t(this.settings.language, "incomeUpgraded")} +${this.economy.incomePerSecond}/s`);
    this.recentActions.push(`upgraded_income_${this.economy.incomeLevel}`);
    this.logger.log("ECONOMY_UPGRADED", this.battleTime, {
      level: this.economy.incomeLevel,
      cost,
      incomePerSecond: this.economy.incomePerSecond,
      playerGoldAfter: this.economy.gold
    });
  }

  private selectEconomyInvestment() {
    if (!this.economy.canAfford(ECONOMY_INVESTMENT_COST)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }
    this.selection = { kind: "economy", id: "gold_vault", cost: ECONOMY_INVESTMENT_COST };
    this.interactionMode = "build";
    this.clearSelectedUnits();
    this.buildPanel.setStatus(t(this.settings.language, "economyBuildReady"));
  }

  private buildEconomyInvestment(lane: LaneNumber) {
    if (!this.economyInvestments.canBuild(lane)) {
      this.buildPanel.setStatus(t(this.settings.language, "economyAlreadyBuilt"));
      return;
    }
    if (!this.economy.spend(ECONOMY_INVESTMENT_COST)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }
    this.economyInvestments.build(lane, this.time.now);
    this.selection = undefined;
    this.interactionMode = "unitCommand";
    this.buildPanel.setStatus(`${t(this.settings.language, "economyBuilt")} ${lane}`);
    this.recentActions.push(`built_economy_investment_lane_${lane}`);
  }

  private researchTech(tech: TechUpgradeId) {
    const cost = this.tech.costFor(tech);
    if (!Number.isFinite(cost)) {
      this.buildPanel.setStatus(this.tech.has(tech) ? t(this.settings.language, "techAlreadyResearched") : t(this.settings.language, "techLimitReached"));
      return;
    }
    if (!this.economy.spend(cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }
    if (!this.tech.research(tech)) {
      this.buildPanel.setStatus(t(this.settings.language, "techLimitReached"));
      return;
    }
    for (const unit of this.playerUnits) {
      this.applySingleTechToUnit(unit, tech);
    }
    for (const tower of this.towers) {
      this.applySingleTechToTower(tower, tech);
    }
    this.recentActions.push(`researched_${tech}`);
    this.upgradePatterns.push(`tech_${tech}`);
    this.logger.log("TECH_RESEARCHED", this.battleTime, { tech, cost, researched: this.tech.researched, playerGoldAfter: this.economy.gold });
    this.buildPanel.setStatus(`${t(this.settings.language, "techResearched")}: ${this.techLabel(tech)}`);
  }

  private applyTechToUnit(unit: Unit) {
    for (const tech of this.tech.researched) {
      this.applySingleTechToUnit(unit, tech);
    }
  }

  private applySingleTechToUnit(unit: Unit, tech: TechUpgradeId) {
    if (!unit.alive) return;
    if (tech === "infantry_drills" && ["swordsman", "shield_soldier", "spearman_new", "standard_bearer"].includes(unit.id)) {
      this.boostUnitHp(unit, 0.14);
      unit.damage = Math.round(unit.damage * 1.06);
    } else if (tech === "arcane_focus" && ["frost_mage", "alchemist", "monk"].includes(unit.id)) {
      unit.damage = unit.damage < 0 ? Math.round(unit.damage * 1.18) : Math.round(unit.damage * 1.14);
      unit.range = Math.round(unit.range + 10);
    } else if (tech === "siege_engineering" && unit.id === "powder_carrier") {
      unit.damage = Math.round(unit.damage * 1.18);
      unit.range = Math.round(unit.range + 12);
    } else if (tech === "fortification_plans" && ["engineer", "shield_soldier"].includes(unit.id)) {
      this.boostUnitHp(unit, 0.1);
    }
    unit.sprite.setTint(0xf8efc9);
    this.time.delayedCall(140, () => {
      if (unit.alive) unit.sprite.clearTint();
    });
  }

  private boostUnitHp(unit: Unit, ratio: number) {
    const gain = Math.max(1, Math.round(unit.maxHp * ratio));
    unit.maxHp += gain;
    unit.hp += gain;
  }

  private applyTechToTower(tower: Tower) {
    for (const tech of this.tech.researched) {
      this.applySingleTechToTower(tower, tech);
    }
  }

  private applySingleTechToTower(tower: Tower, tech: TechUpgradeId) {
    if (tower.destroyed) return;
    if (tech === "siege_engineering" && ["heavy_ballista", "mortar_emplacement", "powder_trap"].includes(tower.id)) {
      tower.damage = Math.round(tower.damage * 1.14);
      tower.range = Math.round(tower.range + 10);
    } else if (tech === "fortification_plans") {
      const ratio = tower.blocker ? 0.24 : 0.13;
      const gain = Math.max(1, Math.round(tower.maxHp * ratio));
      tower.maxHp += gain;
      tower.hp += gain;
    }
    tower.sprite.setTint(0xf8efc9);
    this.time.delayedCall(140, () => {
      if (!tower.destroyed) tower.sprite.clearTint();
    });
  }

  private techLabel(tech: TechUpgradeId) {
    const ko = this.settings.language === "ko";
    if (tech === "infantry_drills") return ko ? "보병 훈련" : "Infantry Drills";
    if (tech === "arcane_focus") return ko ? "마력 집중" : "Arcane Focus";
    if (tech === "siege_engineering") return ko ? "공성 공학" : "Siege Engineering";
    return ko ? "요새 설계" : "Fortification Plans";
  }

  private upgradeCounterCharge() {
    const cost = this.counter.chargeUpgradeCost;
    if (!Number.isFinite(cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "counterChargeMaxed"));
      return;
    }
    if (!this.counter.upgradeCharge((price) => this.economy.spend(price))) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }
    this.buildPanel.setStatus(`${t(this.settings.language, "counterChargeUpgraded")} Lv.${this.counter.chargeLevel}`);
    this.recentActions.push(`upgraded_counter_charge_${this.counter.chargeLevel}`);
    this.logger.log("COUNTER_CHARGE_UPGRADED", this.battleTime, {
      level: this.counter.chargeLevel,
      cost,
      gauge: this.counter.gauge,
      playerGoldAfter: this.economy.gold
    });
  }

  private repairWeakestCastleSegment() {
    const target = this.repairTargetSegment();
    if (!target) {
      this.buildPanel.setStatus(t(this.settings.language, "castleRepairFull"));
      return;
    }

    const cooldownMs = this.castleRepairCooldownMs(target.id);
    if (cooldownMs > 0) {
      this.buildPanel.setStatus(`${t(this.settings.language, "castleRepairCoolingDown")} ${Math.ceil(cooldownMs / 1000)}s`);
      return;
    }

    const missingHp = target.castle.maxHp - target.castle.hp;
    const repairedHp = Math.min(CASTLE_REPAIR_MAX_HP, missingHp);
    const cost = this.castleRepairCostFor(target.castle);
    if (!this.economy.spend(cost)) {
      this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
      return;
    }

    target.castle.heal(repairedHp);
    this.lastCastleRepairAt[target.id] = this.time.now;
    this.recentActions.push(`repaired_castle_${target.id}`);
    this.logger.log("CASTLE_REPAIRED", this.battleTime, {
      segment: target.id,
      repairedHp: Math.round(repairedHp),
      cost,
      playerGoldAfter: this.economy.gold
    });
    this.buildPanel.setStatus(`${t(this.settings.language, "castleRepaired")}: ${this.castleSegmentLabel(target.id)} +${Math.round(repairedHp)}`);
  }

  private repairTargetSegment() {
    return (Object.entries(this.playerCastles) as Array<[CastleSegmentId, Castle]>)
      .map(([id, castle]) => ({ id, castle, missingHp: castle.maxHp - castle.hp }))
      .filter((entry) => entry.missingHp > 0 && (entry.id !== "main" || entry.castle.hp > 0))
      .sort((a, b) => a.castle.hp / a.castle.maxHp - b.castle.hp / b.castle.maxHp || b.missingHp - a.missingHp)[0];
  }

  private currentCastleRepairCost() {
    const target = this.repairTargetSegment();
    return target ? this.castleRepairCostFor(target.castle) : Infinity;
  }

  private castleRepairCostFor(castle: Castle) {
    const missingHp = Math.max(0, castle.maxHp - castle.hp);
    if (missingHp <= 0) return Infinity;
    return Math.max(12, Math.ceil(Math.min(CASTLE_REPAIR_MAX_HP, missingHp) * CASTLE_REPAIR_GOLD_PER_HP));
  }

  private currentCastleRepairCooldownMs() {
    const target = this.repairTargetSegment();
    return target ? this.castleRepairCooldownMs(target.id) : 0;
  }

  private castleRepairCooldownMs(segment: CastleSegmentId) {
    const lastRepairAt = this.lastCastleRepairAt[segment] ?? -CASTLE_REPAIR_COOLDOWN_MS;
    return Math.max(0, CASTLE_REPAIR_COOLDOWN_MS - (this.time.now - lastRepairAt));
  }

  private castleSegmentLabel(segment: CastleSegmentId) {
    if (segment === "top") return t(this.settings.language, "topSegment");
    if (segment === "bottom") return t(this.settings.language, "bottomSegment");
    return t(this.settings.language, "mainSegment");
  }

  private applyAiManeuverCommands(commands: UnitManeuverCommand[]) {
    for (const command of commands) {
      const run = () => this.applyAiManeuverCommand(command);
      if ((command.delayMs ?? 0) > 0) {
        this.time.delayedCall(command.delayMs ?? 0, run);
      } else {
        run();
      }
    }
  }

  private applyAiManeuverCommand(command: UnitManeuverCommand) {
    const targetX = command.targetX ?? this.defaultManeuverX(command);
    const candidates = this.enemies
      .filter((enemy) => enemy.alive && enemy.lane === command.sourceLane)
      .sort((a, b) => this.maneuverSort(command, a, b))
      .slice(0, Math.max(1, command.maxUnits));

    let issued = 0;
    for (let index = 0; index < candidates.length; index += 1) {
      const enemy = candidates[index]!;
      const direction = targetX < enemy.x ? -1 : 1;
      const formationX = this.formationTargetX(enemy, targetX, index, candidates.length, direction);
      const waypoints = this.createUnitPath(enemy, command.targetLane, formationX);
      if (waypoints.length === 0) continue;
      const fromLane = enemy.lane;
      enemy.commandMove(waypoints, this.time.now, command.mode === "retreat" ? 9000 : 6000, command.mode);
      this.showMovePath([{ x: enemy.x, y: enemy.y }, ...waypoints], { lineColor: 0xffb15a, fillColor: 0xff604f, alpha: 0.5, depth: 56 });
      this.showAiManeuverBadge(enemy, command.mode);
      this.logger.log("AI_MANEUVER_COMMAND", this.battleTime, { enemyId: enemy.id, mode: command.mode, fromLane, targetLane: command.targetLane, targetX: Math.round(formationX) }, fromLane);
      if (fromLane !== command.targetLane) {
        this.logger.log("UNIT_LANE_CHANGED", this.battleTime, { unitId: enemy.id, owner: "enemy", fromLane, targetLane: command.targetLane }, fromLane);
      }
      issued += 1;
    }

    if (issued > 0) {
      this.recentActions.push(`ai_${command.mode}_lane_${command.sourceLane}_to_${command.targetLane}`);
    }
    return issued;
  }

  private showAiManeuverBadge(enemy: Unit, mode: UnitManeuverCommand["mode"]) {
    const labelText = mode === "retreat" ? "RET" : mode === "reinforce" ? "SUP" : "MOV";
    const color = mode === "retreat" ? "#d5d8df" : mode === "reinforce" ? "#ffd36e" : "#ffb15a";
    const badge = this.add
      .text(enemy.x, enemy.y - 48, labelText, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "11px",
        color,
        stroke: "#111016",
        strokeThickness: 3
      })
      .setResolution(2)
      .setOrigin(0.5)
      .setDepth(88);
    this.tweens.add({
      targets: badge,
      y: badge.y - 18,
      alpha: 0,
      duration: 980,
      ease: "Sine.easeOut",
      onComplete: () => badge.destroy()
    });
  }

  private defaultManeuverX(command: UnitManeuverCommand) {
    if (command.mode === "retreat") return mapConfig.aiFortressX + 150;
    if (command.mode === "advance") return mapConfig.playerCastleX - 150;
    return Phaser.Math.Clamp((mapConfig.aiFortressX + mapConfig.playerCastleX) / 2, mapConfig.aiFortressX + 84, mapConfig.playerCastleX - 84);
  }

  private maneuverSort(command: UnitManeuverCommand, a: Unit, b: Unit) {
    if (command.mode === "retreat") return b.x - a.x;
    return b.x - a.x;
  }

  private maybeRunFieldCommanderManeuver(time: number) {
    if (time - this.lastFieldManeuverAt < 16500 || this.enemies.filter((enemy) => enemy.alive).length < 2) return;
    this.lastFieldManeuverAt = time;
    const command = this.createFieldManeuverCommand();
    if (command) {
      const issued = this.applyAiManeuverCommand(command);
      if (issued > 0) {
        const key =
          command.mode === "retreat"
            ? "aiRetreatObserved"
            : command.mode === "reinforce"
              ? "aiReinforceObserved"
              : "aiManeuverObserved";
        this.chat.pushTaunt(t(this.settings.language, key), "tactical");
      }
    }
  }

  private createFieldManeuverCommand(): UnitManeuverCommand | undefined {
    const states = this.createLaneStates();
    return this.createRetreatManeuverCommand(states) ?? this.createReinforceManeuverCommand(states) ?? this.createPressureRotateCommand(states);
  }

  private createRetreatManeuverCommand(states: LaneStateSummary[]): UnitManeuverCommand | undefined {
    const source = [...states]
      .filter((state) => {
        const enemyCount = state.enemyUnitCount ?? 0;
        const playerCommitment = state.playerCommitmentScore ?? 0;
        const enemyResponse = state.enemyResponseScore ?? 0;
        const moraleBroken = (state.enemyMorale ?? 60) < 45 && playerCommitment >= enemyResponse + 2;
        return enemyCount >= 1 && enemyCount <= 3 && (playerCommitment >= enemyResponse + 4 || moraleBroken) && (state.openAdjacentLanes?.length ?? 0) > 0;
      })
      .sort((a, b) =>
        (b.playerCommitmentScore ?? 0) - (a.playerCommitmentScore ?? 0) ||
        (a.enemyMorale ?? 60) - (b.enemyMorale ?? 60) ||
        (b.enemyEncircledUnits ?? 0) - (a.enemyEncircledUnits ?? 0) ||
        (b.forwardPlayerUnits ?? 0) - (a.forwardPlayerUnits ?? 0)
      )[0];
    if (!source) return undefined;

    const targetLane = this.safestOpenLaneForRetreat(source, states);
    if (!targetLane) return undefined;
    return {
      sourceLane: source.lane,
      targetLane,
      targetX: mapConfig.aiFortressX + 170,
      maxUnits: Math.min(3, Math.max(1, source.enemyUnitCount ?? 1)),
      mode: "retreat"
    };
  }

  private safestOpenLaneForRetreat(source: LaneStateSummary, states: LaneStateSummary[]) {
    const open = source.openAdjacentLanes ?? [];
    return open
      .map((lane) => states.find((state) => state.lane === lane))
      .filter((state): state is LaneStateSummary => Boolean(state))
      .sort((a, b) =>
        (a.playerCommitmentScore ?? 0) - (b.playerCommitmentScore ?? 0) ||
        this.riskRank(a.riskLevel) - this.riskRank(b.riskLevel) ||
        (b.enemyUnitCount ?? 0) - (a.enemyUnitCount ?? 0)
      )[0]?.lane;
  }

  private maybeTriggerAiTrap(time: number) {
    if (this.trapCount >= 2 || this.battleTime < 85 || time - this.lastTrapAt < AI_TRAP_COOLDOWN_MS) return;
    const states = this.createLaneStates();
    const bait = [...states]
      .filter((state) => (state.playerCommitmentScore ?? 0) >= 5 || (state.forwardPlayerUnits ?? 0) >= 3)
      .sort((a, b) =>
        (b.playerCommitmentScore ?? 0) - (a.playerCommitmentScore ?? 0) ||
        (b.forwardPlayerUnits ?? 0) - (a.forwardPlayerUnits ?? 0) ||
        (b.playerThreatToAiCastle ?? 0) - (a.playerThreatToAiCastle ?? 0)
      )[0];
    if (!bait) return;
    const trapLane = this.trapAdjacentLane(bait, states);
    if (!trapLane) return;

    this.lastTrapAt = time;
    this.trapCount += 1;
    this.chat.pushTaunt(this.aiTrapLine(bait.lane, trapLane), "tactical");
    this.logger.log("AI_EVENT_TRIGGERED", this.battleTime, { type: "trap_lane", baitLane: bait.lane, targetLane: trapLane }, trapLane);
    const reports = this.waves.applyGameCommand(
      {
        taunt: "",
        spawnCommands: [
          { lane: trapLane, units: ["black_assassin", "raider"], purpose: "counter", delayMs: 4200 },
          { lane: bait.lane, units: ["shield_soldier"], purpose: "fake_pressure", delayMs: 1200 }
        ],
        difficultyAdjustment: 0.025,
        commanderMood: "tactical",
        detectedPattern: "ai_trap_lane",
        preferredEnemyRoles: ["flanker", "rush"],
        towerAttackIntent: "ignore",
        objectiveDisruptionIntent: "delay",
        reason: "AI trap lane punished a high-commitment player push."
      },
      this.enemies
    );
    this.logAiBudgetReports(reports);
  }

  private trapAdjacentLane(bait: LaneStateSummary, states: LaneStateSummary[]) {
    const candidates = (bait.openAdjacentLanes ?? [])
      .map((lane) => states.find((state) => state.lane === lane))
      .filter((state): state is LaneStateSummary => Boolean(state))
      .filter((state) => this.hasPathBetweenLanes(bait.lane, state.lane));
    const defenseRank: Record<LaneStateSummary["playerDefenseLevel"], number> = { none: 0, low: 1, medium: 2, high: 3, very_high: 4 };
    return candidates
      .sort((a, b) =>
        defenseRank[a.playerDefenseLevel] - defenseRank[b.playerDefenseLevel] ||
        (a.playerUnitCount ?? 0) - (b.playerUnitCount ?? 0) ||
        Math.abs(a.lane - 3) - Math.abs(b.lane - 3)
      )[0]?.lane;
  }

  private aiTrapLine(baitLane: LaneNumber, trapLane: LaneNumber) {
    if (this.settings.language === "ko") {
      return `${baitLane}번 전선에 눈이 팔렸군. ${trapLane}번 전선의 칼날은 이제야 보일 것이다.`;
    }
    return `Your eyes stayed on lane ${baitLane}. Now you will see the knife on lane ${trapLane}.`;
  }

  private createReinforceManeuverCommand(states: LaneStateSummary[]): UnitManeuverCommand | undefined {
    const target = [...states]
      .filter((state) => (state.playerCommitmentScore ?? 0) >= 6 || (state.enemyMorale ?? 60) < 50 || state.riskLevel === "danger" || state.riskLevel === "collapse")
      .sort((a, b) =>
        this.riskRank(b.riskLevel) - this.riskRank(a.riskLevel) ||
        (b.playerCommitmentScore ?? 0) - (a.playerCommitmentScore ?? 0) ||
        (a.enemyMorale ?? 60) - (b.enemyMorale ?? 60) ||
        (b.forwardPlayerUnits ?? 0) - (a.forwardPlayerUnits ?? 0)
      )[0];
    if (!target) return undefined;

    const source = [...states]
      .filter((state) => state.lane !== target.lane && (state.enemyUnitCount ?? 0) >= 2 && this.hasPathBetweenLanes(state.lane, target.lane))
      .sort((a, b) =>
        Math.abs(a.lane - target.lane) - Math.abs(b.lane - target.lane) ||
        (b.enemyUnitCount ?? 0) - (a.enemyUnitCount ?? 0) ||
        (a.playerCommitmentScore ?? 0) - (b.playerCommitmentScore ?? 0)
      )[0];
    if (!source) return undefined;

    return {
      sourceLane: source.lane,
      targetLane: target.lane,
      targetX: Phaser.Math.Clamp((mapConfig.aiFortressX + mapConfig.playerCastleX) / 2 + 80, mapConfig.aiFortressX + 84, mapConfig.playerCastleX - 180),
      maxUnits: Math.min(2, Math.max(1, Math.floor((source.enemyUnitCount ?? 2) / 2))),
      mode: "reinforce"
    };
  }

  private createPressureRotateCommand(states: LaneStateSummary[]): UnitManeuverCommand | undefined {
    const defenseRank: Record<LaneStateSummary["playerDefenseLevel"], number> = { none: 0, low: 1, medium: 2, high: 3, very_high: 4 };
    const target = [...states]
      .filter((state) => (state.enemyUnitCount ?? 0) <= 3)
      .sort((a, b) =>
        defenseRank[a.playerDefenseLevel] - defenseRank[b.playerDefenseLevel] ||
        (a.playerUnitCount ?? 0) - (b.playerUnitCount ?? 0) ||
        (b.recentDamageToCastle ?? 0) - (a.recentDamageToCastle ?? 0)
      )[0];
    if (!target) return undefined;

    const source = [...states]
      .filter((state) => state.lane !== target.lane && (state.enemyUnitCount ?? 0) >= 2)
      .sort((a, b) => (b.enemyUnitCount ?? 0) - (a.enemyUnitCount ?? 0) || Math.abs(a.lane - target.lane) - Math.abs(b.lane - target.lane))[0];
    if (!source) return undefined;

    const flanking = defenseRank[target.playerDefenseLevel] < 3;
    return {
      sourceLane: source.lane,
      targetLane: target.lane,
      targetX: target.playerDefenseLevel === "none" || target.playerDefenseLevel === "low" ? mapConfig.playerCastleX - 190 : 760,
      maxUnits: flanking ? 2 : 1,
      mode: "rotate"
    };
  }

  private hasPathBetweenLanes(sourceLane: LaneNumber, targetLane: LaneNumber) {
    let lane = sourceLane;
    while (lane !== targetLane) {
      const nextLane = (lane + Math.sign(targetLane - lane)) as LaneNumber;
      if (!this.bestCrossing(lane, nextLane, mapConfig.enemySpawnX, mapConfig.playerCastleX - 220, "enemy")) return false;
      lane = nextLane;
    }
    return true;
  }

  private riskRank(level: LaneStateSummary["riskLevel"]) {
    switch (level) {
      case "collapse":
        return 4;
      case "danger":
        return 3;
      case "contested":
        return 2;
      case "watch":
        return 1;
      case "safe":
      default:
        return 0;
    }
  }

  private createCastleGroup(side: "player" | "ai"): CastleGroup {
    const x = side === "player" ? mapConfig.playerCastleX : mapConfig.aiFortressX;
    const texture = side === "player" ? "player_castle" : "ai_fortress";
    const tint = side === "player" ? 0x70d7ff : 0xff604f;
    const labelBase = side === "player" ? t(this.settings.language, "blueCastle") : t(this.settings.language, "aiFortress");
    const layout = loadCastleLayout()[side];
    // War Funds shop: "Fortify" boosts ONLY the player's castle HP.
    const hpMult = side === "player" ? fortressHpMultiplier(loadPlayerMemory(getOrCreatePlayerId()).upgrades.fortify ?? 0) : 1;
    const sideHp = Math.round(SIDE_CASTLE_HP * hpMult);
    const mainHp = Math.round(MAIN_CASTLE_HP * hpMult);
    const group: CastleGroup = {
      top: new Castle(this, x, 186, texture, `${labelBase} ${t(this.settings.language, "topSegment")}`, tint, sideHp, 0.43),
      main: new Castle(this, x, 360, texture, `${labelBase} ${t(this.settings.language, "mainSegment")}`, tint, mainHp, 0.62),
      bottom: new Castle(this, x, 534, texture, `${labelBase} ${t(this.settings.language, "bottomSegment")}`, tint, sideHp, 0.43)
    };
    // Apply the visual layout (default = original on-map position; or a hand-placed override
    // from the castle-edit tool). Gameplay coordinate stays at mapConfig.* untouched.
    (Object.keys(group) as CastleSegmentId[]).forEach((seg) => {
      const tf = layout[seg];
      group[seg].setVisualTransform(tf.x, tf.y, tf.scale);
    });
    return group;
  }

  private segmentForLane(lane: LaneNumber): CastleSegmentId {
    if (lane <= 2) return "top";
    if (lane >= 4) return "bottom";
    return "main";
  }

  private damageCastleForLane(group: CastleGroup, lane: LaneNumber, damage: number) {
    const preferred = this.segmentForLane(lane);
    const target = preferred === "main" || group[preferred].hp > 0 ? group[preferred] : group.main;
    const damagedSegment = target === group.main ? "main" : preferred;
    const owner = group === this.playerCastles ? "player" : "ai";
    target.takeDamage(damage);
    if (target.hp <= 0) {
      target.setDestroyedTint();
      if (target === group.main) {
        group.top.setDestroyedTint();
        group.bottom.setDestroyedTint();
      }
    }
    this.logger.log("CASTLE_DAMAGED", this.battleTime, { owner, segment: damagedSegment, damage, remainingHp: target.hp }, lane);
    if (owner === "player") {
      this.addAiUltimate(Math.min(10, Math.max(2, damage / 8)));
      this.addPlayerUltimate(Math.min(4, Math.max(1, damage / 18)));
    } else {
      this.addPlayerUltimate(Math.min(10, Math.max(2, damage / 8)));
      this.addAiUltimate(Math.min(4, Math.max(1, damage / 18)));
    }
    return damagedSegment;
  }

  private triggerLaneCollapse(lane: LaneNumber) {
    const now = this.time.now;
    const repeated = now - (this.laneLastCollapseAt.get(lane) ?? -Infinity) <= LANE_COLLAPSE_REPEAT_WINDOW_MS;
    const directPenalty = repeated ? 27 : 18;
    this.applyLaneCollapse(lane, LANE_COLLAPSE_MS, directPenalty);
    for (const adjacent of this.adjacentLanes(lane)) {
      this.applyLaneCollapse(adjacent, ADJACENT_LANE_COLLAPSE_MS, repeated ? 12 : 8);
    }
    this.laneLastCollapseAt.set(lane, now);
    this.showLaneCollapseMarker(lane, repeated);
  }

  private applyLaneCollapse(lane: LaneNumber, durationMs: number, penalty: number) {
    const current = this.laneCollapseStates.get(lane);
    this.laneCollapseStates.set(lane, {
      until: Math.max(current?.until ?? 0, this.time.now + durationMs),
      penalty: Math.max(current?.penalty ?? 0, penalty)
    });
  }

  private adjacentLanes(lane: LaneNumber): LaneNumber[] {
    return [lane - 1, lane + 1].filter((value): value is LaneNumber => value >= 1 && value <= 5);
  }

  private showLaneCollapseMarker(lane: LaneNumber, repeated: boolean) {
    const y = this.laneY(lane);
    const marker = this.add.graphics().setDepth(68);
    marker.lineStyle(repeated ? 4 : 3, repeated ? 0xff2f2f : 0xff604f, repeated ? 0.72 : 0.52);
    marker.lineBetween(mapConfig.aiFortressX + 120, y + 24, mapConfig.playerCastleX - 90, y + 24);
    marker.lineStyle(1, 0xffd36e, 0.48);
    marker.lineBetween(mapConfig.aiFortressX + 150, y + 30, mapConfig.playerCastleX - 120, y + 30);
    this.tweens.add({
      targets: marker,
      alpha: 0.12,
      duration: 420,
      yoyo: true,
      repeat: 5,
      onComplete: () => marker.destroy()
    });
  }

  private castleSummary(group: CastleGroup): CastleStateSummary {
    return {
      top: this.castleSegmentSummary("top", group.top),
      main: this.castleSegmentSummary("main", group.main),
      bottom: this.castleSegmentSummary("bottom", group.bottom)
    };
  }

  private castleSegmentSummary(id: CastleSegmentId, castle: Castle) {
    return {
      id,
      hp: castle.hp,
      maxHp: castle.maxHp,
      destroyed: castle.hp <= 0,
      damageStage: castle.damageStage
    };
  }

  private tryUpgradeAt(x: number, y: number) {
    const tower = this.towers
      .filter((candidate) => !candidate.destroyed)
      .sort((a, b) => Phaser.Math.Distance.Between(x, y, a.x, a.y) - Phaser.Math.Distance.Between(x, y, b.x, b.y))[0];
    if (tower && Phaser.Math.Distance.Between(x, y, tower.x, tower.y) <= 44) {
      if (!this.economy.spend(tower.upgradeCost)) {
        this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
        return;
      }
      const changed = tower.upgrade();
      this.upgradePatterns.push(`tower_${tower.id}_level_${tower.level}_${changed.join("_")}`);
      this.logger.log("TOWER_UPGRADED", this.battleTime, { towerId: tower.id, level: tower.level, changed }, tower.lane);
      this.buildPanel.setStatus(`${t(this.settings.language, "towerUpgraded")} Lv.${tower.level}`);
      this.showTowerRange(tower);
      return;
    }

    const unit = this.playerUnits
      .filter((candidate) => candidate.alive)
      .sort((a, b) => Phaser.Math.Distance.Between(x, y, a.x, a.y) - Phaser.Math.Distance.Between(x, y, b.x, b.y))[0];
    if (unit && Phaser.Math.Distance.Between(x, y, unit.x, unit.y) <= 34) {
      const cost = 45 + unit.level * 35;
      if (!this.economy.spend(cost)) {
        this.buildPanel.setStatus(t(this.settings.language, "needMoreGold"));
        return;
      }
      const changed = unit.upgrade();
      this.upgradePatterns.push(`unit_${unit.id}_level_${unit.level}_${changed.join("_")}`);
      this.logger.log("UNIT_UPGRADED", this.battleTime, { unitId: unit.id, level: unit.level, changed }, unit.lane);
      this.buildPanel.setStatus(`${t(this.settings.language, "unitUpgraded")} Lv.${unit.level}`);
    }
  }

  private handleTowerDestroyed(tower: Tower, attackerId: string) {
    if (this.destroyedTowerIds.includes(tower.padId)) return;
    this.destroyedTowerIds.push(tower.padId);
    this.occupiedPads.delete(tower.padId);
    this.clearTowerRangePreview();
    this.clearTowerPriorityMenu();
    this.padSprites.get(tower.padId)?.setAlpha(0.78);
    this.logger.log("TOWER_DESTROYED", this.battleTime, { towerId: tower.id, padId: tower.padId, attackerId }, tower.lane);
    this.buildPanel.setStatus(t(this.settings.language, "towerDestroyed"));
  }

  private objectiveRewardLabel(reward: "gold" | "points" | "buff") {
    if (reward === "gold") return t(this.settings.language, "goldReward");
    if (reward === "points") return t(this.settings.language, "pointReward");
    return t(this.settings.language, "buffReward");
  }

  private resolveCastleContacts() {
    for (const enemy of this.enemies) {
      if (!enemy) continue;
      if (enemy.x >= mapConfig.playerCastleX - 74) {
        enemy.setX(mapConfig.playerCastleX - 74);
        enemy.setMovementBlocked(true);
        if (!enemy.canAttack(this.time.now)) continue;
        const damage = Math.round(enemy.castleDamage * (enemy.targetPriority === "siege" ? enemy.siegeDamageMultiplier : 1));
        const segment = this.damageCastleForLane(this.playerCastles, enemy.lane, damage);
        this.economyInvestments.disableLane(enemy.lane, this.time.now);
        this.logger.log("LANE_BREACHED", this.battleTime, { enemyType: enemy.id, damageToCastle: damage, castleSegment: segment }, enemy.lane);
        this.triggerLaneCollapse(enemy.lane);
        enemy.markAttack(this.time.now);
      }
    }

    for (const unit of this.playerUnits) {
      if (!unit) continue;
      if (unit.x <= mapConfig.aiFortressX + 72) {
        unit.setX(mapConfig.aiFortressX + 72);
        unit.setMovementBlocked(true);
        if (!unit.canAttack(this.time.now)) continue;
        this.damageCastleForLane(this.aiCastles, unit.lane, Math.max(5, unit.damage));
        this.counter.add(3);
        unit.markAttack(this.time.now);
      }
    }
  }

  private updatePanels() {
    this.buildPanel.update(
      this.economy.gold,
      this.counter.gauge,
      this.counter.canActivate(),
      this.economy.incomeUpgradeCost,
      this.counter.chargeUpgradeCost,
      this.economy.upkeepPenaltyPercent,
      this.economy.effectiveIncomePerSecond,
      this.economy.economyOutpostCount,
      this.currentCastleRepairCost(),
      this.currentCastleRepairCooldownMs(),
      ECONOMY_INVESTMENT_COST,
      this.tech.state
    );
    const laneStates = this.createLaneStates();
    this.lanePanel.updateLaneStates(laneStates);
    this.miniMapPanel.update(laneStates, this.cameraViewport());
    this.updateUnitAbilityButton();
  }

  private updateUnitAbilityButton() {
    const unit = this.selectedAbilityUnit();
    const state = unit?.getManualAbilityState(this.time.now);
    if (!unit || !state) {
      this.destroyUnitAbilityButton();
      return;
    }

    if (!this.unitAbilityButton) {
      const button = this.add.container(1044, 596).setDepth(155).setScrollFactor(0);
      this.unitAbilityButtonBg = this.add.rectangle(0, 0, 188, 38, 0x101820, 0.9).setStrokeStyle(1, 0x8de7ff, 0.72);
      this.unitAbilityButtonLabel = this.add
        .text(0, -1, "", {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: this.settings.language === "ko" ? "13px" : "12px",
          color: "#f7f2df",
          stroke: "#08070a",
          strokeThickness: 2,
          fixedWidth: 176
        })
        .setOrigin(0.5)
        .setAlign("center")
        .setResolution(2);
      button.add([this.unitAbilityButtonBg, this.unitAbilityButtonLabel]);
      button.setSize(188, 38);
      button.setInteractive({ useHandCursor: true });
      button.on("pointerover", () => this.unitAbilityButtonBg?.setFillStyle(0x17364a, 0.96));
      button.on("pointerout", () => this.unitAbilityButtonBg?.setFillStyle(0x101820, 0.9));
      button.on("pointerdown", () => this.activateSelectedUnitAbility());
      this.unitAbilityButton = button;
      this.syncCameraLayers();
    }

    const active = state.activeRemainingMs > 0;
    const label = state.ready
      ? `A ${this.unitAbilityLabel(state.id)}`
      : `${this.unitAbilityLabel(state.id)} ${Math.ceil(state.cooldownRemainingMs / 1000)}s`;
    this.unitAbilityButtonLabel?.setText(active ? `${label} ${Math.ceil(state.activeRemainingMs / 1000)}s` : label);
    this.unitAbilityButtonBg?.setStrokeStyle(state.ready ? 2 : 1, active ? 0xffe58e : state.ready ? 0xa7ffcf : 0x7d8797, state.ready || active ? 0.95 : 0.72);
    this.unitAbilityButtonBg?.setFillStyle(active ? 0x2a2410 : state.ready ? 0x10251d : 0x101820, 0.92);
  }

  private destroyUnitAbilityButton() {
    this.unitAbilityButton?.destroy(true);
    this.unitAbilityButton = undefined;
    this.unitAbilityButtonBg = undefined;
    this.unitAbilityButtonLabel = undefined;
  }

  private showMerchantOffers(power: number) {
    if (this.merchantOffered || this.merchantPanel || this.battleTime < 55) return;
    this.merchantOffered = true;
    this.merchantExpiresAt = this.time.now + MERCHANT_OFFER_DURATION_MS;
    const offers = this.createMerchantOffers(power);
    const panel = this.add.container(640, 146).setDepth(180).setScrollFactor(0);
    const background = this.add.rectangle(0, 0, 520, 156, 0x15121a, 0.94).setStrokeStyle(2, 0xffd37a, 0.94);
    const title = this.add
      .text(0, -56, this.settings.language === "ko" ? "떠돌이 상인" : "Wandering Merchant", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "18px",
        color: "#ffe58e",
        stroke: "#08070a",
        strokeThickness: 4
      })
      .setOrigin(0.5);
    const subtitle = this.add
      .text(0, -32, this.settings.language === "ko" ? "12초 안에 전장 거래 하나를 고르세요" : "Choose one battlefield deal within 12 seconds", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: "#d9e9ff",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    panel.add([background, title, subtitle]);

    offers.forEach((offer, index) => {
      const x = -170 + index * 170;
      const button = this.add.container(x, 30);
      const frame = this.add.rectangle(0, 0, 152, 74, 0x17364a, 0.92).setStrokeStyle(1, 0x8de7ff, 0.86);
      const label = this.add
        .text(0, -14, offer.label, {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: "13px",
          color: "#f7f2df",
          stroke: "#08070a",
          strokeThickness: 3,
          fixedWidth: 136
        })
        .setOrigin(0.5)
        .setAlign("center");
      const description = this.add
        .text(0, 16, offer.description, {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: "11px",
          color: "#bfefff",
          stroke: "#08070a",
          strokeThickness: 2,
          fixedWidth: 136,
          wordWrap: { width: 136 }
        })
        .setOrigin(0.5)
        .setAlign("center");
      button.add([frame, label, description]);
      button.setSize(152, 74);
      button.setInteractive({ useHandCursor: true });
      button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
      button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.92));
      button.on("pointerdown", () => this.acceptMerchantOffer(offer));
      panel.add(button);
    });

    this.merchantPanel = panel;
    this.time.delayedCall(MERCHANT_OFFER_DURATION_MS, () => this.expireMerchantOffer());
  }

  private createMerchantOffers(power: number): MerchantOffer[] {
    const strength = Phaser.Math.Clamp(power || 0.35, 0.2, 0.6);
    const gold = Math.round(70 + strength * 80);
    const repair = Math.round(70 + strength * 130);
    const counter = Math.round(14 + strength * 24);
    if (this.settings.language === "ko") {
      return [
        { id: "gold", label: `골드 +${gold}`, description: "즉시 군자금", apply: () => this.economy.reward(gold) },
        { id: "repair", label: `성 수리 +${repair}`, description: "가장 약한 성벽", apply: () => this.applyFreeMerchantRepair(repair) },
        { id: "counter", label: `반격 +${counter}%`, description: "게이지 충전", apply: () => this.counter.add(counter) }
      ];
    }
    return [
      { id: "gold", label: `Gold +${gold}`, description: "Instant war funds", apply: () => this.economy.reward(gold) },
      { id: "repair", label: `Repair +${repair}`, description: "Weakest wall", apply: () => this.applyFreeMerchantRepair(repair) },
      { id: "counter", label: `Counter +${counter}%`, description: "Charge gauge", apply: () => this.counter.add(counter) }
    ];
  }

  private acceptMerchantOffer(offer: MerchantOffer) {
    if (!this.merchantPanel) return;
    offer.apply();
    this.logger.log("MERCHANT_OFFER_ACCEPTED", this.battleTime, { offerId: offer.id, expiresInMs: Math.max(0, Math.round(this.merchantExpiresAt - this.time.now)) });
    this.buildPanel.setStatus(this.settings.language === "ko" ? `상인 거래 완료: ${offer.label}` : `Merchant deal: ${offer.label}`);
    this.closeMerchantPanel();
  }

  private expireMerchantOffer() {
    if (!this.merchantPanel) return;
    this.logger.log("MERCHANT_OFFER_EXPIRED", this.battleTime, { durationMs: MERCHANT_OFFER_DURATION_MS });
    this.closeMerchantPanel();
  }

  private closeMerchantPanel() {
    this.merchantPanel?.destroy(true);
    this.merchantPanel = undefined;
  }

  private applyFreeMerchantRepair(amount: number) {
    const segment = this.weakestPlayerSegment();
    this.playerCastles[segment].heal(amount);
    this.logger.log("CASTLE_REPAIRED", this.battleTime, { segment, repairedHp: Math.round(amount), source: "merchant" });
  }

  private maybeRunMercyDirector(time: number) {
    const hpRatio = this.playerCastle.hp / this.playerCastle.maxHp;
    const pressure = this.enemyBreakPressure();
    if (time - this.lastHumiliationOfferAt > HUMILIATION_OFFER_COOLDOWN_MS && (hpRatio < 0.46 || pressure >= 7)) {
      this.lastHumiliationOfferAt = time;
      this.chat.pushTaunt(this.humiliationOfferLine(), hpRatio < 0.28 ? "furious" : "mocking");
    }

    if (time - this.lastMercyAt <= MERCY_COOLDOWN_MS) return;
    if ((hpRatio < 0.24 && pressure >= 4) || (hpRatio < 0.34 && pressure >= 8)) {
      this.applyCommanderMercy("auto");
    }
  }

  private maybeRunNarrativeTaunt(time: number) {
    if (time - this.lastNarrativeTauntAt < NARRATIVE_TAUNT_COOLDOWN_MS) return;
    if (this.battleTime < 18) return;

    const playerHpRatio = this.playerCastle.hp / this.playerCastle.maxHp;
    const aiHpRatio = this.aiCastle.hp / this.aiCastle.maxHp;
    const pressure = this.enemyBreakPressure();
    const memory = loadPlayerMemory(this.playerId);
    const nickname = memory.nickname;
    const context = this.narrativeTauntContext(playerHpRatio, aiHpRatio, pressure, memory.rivalryStage);
    if (!context || context.key === this.lastNarrativeTauntKey) return;

    this.lastNarrativeTauntAt = time;
    this.lastNarrativeTauntKey = context.key;
    this.chat.pushTaunt(this.narrativeTauntLine(context.key, nickname), context.mood);
  }

  private narrativeTauntContext(playerHpRatio: number, aiHpRatio: number, pressure: number, rivalryStage?: string): { key: string; mood: string } | undefined {
    if (playerHpRatio < 0.32 || pressure >= 8) return { key: "enemy_dominating", mood: "mocking" };
    if (aiHpRatio < 0.38) return { key: "player_dominating", mood: "furious" };
    if (this.counter.gauge >= 88) return { key: "counter_ready", mood: "tactical" };
    if (this.playerUltimateGauge >= 90) return { key: "player_ultimate_ready", mood: "tactical" };
    if (this.aiUltimateUntil > this.time.now) return { key: "ai_ultimate_active", mood: "furious" };
    if (rivalryStage === "obsessed" || rivalryStage === "respectful_enemy") return { key: "rivalry_memory", mood: rivalryStage === "obsessed" ? "furious" : "calm" };
    return undefined;
  }

  private narrativeTauntLine(key: string, nickname?: string) {
    const name = nickname ? `${nickname}, ` : "";
    if (this.settings.language === "ko") {
      const lines: Record<string, string[]> = {
        enemy_dominating: [
          `${name}성벽이 뒤로 물러나는 소리가 들리는군. 더 버텨 봐라.`,
          `${name}네 병사들이 명령보다 공포를 먼저 듣고 있다.`,
          `${name}지금 비는 것은 자비가 아니라 시간뿐이지.`
        ],
        player_dominating: [
          `${name}내 요새에 흠을 냈군. 그 오만의 값을 곧 받아 가겠다.`,
          `${name}좋다. 이제야 네 이름을 지도 위에 적을 이유가 생겼다.`,
          `${name}그 돌파는 기억하겠다. 그리고 꺾겠다.`
        ],
        counter_ready: [
          `${name}나팔을 숨겨도 게이지는 보인다. 헛치게 만들어 주마.`,
          `${name}반격을 모았군. 문제는 어디에 쓰느냐다.`,
          `${name}그 한 방을 기다렸다. 너무 일찍 쓰게 해 주지.`
        ],
        player_ultimate_ready: [
          `${name}병사들의 사기가 끓는군. 끓는 냄비는 엎으면 그만이다.`,
          `${name}그 함성, 오래가지 못한다.`,
          `${name}궁극기를 쥔 손은 언제나 먼저 떨린다.`
        ],
        ai_ultimate_active: [
          `${name}공포의 칙령이 내려졌다. 이 전선은 이제 내 목소리로 걷는다.`,
          `${name}무릎이 아니라 전선부터 꺾어 주마.`,
          `${name}내 명령 아래에서는 돌도 겁을 먹는다.`
        ],
        rivalry_memory: [
          `${name}네 습관은 이제 전장보다 먼저 도착한다.`,
          `${name}처음 보는 적은 아니다. 그래서 더 재미있지.`,
          `${name}너를 기억한다. 그건 칭찬이 아니라 경고다.`
        ]
      };
      return Phaser.Utils.Array.GetRandom(lines[key] ?? lines.rivalry_memory ?? ["너를 기억한다. 그건 칭찬이 아니라 경고다."]);
    }

    const lines: Record<string, string[]> = {
      enemy_dominating: [
        `${name}I can hear your wall stepping backward. Hold a little longer.`,
        `${name}your soldiers hear fear before orders now.`,
        `${name}you are not begging for mercy yet, only for time.`
      ],
      player_dominating: [
        `${name}you scarred my fortress. I will collect the price for that arrogance.`,
        `${name}good. Now your name is worth writing on the map.`,
        `${name}I will remember that breach, then break it.`
      ],
      counter_ready: [
        `${name}you can hide the horn, not the charge behind it.`,
        `${name}your counterattack is ready. The question is where I make you waste it.`,
        `${name}I have been waiting for that one blow. Spend it badly.`
      ],
      player_ultimate_ready: [
        `${name}your morale is boiling. Boiling pots spill easily.`,
        `${name}that war cry will not last.`,
        `${name}the hand holding an ultimate always trembles first.`
      ],
      ai_ultimate_active: [
        `${name}the dread decree is spoken. This lane marches in my voice now.`,
        `${name}I will break the lane before I ask for knees.`,
        `${name}even stone learns fear under my command.`
      ],
      rivalry_memory: [
        `${name}your habits arrive before the battle now.`,
        `${name}you are no stranger. That makes this more interesting.`,
        `${name}I remember you. That is warning, not praise.`
      ]
    };
    return Phaser.Utils.Array.GetRandom(lines[key] ?? lines.rivalry_memory ?? ["I remember you. That is warning, not praise."]);
  }

  private tryHumiliationPlea(text: string) {
    const intent = classifyTauntIntent(text);
    if (intent === "none") return false;
    // The player is taunting / ordering the AI to surrender. This must NEVER be
    // treated as the player begging (the original bug granted the player mercy
    // here). The commander refuses scornfully and no mercy is applied.
    if (intent === "demand") {
      this.chat.pushTaunt(this.surrenderDemandReply(), "furious");
      return true;
    }
    if (this.time.now - this.lastMercyAt < 8000) {
      this.chat.pushTaunt(
        this.settings.language === "ko" ? "방금 자비를 줬다. 너무 빨리 다시 기면 값이 떨어지지." : "I just granted mercy. Beg too quickly and it loses value.",
        "amused"
      );
      return true;
    }
    this.humiliationPleaCount += 1;
    this.applyCommanderMercy("plea");
    this.chat.pushTaunt(this.humiliationAcceptedLine(), "mocking");
    return true;
  }

  private surrenderDemandReply() {
    return this.settings.language === "ko"
      ? Phaser.Utils.Array.GetRandom([
          "내가 항복? 네 성벽이 먼저 무너질 거다. 계속 짖어 봐라.",
          "감히 나에게 항복을 명령해? 그 입을 전장에서 다물게 해주지.",
          "항복은 패자의 단어다. 네가 먼저 배우게 될 텐데."
        ])
      : Phaser.Utils.Array.GetRandom([
          "Me, surrender? Your walls will fall long before my knees do.",
          "You order me to yield? I will silence that mouth on the field.",
          "Surrender is a loser's word. You will learn it first."
        ]);
  }

  private applyCommanderMercy(reason: "auto" | "plea") {
    const now = this.time.now;
    this.lastMercyAt = now;
    const strongMercy = reason === "plea";
    this.difficulty.grantMercy(strongMercy ? 0.18 : 0.11);
    this.waves.delayPressure(strongMercy ? 9000 : 6000);
    this.playerCastle.heal(strongMercy ? 140 : 90);
    const pressuredSegment = this.weakestPlayerSegment();
    this.playerCastles[pressuredSegment].heal(strongMercy ? 90 : 55);

    const softened = this.enemies
      .filter((enemy) => enemy.alive)
      .sort((a, b) => b.x - a.x)
      .slice(0, strongMercy ? 6 : 4);
    for (const enemy of softened) {
      enemy.applySlow(strongMercy ? 0.52 : 0.38, now + (strongMercy ? 7600 : 5600));
      enemy.takeDamage(Math.max(4, Math.round(enemy.maxHp * (strongMercy ? 0.12 : 0.07))));
    }

    this.buildPanel.setStatus(t(this.settings.language, "aiMercyGranted"));
    this.logger.log("AI_MERCY_GRANTED", this.battleTime, {
      reason,
      pressure: this.difficulty.pressure,
      softenedEnemies: softened.length,
      pleaCount: this.humiliationPleaCount
    });
  }

  private enemyBreakPressure() {
    const closeEnemies = this.enemies.filter((enemy) => enemy.alive && enemy.x >= mapConfig.playerCastleX - 330).length;
    const siegeEnemies = this.enemies.filter((enemy) => enemy.alive && enemy.targetPriority === "siege" && enemy.x >= mapConfig.playerCastleX - 520).length;
    const damagedCastleWeight = this.playerCastle.hp / this.playerCastle.maxHp < 0.42 ? 3 : 0;
    return closeEnemies + siegeEnemies * 2 + damagedCastleWeight;
  }

  private weakestPlayerSegment(): CastleSegmentId {
    const entries = Object.entries(this.playerCastles) as Array<[CastleSegmentId, Castle]>;
    return entries.sort(([, a], [, b]) => a.hp / a.maxHp - b.hp / b.maxHp)[0]?.[0] ?? "main";
  }

  private humiliationOfferLine() {
    if (this.settings.language === "ko") {
      return Phaser.Utils.Array.GetRandom([
        "성문이 숨을 헐떡이는군. 삼배구고두례라도 하면, 한 박자쯤 늦춰 줄 수도 있지.",
        "이 정도면 전술이 아니라 처형이다. 무릎 꿇고 자비를 말해 봐라.",
        "네 성이 거의 울고 있다. 빌어라. 그러면 내 병사들이 웃느라 조금 늦을지도 모르지."
      ]);
    }
    return Phaser.Utils.Array.GetRandom([
      "Your gate is wheezing. Kneel properly, and I may delay the next blow.",
      "This is becoming an execution, not a battle. Beg, and my soldiers may laugh long enough to slow down.",
      "Your castle is almost pleading for you. Try saying mercy before I finish the lesson."
    ]);
  }

  private humiliationAcceptedLine() {
    if (this.settings.language === "ko") {
      return Phaser.Utils.Array.GetRandom([
        "하하, 들었다. 그 굴욕값으로 숨 몇 번은 사게 해 주마.",
        "좋다. 그 무릎값만큼 전선을 조금 늦춰 주지.",
        "자비라. 아주 작게, 아주 모욕적으로 내려 주마."
      ]);
    }
    return Phaser.Utils.Array.GetRandom([
      "I heard that. Your humiliation buys a few breaths.",
      "Good. That kneeling is worth a small delay.",
      "Mercy, then. Tiny, insulting, and temporary."
    ]);
  }

  private maybeSendAiMessages(time: number) {
    if (!this.commanderSocket) return;

    if (time - this.lastEventsAt > 6000) {
      this.commanderSocket.sendEvents(this.logger.drainRecent());
      this.lastEventsAt = time;
    }

    const urgentSummary = this.createSummary();
    if (time - this.lastThreatDecisionAt > AI_URGENT_DECISION_COOLDOWN_MS && this.hasUrgentPlayerPush(urgentSummary)) {
      this.commanderSocket.requestDecision(urgentSummary);
      this.lastThreatDecisionAt = time;
      this.lastSummaryAt = time;
      return;
    }

    if (time - this.lastSummaryAt > 20000) {
      this.commanderSocket.sendSummary(urgentSummary);
      this.lastSummaryAt = time;
    }
  }

  private hasUrgentPlayerPush(summary: BattleSummary) {
    return summary.laneStates.some((state) => (state.playerCommitmentScore ?? 0) >= 6 || (state.forwardPlayerUnits ?? 0) >= 4);
  }

  private logAiBudgetReports(reports: AiBudgetReport[]) {
    for (const report of reports) {
      if (report.delayedUnits.length === 0 && report.deniedUnits.length === 0 && !report.debtUsed) continue;
      this.logger.log(
        "AI_BUDGET_COMMAND_LIMITED",
        this.battleTime,
        {
          purpose: report.purpose,
          requestedUnits: report.requestedUnits,
          approvedUnits: report.approvedUnits,
          delayedUnits: report.delayedUnits,
          deniedUnits: report.deniedUnits,
          spent: report.spent,
          budgetAfter: report.budgetAfter,
          debtUsed: report.debtUsed,
          delayMsAdded: report.delayMsAdded
        },
        report.lane
      );
      if (report.delayedUnits.length > 0 || report.deniedUnits.length > 0) {
        this.recentActions.push(`ai_budget_limited_lane_${report.lane}`);
      }
    }
  }

  private createSummary() {
    const recentCastlePressure = this.recentCastlePressure();
    return createBattleSummary({
      battleTime: this.battleTime,
      playerCastleHp: this.playerCastle.hp,
      aiCastleHp: this.aiCastle.hp,
      playerCastleSegments: this.castleSummary(this.playerCastles),
      aiCastleSegments: this.castleSummary(this.aiCastles),
      recentCastleDamageByLane: recentCastlePressure.damageByLane,
      pressuredPlayerCastleSegment: recentCastlePressure.segment,
      counterGauge: this.counter.gauge,
      commanderUltimate: this.commanderUltimateSummary(),
      playerGold: this.economy.gold,
      activePlayerUnits: this.playerUnits.filter((unit) => unit.alive).length,
      upkeepPenaltyPercent: this.economy.upkeepPenaltyPercent,
      effectiveIncomePerSecond: this.economy.effectiveIncomePerSecond,
      economyInvestmentIncomePerSecond: this.economy.investmentIncomePerSecond,
      economyInvestments: this.economyInvestments.summaries(this.time.now),
      playerTechState: this.tech.state,
      activeVeteranUnits: this.playerUnits.filter((unit) => unit.alive && unit.isVeteran).length,
      activePlayerAbilities: this.activePlayerAbilitySummary(),
      aiBudget: this.waves.budgetSummary,
      playerEconomyOutposts: this.economyOutposts.playerOwnedCount,
      enemyEconomyOutposts: this.economyOutposts.enemyOwnedCount,
      economicOutposts: this.economyOutposts.summaries,
      crossingControls: this.crossingControlSummaries(),
      laneMoraleByLane: this.laneMoraleSummary(),
      laneCollapseByLane: this.laneCollapseSummary(),
      recentCastleRepairs: this.recentCastleRepairs(),
      queuedPlayerUnitsByLane: this.queuedPlayerUnitsByLane(),
      objectivePoints: this.economy.objectivePoints,
      activeObjectives: this.objectives.activeLabels,
      activeObjectiveDetails: this.objectives.activeDetails,
      destroyedTowers: this.destroyedTowerIds,
      upgradePatterns: this.upgradePatterns,
      objectiveSuccessRate: this.objectives.successRate,
      towers: this.towers,
      playerUnits: this.playerUnits,
      enemies: this.enemies,
      recentActions: this.recentActions
    });
  }

  private createLaneStates(): LaneStateSummary[] {
    return this.createSummary().laneStates;
  }

  private markAiPriorityTowerTargets(command: GameCommand) {
    const hasTowerIntent = command.towerAttackIntent === "siege_focus" || command.towerAttackIntent === "break_walls";
    const towerPressureLanes = new Set(
      command.spawnCommands
        .filter((spawn) => spawn.purpose === "siege" || spawn.units.some((unit) => unit === "siege_cart" || unit === "assassin"))
        .map((spawn) => spawn.lane)
    );
    if (!hasTowerIntent && towerPressureLanes.size === 0) return;

    const priorityTowers = this.createSummary().priorityTowers ?? [];
    const selectedTargets = priorityTowers
      .filter((target) => towerPressureLanes.size === 0 || towerPressureLanes.has(target.lane))
      .slice(0, 2);
    const targets = selectedTargets.length > 0 ? selectedTargets : priorityTowers.slice(0, 1);

    for (const target of targets) {
      const tower = this.towers
        .filter((candidate) => !candidate.destroyed && !candidate.consumed && candidate.lane === target.lane && candidate.id === target.towerId)
        .sort((a, b) => Math.abs(a.x - target.x) - Math.abs(b.x - target.x))[0];
      if (!tower) continue;
      this.showPriorityTowerMarker(tower, target.reason);
      this.logger.log("AI_TOWER_TARGETED", this.battleTime, { towerId: tower.id, reason: target.reason, threatScore: target.threatScore }, tower.lane);
      this.recentActions.push(`ai_targeted_${tower.id}_lane_${tower.lane}`);
    }

    if (targets.length > 0) {
      this.buildPanel.setStatus(t(this.settings.language, "aiTowerTargeted"));
    }
  }

  private queuedPlayerUnitsByLane(): Partial<Record<LaneNumber, number>> {
    const queued: Partial<Record<LaneNumber, number>> = {};
    for (const item of this.summonQueue) {
      queued[item.lane] = (queued[item.lane] ?? 0) + 1;
    }
    return queued;
  }

  private recentCastlePressure() {
    const damageByLane: Partial<Record<LaneNumber, number>> = {};
    const damageBySegment: Partial<Record<CastleSegmentId, number>> = {};
    const recentWindowStart = Math.max(0, this.battleTime - 45);

    for (const event of this.logger.snapshot()) {
      if (!this.isRecentPlayerCastleDamage(event, recentWindowStart)) continue;
      const damage = Number(event.payload.damage ?? 0);
      if (!Number.isFinite(damage) || damage <= 0) continue;
      damageByLane[event.lane] = (damageByLane[event.lane] ?? 0) + damage;
      const segment = event.payload.segment as CastleSegmentId | undefined;
      if (segment === "top" || segment === "main" || segment === "bottom") {
        damageBySegment[segment] = (damageBySegment[segment] ?? 0) + damage;
      }
    }

    const segment = (["main", "top", "bottom"] as CastleSegmentId[])
      .map((id) => ({ id, damage: damageBySegment[id] ?? 0 }))
      .sort((a, b) => b.damage - a.damage)[0];

    return {
      damageByLane,
      segment: segment && segment.damage > 0 ? segment.id : undefined
    };
  }

  private recentCastleRepairs() {
    const repairs: Partial<Record<CastleSegmentId, number>> = {};
    const recentWindowStart = Math.max(0, this.battleTime - 60);

    for (const event of this.logger.snapshot()) {
      if (event.type !== "CASTLE_REPAIRED" || event.time < recentWindowStart) continue;
      const segment = event.payload.segment as CastleSegmentId | undefined;
      const repairedHp = Number(event.payload.repairedHp ?? 0);
      if ((segment === "top" || segment === "main" || segment === "bottom") && Number.isFinite(repairedHp) && repairedHp > 0) {
        repairs[segment] = (repairs[segment] ?? 0) + repairedHp;
      }
    }

    return repairs;
  }

  private isRecentPlayerCastleDamage(event: BattleEvent, recentWindowStart: number): event is BattleEvent & { lane: LaneNumber } {
    return event.type === "CASTLE_DAMAGED" && event.payload.owner === "player" && event.time >= recentWindowStart && Boolean(event.lane);
  }

  private finishBattle(result: "win" | "loss", destroyedBy: string) {
    if (this.ended) return;
    this.ended = true;
    AudioManager.playSfx(result === "win" ? "win" : "lose");
    this.recordSurvivingVeterans();
    const events = this.logger.snapshot();
    const battleResult: BattleResult = {
      result,
      battleTime: this.battleTime,
      playerCastleHp: this.playerCastle.hp,
      aiCastleHp: this.aiCastle.hp,
      destroyedBy,
      strategyFaced: this.inferStrategyFaced(events),
      commanderMood: "tactical",
      laneAnalysis: this.createLaneBattleAnalysis(events),
      mercySummary: this.createMercyBattleSummary(events),
      mode: this.battleMode,
      dailySeed: this.daily?.seed,
      events
    };
    this.pendingResult = battleResult;
    this.commanderSocket?.sendBattleEnded(battleResult);
    this.time.delayedCall(1400, () => {
      if (this.resultReport) return;
      const report: BattleReport = this.settings.language === "ko" ? {
        result,
        commanderComment: result === "win" ? "요새 전선을 뚫었다. 다음 전투에서는 그 길을 먼저 막겠다." : "성벽의 약한 돌이 드러났다. 다음에는 그 전선을 더 세게 누르겠다.",
        playerMistakes: result === "win" ? ["일부 전선의 정찰 공백이 있었습니다."] : ["전선 돌파 대응이 늦었습니다.", "반격 압박이 늦게 도착했습니다."],
        playerGoodMoves: result === "win" ? ["반격이 적 요새까지 도달했습니다."] : ["방어가 여러 파도를 지연했습니다."],
        nextBattleThreat: "다음 전투에서는 분산 압박과 주력 전선 재배치를 함께 시도할 것입니다.",
        memoryUpdateSummary: "타워 배치 습관, 약한 전선, 반격 타이밍을 다음 결투에 반영합니다.",
        learnedInsights: battleResult.laneAnalysis?.slice(0, 2).map((lane) => `${lane.lane}번 전선 ${lane.verdict}: 소환 ${lane.playerUnitsSummoned}, 성 피해 ${lane.playerDamageToAiCastle}/${lane.aiDamageToPlayerCastle}`),
        strategyFaced: battleResult.strategyFaced
      } : {
        result,
        commanderComment: result === "win" ? "You broke the fortress line. The commander will remember that." : "The fortress found the weak stone in your wall.",
        playerMistakes: result === "win" ? ["Some lanes briefly lacked scout coverage."] : ["A lane breach reached the castle.", "Counterattack pressure arrived too late."],
        playerGoodMoves: result === "win" ? ["Counterpressure reached the enemy fortress."] : ["The defense delayed several waves."],
        nextBattleThreat: "Expect adaptive split pressure in the next battle.",
        memoryUpdateSummary: "Track tower placement habits, weak lanes, and counterattack timing for the next duel.",
        learnedInsights: battleResult.laneAnalysis?.slice(0, 2).map((lane) => `Lane ${lane.lane} ${lane.verdict}: summons ${lane.playerUnitsSummoned}, castle damage ${lane.playerDamageToAiCastle}/${lane.aiDamageToPlayerCastle}`),
        strategyFaced: battleResult.strategyFaced
      };
      const updated = updateMemoryFromReport(loadPlayerMemory(this.playerId), battleResult, report);
      savePlayerMemory(updated);
      appendBattleHistory(battleResult, report);
      this.scene.start("ResultScene", { result, report, battleResult });
    });
  }

  private createLaneBattleAnalysis(events: BattleEvent[]): LaneBattleAnalysis[] {
    const lanes = ([1, 2, 3, 4, 5] as LaneNumber[]).map((lane) => ({
      lane,
      playerDamageToAiCastle: 0,
      aiDamageToPlayerCastle: 0,
      playerUnitsSummoned: 0,
      enemyUnitsKilled: 0,
      playerUnitsLost: 0,
      laneBreaches: 0,
      moveCommands: 0,
      aiManeuvers: 0,
      verdict: "quiet" as const
    }));

    const byLane = new Map<LaneNumber, LaneBattleAnalysis>(lanes.map((lane) => [lane.lane, lane]));
    for (const event of events) {
      if (!event.lane) continue;
      const lane = byLane.get(event.lane);
      if (!lane) continue;

      if (event.type === "CASTLE_DAMAGED") {
        const damage = Number(event.payload.damage ?? event.payload.amount ?? 0);
        if (Number.isFinite(damage) && damage > 0) {
          if (event.payload.owner === "ai") lane.playerDamageToAiCastle += damage;
          if (event.payload.owner === "player") lane.aiDamageToPlayerCastle += damage;
        }
      }
      if (event.type === "UNIT_SUMMONED") lane.playerUnitsSummoned += 1;
      if (event.type === "ENEMY_KILLED") lane.enemyUnitsKilled += 1;
      if (event.type === "PLAYER_UNIT_KILLED") lane.playerUnitsLost += 1;
      if (event.type === "LANE_BREACHED") lane.laneBreaches += 1;
      if (event.type === "UNIT_MOVE_COMMAND") lane.moveCommands += 1;
      if (event.type === "AI_MANEUVER_COMMAND") lane.aiManeuvers += 1;
    }

    return lanes.map((lane) => ({
      ...lane,
      playerDamageToAiCastle: Math.round(lane.playerDamageToAiCastle),
      aiDamageToPlayerCastle: Math.round(lane.aiDamageToPlayerCastle),
      verdict: this.resolveLaneBattleVerdict(lane)
    }));
  }

  private resolveLaneBattleVerdict(lane: Omit<LaneBattleAnalysis, "verdict">): LaneBattleAnalysis["verdict"] {
    const hadAction = lane.playerDamageToAiCastle + lane.aiDamageToPlayerCastle + lane.playerUnitsSummoned + lane.enemyUnitsKilled + lane.playerUnitsLost + lane.laneBreaches > 0;
    if (!hadAction) return "quiet";
    if (lane.laneBreaches > 0 || lane.aiDamageToPlayerCastle >= 60) return "collapsed";
    if (lane.playerDamageToAiCastle >= 60 && lane.enemyUnitsKilled >= lane.playerUnitsLost) return "breakthrough";
    if (lane.enemyUnitsKilled >= 3 && lane.aiDamageToPlayerCastle <= 0) return "held";
    return "traded";
  }

  private createMercyBattleSummary(events: BattleEvent[]): MercyBattleSummary {
    const mercyEvents = events.filter((event) => event.type === "AI_MERCY_GRANTED");
    return mercyEvents.reduce<MercyBattleSummary>(
      (summary, event) => ({
        grantedCount: summary.grantedCount + 1,
        pleaCount: summary.pleaCount + (event.payload.reason === "plea" ? 1 : 0),
        autoCount: summary.autoCount + (event.payload.reason === "auto" ? 1 : 0),
        softenedEnemies: summary.softenedEnemies + Math.max(0, Number(event.payload.softenedEnemies ?? 0))
      }),
      { grantedCount: 0, pleaCount: 0, autoCount: 0, softenedEnemies: 0 }
    );
  }

  private inferStrategyFaced(events: BattleEvent[]): StrategyType {
    const enemyIds = new Set<string>();
    const lanesPressured = new Set<LaneNumber>();
    let maneuverCount = 0;
    let aiEventType = "";

    for (const event of events) {
      if (event.lane) lanesPressured.add(event.lane);
      const enemyType = event.payload.enemyType ?? event.payload.enemyId ?? event.payload.attackerId;
      if (typeof enemyType === "string") enemyIds.add(enemyType);
      if (event.type === "AI_MANEUVER_COMMAND" || (event.type === "UNIT_LANE_CHANGED" && event.payload.owner === "enemy")) {
        maneuverCount += 1;
      }
      if (event.type === "AI_EVENT_TRIGGERED" && typeof event.payload.type === "string") {
        aiEventType = event.payload.type;
      }
    }

    for (const enemy of this.enemies) {
      if (enemy.alive) enemyIds.add(enemy.id);
    }

    if (["siege_cart", "heavy_ram", "armored_beast"].some((id) => enemyIds.has(id))) return "siege_breaker";
    if (aiEventType === "boss_warning" || enemyIds.has("knight")) return "boss_pressure";
    if (maneuverCount >= 2 || lanesPressured.size >= 3) return "split_push";
    if (enemyIds.has("warlock") || enemyIds.has("plague_bishop")) return "counter_build";
    if (enemyIds.has("assassin") || enemyIds.has("black_assassin") || enemyIds.has("scout_wolf")) return "healer_hunter";
    if (aiEventType === "fog" || aiEventType === "southern_fog") return "fake_attack";
    return "rush";
  }

  private drawLaneGuides() {
    for (const lane of mapConfig.lanes) {
      this.add.rectangle(640, lane.y, 1060, 2, 0x9bdcff, 0.04).setDepth(20);
    }
  }

  private drawCrossingGuides() {
    const crossings = (mapConfig.laneCrossings as LaneCrossing[] | undefined ?? []).filter((crossing) => crossing.enabled);
    for (const crossing of crossings) {
      const fromY = this.laneY(crossing.fromLane);
      const toY = this.laneY(crossing.toLane);
      const midY = (fromY + toY) / 2;
      const path = this.add.graphics().setDepth(21);
      path.lineStyle(2, 0x8df3ff, 0.1);
      path.fillStyle(0x5de9ff, 0.045);
      path.lineBetween(crossing.x, fromY, crossing.x, toY);
      path.fillCircle(crossing.x, midY, Math.max(14, crossing.radius * 0.34));
      path.strokeCircle(crossing.x, midY, Math.max(14, crossing.radius * 0.34));
      const ring = this.add.graphics().setDepth(24);
      const progressFill = this.add.rectangle(crossing.x - 24, midY + 22, 1, 4, 0xd2c37d, 0.9).setOrigin(0, 0.5).setDepth(25);
      const flag = this.add
        .text(crossing.x, midY - 22, "◇", {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: "13px",
          color: "#d2c37d",
          stroke: "#121016",
          strokeThickness: 2
        })
        .setOrigin(0.5)
        .setResolution(2)
        .setDepth(26);
      const state: CrossingControlState = { config: crossing, owner: "neutral", progress: 0, playerUnits: 0, enemyUnits: 0, ring, progressFill, flag };
      this.crossingStates.set(crossing.id, state);
      this.updateCrossingMarker(state);
    }
  }

  /** Fit both cameras to the viewport now and on every resize/rotate; keep the backdrop covering. */
  private initResponsiveLayout() {
    const apply = () => {
      const size = this.scale.gameSize;
      this.layoutBattlefield(size.width, size.height);
    };
    apply();
    this.scale.on("resize", apply);
    this.events.once("shutdown", () => this.scale.off("resize", apply));
    this.events.once("destroy", () => this.scale.off("resize", apply));
  }

  /** Re-fit the world + HUD cameras to the current viewport and resize the full-bleed backdrop. */
  private layoutBattlefield(viewportW: number, viewportH: number) {
    if (viewportW <= 0 || viewportH <= 0) return;
    fitCameraToDesign(this.cameras.main, viewportW, viewportH, 1);
    this.clampCameraScroll();
    if (this.uiCamera) fitHudCamera(this.uiCamera, viewportW, viewportH);
    if (this.fullBleedBackdrop) {
      const cover = backdropCoverSize(viewportW, viewportH);
      this.fullBleedBackdrop.setDisplaySize(cover.width, cover.height);
    }
  }

  /** Camera zoom at which the 1280x720 design map exactly fits the viewport (1 in FIT mode). */
  private currentFitZoom(): number {
    const camera = this.cameras.main;
    return Math.min(camera.width / mapConfig.designWidth, camera.height / mapConfig.designHeight);
  }

  private setupCameraControls() {
    const camera = this.cameras.main;
    camera.setZoom(1);
    camera.centerOn(mapConfig.designWidth / 2, mapConfig.designHeight / 2);
    this.clampCameraScroll();

    const panel = this.add.image(640, 28, "lane_panel").setDepth(150).setScrollFactor(0).setDisplaySize(310, 34).setAlpha(0.86);
    this.add
      .text(640, 28, this.cameraHint(), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setDepth(151)
      .setScrollFactor(0);
    panel.setInteractive({ useHandCursor: true });
    panel.on("pointerdown", () => this.resetCamera());
  }

  private handleWheelZoom(pointer: Phaser.Input.Pointer, deltaY: number) {
    if (pointer.y > 610) return;
    const camera = this.cameras.main;
    const before = this.pointerToWorld(pointer);
    const nextZoom = clampZoom(camera.zoom * (deltaY < 0 ? WHEEL_STEP_IN : WHEEL_STEP_OUT), this.currentFitZoom());
    camera.setZoom(nextZoom);
    // Refresh the stale transform matrix before re-reading the focal point (see updatePinchZoom).
    camera.preRender();
    const after = this.pointerToWorld(pointer);
    camera.scrollX += before.x - after.x;
    camera.scrollY += before.y - after.y;
    this.clampCameraScroll();
    this.showZoomToast(nextZoom);
  }

  private resetCamera() {
    const camera = this.cameras.main;
    camera.setZoom(this.currentFitZoom());
    camera.centerOn(mapConfig.designWidth / 2, mapConfig.designHeight / 2);
    this.clampCameraScroll();
  }

  private focusCameraAt(x: number, y: number) {
    this.cameras.main.centerOn(x, y);
    this.clampCameraScroll();
  }

  private cameraViewport() {
    const camera = this.cameras.main;
    return {
      x: camera.scrollX,
      y: camera.scrollY,
      width: camera.width / camera.zoom,
      height: camera.height / camera.zoom
    };
  }

  private clampCameraScroll() {
    const camera = this.cameras.main;
    // Keep the visible rect inside the map. The math accounts for the camera's 0.5 origin
    // (scroll is not the top-left); see clampCameraScrollAxis. Without it, panning while
    // zoomed reveals the black void past the right/bottom edges.
    camera.scrollX = clampCameraScrollAxis(camera.scrollX, camera.width, camera.zoom, mapConfig.designWidth);
    camera.scrollY = clampCameraScrollAxis(camera.scrollY, camera.height, camera.zoom, mapConfig.designHeight);
  }

  private showZoomToast(zoom: number) {
    this.zoomToast?.destroy();
    this.zoomToast = this.add
      .text(640, 68, `${Math.round(zoom * 100)}%`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setResolution(2)
      .setOrigin(0.5)
      .setDepth(152)
      .setScrollFactor(0);
    this.tweens.add({
      targets: this.zoomToast,
      alpha: 0,
      duration: 800,
      onComplete: () => {
        this.zoomToast?.destroy();
        this.zoomToast = undefined;
      }
    });
  }

  private pointerToWorld(pointer: Phaser.Input.Pointer) {
    return this.cameras.main.getWorldPoint(pointer.x, pointer.y) as Phaser.Math.Vector2;
  }

  private unitCommandHint() {
    return t(this.settings.language, this.prefersTouchControls() ? "unitCommandReadyTouch" : "unitCommandReady");
  }

  private cameraHint() {
    return t(this.settings.language, this.prefersTouchControls() ? "cameraHintTouch" : "cameraHint");
  }

  private applyAccessibilityTextSettings() {
    if (!this.settings.highContrastText) return;
    for (const child of this.children.list) {
      if (!(child instanceof Phaser.GameObjects.Text)) continue;
      child.setResolution(2);
      child.setStroke("#030306", 3);
      child.setShadow(0, 1, "#000000", 3, true, true);
    }
  }

  private prefersTouchControls() {
    return (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) || (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches);
  }

  private createUiCamera() {
    this.uiCamera = this.cameras.add(0, 0, mapConfig.designWidth, mapConfig.designHeight, false, "hud-camera");
    this.uiCamera.setScroll(0, 0);
    this.uiCamera.setZoom(1);
    this.syncCameraLayers();
  }

  private createFullscreenButton() {
    const button = this.add.container(850, 27).setDepth(154).setScrollFactor(0);
    const background = this.add.rectangle(0, 0, 78, 31, 0x101820, 0.84).setStrokeStyle(1, 0x8de7ff, 0.78);
    const label = this.add
      .text(0, -1, t(this.settings.language, "fullscreen"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "12px" : "11px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 2
      })
      .setOrigin(0.5);
    button.add([background, label]);
    button.setSize(78, 31);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x17364a, 0.94));
    button.on("pointerout", () => background.setFillStyle(0x101820, 0.84));
    button.on("pointerdown", () => this.requestFullscreen());
  }

  private createUltimateButton() {
    const button = this.add.container(738, 27).setDepth(154).setScrollFactor(0);
    this.ultimateButtonBg = this.add.rectangle(0, 0, 96, 31, 0x141222, 0.86).setStrokeStyle(1, 0xf7e9bb, 0.7);
    this.ultimateButtonFill = this.add.rectangle(-47, 13, 0, 4, 0xf7d66f, 0.95).setOrigin(0, 0.5);
    this.ultimateButtonText = this.add
      .text(0, -2, `${t(this.settings.language, "ultimate")} 0%`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "12px" : "11px",
        color: "#fff3c2",
        stroke: "#08070a",
        strokeThickness: 2
      })
      .setResolution(2)
      .setOrigin(0.5);
    button.add([this.ultimateButtonBg, this.ultimateButtonFill, this.ultimateButtonText]);
    button.setSize(96, 31);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => this.ultimateButtonBg?.setFillStyle(0x2b2341, 0.94));
    button.on("pointerout", () => this.ultimateButtonBg?.setFillStyle(0x141222, 0.86));
    button.on("pointerdown", () => this.activatePlayerUltimate());
    this.ultimateButton = button;
    this.updateUltimateButton();
  }

  private updateUltimateButton() {
    if (!this.ultimateButton || !this.ultimateButtonFill || !this.ultimateButtonText || !this.ultimateButtonBg) return;
    const ready = this.playerUltimateGauge >= 100;
    const active = this.isPlayerUltimateActive();
    const remaining = Math.ceil(Math.max(0, this.playerUltimateUntil - this.time.now) / 1000);
    this.ultimateButtonFill.width = Math.min(94, Math.max(0, this.playerUltimateGauge * 0.94));
    this.ultimateButtonBg.setStrokeStyle(ready || active ? 2 : 1, active ? 0x8dffbf : ready ? 0xf7d66f : 0xf7e9bb, active || ready ? 0.95 : 0.7);
    this.ultimateButtonText.setText(active ? `${t(this.settings.language, "ultimate")} ${remaining}s` : ready ? t(this.settings.language, "ultimateReady") : `${t(this.settings.language, "ultimate")} ${Math.floor(this.playerUltimateGauge)}%`);
  }

  private createSpeedButton() {
    const button = this.add.container(952, 27).setDepth(154).setScrollFactor(0);
    this.speedButtonBg = this.add.rectangle(0, 0, 78, 31, 0x101820, 0.84).setStrokeStyle(1, 0x8de7ff, 0.78);
    this.speedButtonText = this.add
      .text(0, -1, this.speedButtonLabel(), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "12px" : "11px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 2
      })
      .setResolution(2)
      .setOrigin(0.5);
    button.add([this.speedButtonBg, this.speedButtonText]);
    button.setSize(78, 31);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => this.speedButtonBg?.setFillStyle(0x17364a, 0.94));
    button.on("pointerout", () => this.speedButtonBg?.setFillStyle(0x101820, 0.84));
    button.on("pointerdown", () => this.cycleBattleSpeed());
  }

  private cycleBattleSpeed() {
    const speeds = [0.75, 1, 1.25];
    const current = speeds.findIndex((speed) => Math.abs(speed - this.battleTimeScale) < 0.01);
    this.battleTimeScale = speeds[(current + 1) % speeds.length] ?? 1;
    this.speedButtonText?.setText(this.speedButtonLabel());
  }

  private speedButtonLabel() {
    return `${t(this.settings.language, "speed")} ${this.battleTimeScale}x`;
  }

  private createZoomButtons() {
    if (!this.settings.beta.cameraZoom || !this.prefersTouchControls()) return;
    this.createZoomButton(1232, 286, "+", WHEEL_STEP_IN);
    this.createZoomButton(1232, 346, "−", WHEEL_STEP_OUT);
  }

  private createZoomButton(x: number, y: number, glyph: string, factor: number) {
    const size = 54;
    const button = this.add
      .container(x, y)
      .setDepth(154)
      .setSize(size, size)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    const bg = this.add.rectangle(0, 0, size, size, 0x0d1622, 0.92).setStrokeStyle(1, 0x8de7ff, 0.9);
    const label = this.add
      .text(0, -2, glyph, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "32px",
        color: "#dff1ff"
      })
      .setOrigin(0.5);
    button.add([bg, label]);
    button.on("pointerdown", () => this.stepZoom(factor));
    button.on("pointerover", () => bg.setFillStyle(0x16263a, 0.95));
    button.on("pointerout", () => bg.setFillStyle(0x0d1622, 0.92));
    this.cameras.main.ignore(button);
  }

  /** Zoom one step about the screen centre, reusing the wheel/pinch focal-recenter logic. */
  private stepZoom(factor: number) {
    const camera = this.cameras.main;
    const next = clampZoom(camera.zoom * factor, this.currentFitZoom());
    const cx = camera.width / 2;
    const cy = camera.height / 2;
    const before = camera.getWorldPoint(cx, cy);
    camera.setZoom(next);
    // Refresh the stale transform matrix before re-reading the focal point (see updatePinchZoom).
    camera.preRender();
    const after = camera.getWorldPoint(cx, cy);
    camera.scrollX += before.x - after.x;
    camera.scrollY += before.y - after.y;
    this.clampCameraScroll();
    this.showZoomToast(next);
  }

  /** Scroll the world camera with the arrow keys / WASD, scaled by zoom and frame time. */
  private handleKeyboardPan(deltaMs: number) {
    const cursors = this.cameraCursors;
    const wasd = this.cameraPanKeys;
    if (!cursors && !wasd) return;
    // Ignore while typing in the commander chat input so arrows move the caret, not the map.
    const activeEl = typeof document !== "undefined" ? document.activeElement : null;
    if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")) return;

    const left = Boolean(cursors?.left.isDown || wasd?.A.isDown);
    const right = Boolean(cursors?.right.isDown || wasd?.D.isDown);
    const up = Boolean(cursors?.up.isDown || wasd?.W.isDown);
    const down = Boolean(cursors?.down.isDown || wasd?.S.isDown);
    if (!left && !right && !up && !down) return;

    const camera = this.cameras.main;
    // ~900 design px/s, divided by zoom so the on-screen pan speed feels constant.
    const distance = (900 * deltaMs) / 1000 / camera.zoom;
    if (left) camera.scrollX -= distance;
    if (right) camera.scrollX += distance;
    if (up) camera.scrollY -= distance;
    if (down) camera.scrollY += distance;
    this.clampCameraScroll();
  }

  private requestFullscreen() {
    // Route through Phaser's ScaleManager: it resolves vendor prefixes, fires the request
    // inside the button's user-gesture, fullscreens the #game host (so the chat overlay
    // comes along), and re-fits the canvas on the resulting resize.
    if (this.scale.isFullscreen) {
      this.scale.stopFullscreen();
      return;
    }
    if (!this.scale.fullscreen.available) {
      // iOS Safari exposes no element Fullscreen API at all — only an installed PWA is
      // truly fullscreen there. Point the player at "Add to Home Screen".
      this.buildPanel.setStatus(t(this.settings.language, "fullscreenUnsupported"));
      return;
    }
    this.scale.startFullscreen();
    this.buildPanel.setStatus(t(this.settings.language, "fullscreenReady"));
    this.lockLandscapeOrientation();
  }

  /** Best-effort lock to landscape after entering fullscreen (Android); ignored where unsupported. */
  private lockLandscapeOrientation() {
    const orientation = window.screen?.orientation as unknown as
      | { lock?: (orientation: string) => Promise<unknown> }
      | undefined;
    void orientation?.lock?.("landscape").catch(() => undefined);
  }

  private syncCameraLayers() {
    if (!this.uiCamera) return;
    const hasDepth = (child: Phaser.GameObjects.GameObject): child is Phaser.GameObjects.GameObject & { depth: number } =>
      "depth" in child && typeof child.depth === "number";
    const hudObjects = this.children.list.filter((child) => hasDepth(child) && child.depth >= 100);
    const worldObjects = this.children.list.filter((child) => hasDepth(child) && child.depth < 100);
    this.cameras.main.ignore(hudObjects);
    this.uiCamera.ignore(worldObjects);
  }

  private drawTowerPads() {
    for (const pad of mapConfig.towerPads) {
      const padSprite = this.add.image(pad.x, pad.y, "tower_pad").setDepth(25).setScale(0.64).setAlpha(0.78);
      padSprite.setInteractive({ useHandCursor: true });
      padSprite.on("pointerover", () => {
        if (!this.occupiedPads.has(pad.id)) {
          padSprite.setTexture("tower_pad_hover").setScale(0.7).setAlpha(0.95);
        }
      });
      padSprite.on("pointerout", () => {
        if (!this.occupiedPads.has(pad.id)) {
          padSprite.setTexture("tower_pad").setScale(0.64).setAlpha(0.78);
        }
      });
      padSprite.on("pointerdown", () => {
        if (!this.selection || this.selection.kind !== "tower") return;
        const towerData = towersData.find((tower) => tower.id === this.selection?.id);
        if (towerData) {
          this.placeTowerOnPad(towerData, pad);
        }
      });
      this.padSprites.set(pad.id, padSprite);
    }
  }

  private showTowerRange(tower: Tower) {
    const range = this.add.graphics().setDepth(54);
    range.lineStyle(2, 0x8df3ff, 0.35);
    range.fillStyle(0x5de9ff, 0.045);
    range.fillCircle(tower.x, tower.y, tower.range);
    range.strokeCircle(tower.x, tower.y, tower.range);
    this.tweens.add({
      targets: range,
      alpha: 0,
      duration: 1300,
      delay: 500,
      onComplete: () => range.destroy()
    });
  }

  private showTowerRangePreview(tower: Tower) {
    if (tower.destroyed) return;
    this.clearTowerRangePreview();
    this.towerRangePreview = this.add.graphics().setDepth(53);
    this.towerRangePreview.lineStyle(2, tower.healing ? 0x8fff92 : 0x8df3ff, 0.42);
    this.towerRangePreview.fillStyle(tower.healing ? 0x8fff92 : 0x5de9ff, 0.055);
    this.towerRangePreview.fillCircle(tower.x, tower.y, tower.range);
    this.towerRangePreview.strokeCircle(tower.x, tower.y, tower.range);
  }

  private clearTowerRangePreview() {
    this.towerRangePreview?.destroy();
    this.towerRangePreview = undefined;
  }

  private showMovePath(waypoints: UnitWaypoint[], style: MovePathStyle = {}) {
    if (waypoints.length === 0) return;
    const path = this.add.graphics().setDepth(style.depth ?? 55);
    path.lineStyle(2, style.lineColor ?? 0x9feaff, style.alpha ?? 0.6);
    path.fillStyle(style.fillColor ?? style.lineColor ?? 0x9feaff, Math.min(0.28, (style.alpha ?? 0.6) * 0.34));
    for (let index = 0; index < waypoints.length; index += 1) {
      const waypoint = waypoints[index]!;
      if (index > 0) {
        const previous = waypoints[index - 1]!;
        path.lineBetween(previous.x, previous.y, waypoint.x, waypoint.y);
      }
      path.fillCircle(waypoint.x, waypoint.y, index === waypoints.length - 1 ? 6 : 4);
      path.strokeCircle(waypoint.x, waypoint.y, index === waypoints.length - 1 ? 10 : 7);
    }
    this.tweens.add({
      targets: path,
      alpha: 0,
      duration: 1100,
      delay: 700,
      onComplete: () => path.destroy()
    });
  }

  private showCommandMarker(x: number, y: number, unitCount: number) {
    const marker = this.add.graphics().setDepth(68);
    marker.lineStyle(2, 0x9feaff, 0.92);
    marker.fillStyle(0x5de9ff, 0.16);
    marker.fillCircle(x, y, 16);
    marker.strokeCircle(x, y, 16);
    marker.lineBetween(x - 22, y, x + 22, y);
    marker.lineBetween(x, y - 18, x, y + 18);
    const label = this.add
      .text(x, y - 34, `${t(this.settings.language, "commandMarker")} x${unitCount}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: "#dff9ff",
        stroke: "#061018",
        strokeThickness: 2
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(69);
    this.tweens.add({
      targets: [marker, label],
      alpha: 0,
      duration: 900,
      delay: 650,
      onComplete: () => {
        marker.destroy();
        label.destroy();
      }
    });
  }

  private showCommandReject(x: number, y: number, reason: string) {
    const marker = this.add.graphics().setDepth(68);
    marker.lineStyle(2, 0xff604f, 0.88);
    marker.strokeCircle(x, y, 14);
    marker.lineBetween(x - 10, y - 10, x + 10, y + 10);
    marker.lineBetween(x - 10, y + 10, x + 10, y - 10);
    const label = this.add
      .text(x, y - 32, `${t(this.settings.language, "commandBlocked")}: ${reason}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: "#ffd6cb",
        stroke: "#061018",
        strokeThickness: 2
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(69);
    this.tweens.add({
      targets: [marker, label],
      alpha: 0,
      duration: 900,
      delay: 700,
      onComplete: () => {
        marker.destroy();
        label.destroy();
      }
    });
  }

  private showUnitAbilityPulse(unit: Unit, color: number, labelText: string) {
    const pulse = this.add.graphics().setDepth(87);
    pulse.lineStyle(3, color, 0.82);
    pulse.strokeCircle(unit.x, unit.y + 3, 42);
    pulse.lineStyle(1, 0xf7f2df, 0.48);
    pulse.strokeCircle(unit.x, unit.y + 3, 58);
    const label = this.add
      .text(unit.x, unit.y - 48, labelText, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: "#f7f2df",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(88);
    this.tweens.add({
      targets: [pulse, label],
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 760,
      delay: 560,
      onComplete: () => {
        pulse.destroy();
        label.destroy();
      }
    });
  }

  private showAbilityHit(x: number, y: number, color: number) {
    const hit = this.add.graphics().setDepth(86);
    hit.lineStyle(2, color, 0.78);
    hit.strokeCircle(x, y - 4, 18);
    hit.fillStyle(color, 0.12);
    hit.fillCircle(x, y - 4, 12);
    this.tweens.add({
      targets: hit,
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 320,
      onComplete: () => hit.destroy()
    });
  }

  private showPriorityTowerMarker(tower: Tower, reason: string) {
    const marker = this.add.graphics().setDepth(86);
    const x = tower.x;
    const y = tower.y - 6;
    marker.lineStyle(3, 0xff3f3f, 0.9);
    marker.strokeCircle(x, y, 42);
    marker.lineBetween(x - 54, y, x - 28, y);
    marker.lineBetween(x + 28, y, x + 54, y);
    marker.lineBetween(x, y - 52, x, y - 29);
    marker.lineBetween(x, y + 29, x, y + 52);
    marker.lineStyle(1, 0xffd0b8, 0.72);
    marker.strokeCircle(x, y, 28);

    const label = this.add
      .text(x, y - 60, `${t(this.settings.language, "aiTowerTargetMarker")}: ${tower.name}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "12px",
        color: "#ffe1d6",
        stroke: "#190506",
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(87);

    const reasonLabel = this.add
      .text(x, y + 54, reason.replace("_", " "), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "10px",
        color: "#ffb29a",
        stroke: "#190506",
        strokeThickness: 2
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setDepth(87);

    this.tweens.add({
      targets: marker,
      alpha: 0.36,
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 420,
      yoyo: true,
      repeat: 2
    });
    this.tweens.add({
      targets: [marker, label, reasonLabel],
      alpha: 0,
      duration: 820,
      delay: 2100,
      onComplete: () => {
        marker.destroy();
        label.destroy();
        reasonLabel.destroy();
      }
    });
  }

  private nearestLane(y: number): LaneNumber | undefined {
    const lane = mapConfig.lanes
      .map((candidate) => ({ lane: candidate.lane as LaneNumber, distance: Math.abs(candidate.y - y) }))
      .sort((a, b) => a.distance - b.distance)[0];
    return lane && lane.distance <= 62 ? lane.lane : undefined;
  }

  private showTutorial() {
    const steps = ["tutorial1", "tutorial2", "tutorial3", "tutorial4", "tutorial5"] as const;
    const overlay = this.add.container(640, 360).setDepth(240).setScrollFactor(0);
    this.tutorialWasOpen = true;
    const shade = this.add.rectangle(0, 0, 1280, 720, 0x050507, 0.56);
    const panel = this.add.rectangle(0, 0, 660, 250, 0x11131b, 0.94).setStrokeStyle(2, 0xc9b878, 0.95);
    const title = this.add
      .text(0, -86, t(this.settings.language, "tutorial"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "30px" : "32px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 4
      })
      .setOrigin(0.5);
    const body = this.add
      .text(0, -10, t(this.settings.language, steps[this.tutorialStep]!), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "22px" : "21px",
        color: "#eef8ff",
        stroke: "#08070a",
        strokeThickness: 3,
        fixedWidth: 560,
        align: "center",
        wordWrap: { width: 560 }
      })
      .setOrigin(0.5);
    const next = this.createTutorialButton(170, 82, t(this.settings.language, "tutorialNext"), () => {
      this.tutorialStep += 1;
      if (this.tutorialStep >= steps.length) {
        this.closeTutorial();
        return;
      }
      body.setText(t(this.settings.language, steps[this.tutorialStep]!));
      const label = next.getByName("label") as Phaser.GameObjects.Text | undefined;
      label?.setText(this.tutorialStep === steps.length - 1 ? t(this.settings.language, "tutorialDone") : t(this.settings.language, "tutorialNext"));
    });
    const skip = this.createTutorialButton(-170, 82, t(this.settings.language, "tutorialSkip"), () => this.closeTutorial());
    overlay.add([shade, panel, title, body, skip, next]);
    overlay.setSize(1280, 720);
    overlay.setInteractive(new Phaser.Geom.Rectangle(-640, -360, 1280, 720), Phaser.Geom.Rectangle.Contains);
    this.tutorialOverlay = overlay;
    this.syncCameraLayers();
  }

  private createTutorialButton(x: number, y: number, labelText: string, onClick: () => void) {
    const button = this.add.container(x, y);
    const frame = this.add.rectangle(0, 0, 150, 46, 0x17364a, 0.96).setStrokeStyle(2, 0x8de7ff, 0.95);
    const label = this.add
      .text(0, 0, labelText, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "18px" : "17px",
        color: "#f7f2df",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setName("label");
    button.add([frame, label]);
    button.setSize(150, 46);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
    button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.96));
    button.on("pointerdown", onClick);
    return button;
  }

  private closeTutorial() {
    this.tutorialOverlay?.destroy(true);
    this.tutorialOverlay = undefined;
    setTutorialSeen();
    if (this.tutorialWasOpen) {
      this.battleStartedAt = this.time.now;
      this.lastSummaryAt = this.time.now + 8000;
      this.lastEventsAt = this.time.now + 4000;
      this.tutorialWasOpen = false;
    }
    this.syncCameraLayers();
  }

  getDebugState() {
    return {
      tutorialOpen: Boolean(this.tutorialOverlay),
      enemies: this.enemies.length,
      playerUnits: this.playerUnits.length,
      battleTime: this.battleTime,
      enemySummons: this.logger.countByType("ENEMY_SUMMONED"),
      laneBreaches: this.logger.countByType("LANE_BREACHED"),
      castles: this.castleSummary(this.playerCastles),
      aiCastles: this.castleSummary(this.aiCastles)
    };
  }
}
